import Camera from './rendering/Camera.js';
import World from './core/World.js';
import InputManager from './input/InputManager.js';
import ActionButtons from './input/ActionButtons.js';
import MaterialFactory from './materials/MaterialFactory.js';
import Material from './materials/Material.js';
import MaterialEditor from './ui/MaterialEditor.js';
import TopToolbar from './ui/TopToolbar.js';
import TopSheet from './ui/TopSheet.js';
import SettingsSidebar from './ui/SettingsSidebar.js';
import GameWindowManager from './ui/GameWindowManager.js';
import FacePicker from './input/FacePicker.js';
import VoxelEditor from './editor/VoxelEditor.js';
import UndoManager from './editor/UndoManager.js';
import AmbientOcclusion from './ao/AmbientOcclusion.js';
import Debug from './utils/Debug.js';
import ProfanityFilter from './utils/ProfanityFilter.js';

// Math utilities
import Transform from './math/Transform.js';
import DirectionResolver from './character/DirectionResolver.js';
import DOMUtils from './utils/DOMUtils.js';


// Character system imports
import { Character, CharacterSprite, CharacterAnimator, CharacterController, CharacterMetadata, CharacterDebugVisualizer, SpriteAtlasBuilder, CharacterShadow, OptimizedAtlasLoader } from './character/index.js';
import CharacterCollisionSystem from './physics/CharacterCollisionSystem.js';
import GroundDetector from './physics/GroundDetector.js';
import OrbitalCameraController from './rendering/OrbitalCameraController.js';

// Pathfinding system imports
import { NavMeshBuilder, PathFinder, PathFollower } from './pathfinding/index.js';
import ClickToMoveHandler from './input/ClickToMoveHandler.js';

// NPC system imports
import NPCManager from './npc/NPCManager.js';
import NPCTool from './editor/tools/NPCTool.js';

// Dialog system
import DialogManager from './dialog/DialogManager.js';

// Impostor system imports
import Impostor from './rendering/Impostor.js';
import ImpostorTool from './editor/tools/ImpostorTool.js';

// Audio system
import AudioSystemIntegrator from './audio/AudioSystemIntegrator.js';
import AudioControlPanel from './ui/AudioControlPanel.js';

// Combat system
import CombatStats, { SPELL_PROJECTILES } from './combat/CombatStats.js';
import CombatManager from './combat/CombatManager.js';
import ProjectileManager from './combat/ProjectileManager.js';

// HUD
import HUDManager from './hud/HUDManager.js';
import './css/hud.css';

// Loot & Quests
import Inventory, { EQUIP_SLOT_KEYS } from './loot/Inventory.js';
import LootManager from './loot/LootManager.js';
import QuestManager from './combat/QuestManager.js';
import { RARITY_TIERS, getSellPrice, MERCHANT_STOCK, ITEM_DEFS, EQUIPMENT_SLOTS, MYSTERY_REWARDS_BY_TIER, KEY_TIERS } from './loot/ItemDefinitions.js';
import { createItemInstance, getInstanceDisplayName } from './loot/ItemFactory.js';

// Character progression & persistence
import CharacterProgression from './progression/CharacterProgression.js';
import CharacterSave from './progression/CharacterSave.js';

// Dungeon system
import DungeonGenerator from './dungeon/DungeonGenerator.js';
import DungeonConfig from './dungeon/DungeonConfig.js';
import DungeonTheme from './dungeon/DungeonTheme.js';

// Sky system
import CloudBackdrop from './sky/CloudBackdrop.js';

// Character Creator, NPC Creator & Credits
import CharacterCreator from './ui/CharacterCreator.js';
import NPCCreator from './ui/NPCCreator.js';
import { loadManifest as loadLPCManifest } from './character/LPCLayerDefinitions.js';
import CreditsScreen from './ui/CreditsScreen.js';

// Multiplayer system
import NetworkManager from './network/NetworkManager.js';
import WorldSync from './network/WorldSync.js';
import PlayerSync from './network/PlayerSync.js';
import NPCSync from './network/NPCSync.js';
import RoomService from './network/RoomService.js';
import MultiplayerPanel from './ui/MultiplayerPanel.js';
import JoinOverlay from './ui/JoinOverlay.js';
import DungeonInstanceManager from './network/DungeonInstanceManager.js';
import MapRenderer from './ui/MapRenderer.js';
import PlayerChat from './chat/PlayerChat.js';
import NicknameGenerator from './utils/NicknameGenerator.js';

// Import CSS (Vite handles these)
import './css/styles.css';
import './css/materials.css';
import './css/gui.css';
import './css/material-editor.css';
import './css/dialog.css';
import './css/chat.css';
import './css/toolbar.css';
import './css/join-overlay.css';
import './css/dungeon-invite.css';
import './css/player-chat.css';
import './css/windows.css';

// Password-protected room utilities
async function sha256hex(input) {
  const data = new TextEncoder().encode(input);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(hashBuffer)).map(b => b.toString(16).padStart(2, '0')).join('');
}

// 16-char hex prefix of SHA-256(password) — stored in Firebase for server-side verification
async function hashPassword(password) {
  return (await sha256hex(password)).slice(0, 16);
}

// Trystero room ID for password rooms: pure hex hash of roomName+password.
// Must be a clean alphanumeric string — some MQTT brokers reject special chars.
async function derivePasswordRoomId(roomName, password) {
  return (await sha256hex(roomName + '\0' + password)).slice(0, 32);
}

/**
 * Character configuration - maps generic action names to actual animation names
 */
const DEFAULT_SPRITE_OFFSET = { x: 0, y: -0.88, z: 0, scale: 1.4 };
const LPC_SPRITE_OFFSET = { x: 0, y: -0.6, z: 0, scale: 1.4 };

const CHARACTER_CONFIGS = {
  boy: {
    idle: 'fight-stance-idle-8-frames',
    walk: 'walking-8-frames',
    run: 'running-8-frames',
    jump: 'jumping-2',
    punch: 'surprise-uppercut',
    kick: 'hurricane-kick',
    special: 'fireball',
    spriteOffset: DEFAULT_SPRITE_OFFSET
  },
  mage: {
    idle: 'breathing-idle',
    walk: 'walking-8-frames',
    run: 'running-8-frames',
    jump: 'jumping-1',
    punch: 'lead-jab',
    kick: 'roundhouse-kick',
    special: 'fireball',
    spriteOffset: DEFAULT_SPRITE_OFFSET
  },
  merchant: {
    idle: 'breathing-idle',
    walk: 'walking-8-frames',
    run: 'running-8-frames',
    jump: 'jumping-2',
    punch: 'surprise-uppercut',
    kick: 'leg-sweep',
    special: 'fireball',
    spriteOffset: DEFAULT_SPRITE_OFFSET
  },
  surfer: {
    idle: 'breathing-idle',
    walk: 'walking-8-frames',
    run: 'running-8-frames',
    jump: 'jumping-1',
    punch: 'cross-punch',
    kick: 'flying-kick',
    special: 'fireball',
    spriteOffset: DEFAULT_SPRITE_OFFSET
  },
  bear: {
    idle: 'idle-long',
    walk: 'walk-6-frames',
    run: 'running-6-frames',
    jump: 'jump',
    punch: 'going-to-sleep',
    kick: 'going-to-sleep',
    special: 'going-to-sleep',
    spriteOffset: { x: 0, y: -1.12, z: 0, scale: 1.4 }
  },
  hellrat: {
    idle: 'idle',
    walk: 'walk-6-frames',
    run: 'running-6-frames',
    jump: 'jump',
    punch: 'eating',
    kick: 'eating',
    special: 'eating',
    spriteOffset: { x: 0, y: -1.42, z: 0, scale: 1.4 }
  },
  rogue: {
    idle: 'fight-stance-idle-8-frames',
    walk: 'walking-8-frames',
    run: 'running-8-frames',
    jump: 'jumping-2',
    punch: 'surprise-uppercut',
    kick: 'high-kick',
    special: 'fireball',
    spriteOffset: DEFAULT_SPRITE_OFFSET
  }
};

/**
 * Main game class
 */
class Game {
  // Player spawn position (world units)
  static SPAWN_POSITION = { x: 5, y: 3, z: 5 };
  static CUBE_SIZE = 60;

  constructor() {
    // Initialize global sun vector (non normalized) before UI is initialized.
    window.sunVector = { x: -0.5, y: 0.8, z: 1.2 };
    // Initialize sun intensity (0 = extreme shading, 1 = no effect) with default 0.7.
    window.sunIntensity = 0.7;

    // Create material factory
    this.materialFactory = new MaterialFactory();

    // Calculate initial camera position from spawn point (in pixel coordinates)
    // Camera focuses on spawn position immediately to avoid pop on load
    const initialCameraPos = {
      x: Game.SPAWN_POSITION.x * Game.CUBE_SIZE,
      z: -Game.SPAWN_POSITION.z * Game.CUBE_SIZE
    };

    // Create camera
    this.camera = new Camera({
      angle: 30,
      position: initialCameraPos,
      zoomLevel: 1,
      skewAngle: 0.3,
      cubeSize: Game.CUBE_SIZE
    });

    // Create cloud backdrop (behind the scene)
    this.cloudBackdrop = new CloudBackdrop(document.getElementById('container'), this.camera);
    this.cloudBackdrop.setPreset('overworld');

    // Create world
    this.world = new World(this.camera);

    // Create input manager
    this.inputManager = new InputManager(this.camera);

    // Create new UI components
    this.settingsPanel = new SettingsSidebar();
    this.materialsSheet = new TopSheet({ id: 'materials-sheet' });
    this.presetsSheet = new TopSheet({ id: 'presets-sheet' });
    this.toolSheet = new TopSheet({ id: 'tool-sheet' });

    // Create window manager for game windows (inventory, character, map)
    const container = document.getElementById('container');
    this.windowManager = new GameWindowManager({ container });
    this.mapRenderer = new MapRenderer();
    this._createGameWindows();

    this.toolbar = new TopToolbar({
      settingsPanel: this.settingsPanel,
      materialsSheet: this.materialsSheet,
      presetsSheet: this.presetsSheet,
      toolSheet: this.toolSheet,
      windowManager: this.windowManager
    });
    this.sidebarManager = this.toolbar; // backward compat alias

    // Create material editor (renders into materials top sheet)
    this.materialEditor = new MaterialEditor(
      this.world,
      this.materialsSheet.getContentContainer()
    );
    this.materialEditor.setSidebarManager(this.sidebarManager);
    this.materialEditor.setMaterialsSheet(this.materialsSheet);

    // Create face picker for click-to-select functionality
    this.facePicker = new FacePicker(this.world, this.materialEditor);

    // Create voxel editor (presets render into presets top sheet)
    this.voxelEditor = new VoxelEditor(
      this.world,
      this.camera,
      this.inputManager,
      this.presetsSheet.getContentContainer()
    );
    this.voxelEditor.setSidebarManager(this.sidebarManager);
    this.toolbar.setVoxelEditor(this.voxelEditor);

    // Create undo/redo manager and wire to editor subsystems
    this.undoManager = new UndoManager({ maxSize: 50 });
    this.voxelEditor.undoManager = this.undoManager;
    this.materialEditor.undoManager = this.undoManager;
    this.toolbar.setUndoManager(this.undoManager);

    // Initialize character systems
    this.collisionSystem = new CharacterCollisionSystem(this.world);
    this.groundDetector = new GroundDetector(this.collisionSystem);
    this.characterDebug = new CharacterDebugVisualizer(
      this.world,
      this.settingsPanel.getTabContainer('debug'),
      this.inputManager
    );

    // Register collision system with world for automatic invalidation on edits
    this.world.setCollisionSystem(this.collisionSystem);

    // Initialize pathfinding system
    this.navMeshBuilder = new NavMeshBuilder(this.world, this.collisionSystem);
    this.pathFinder = new PathFinder(null, this.collisionSystem); // Graph set after build

    // Create click-to-move handler
    this.clickToMoveHandler = new ClickToMoveHandler(
      this.world,
      this.camera,
      this.pathFinder,
      null, // NavGraph set after build
      this.collisionSystem
    );

    // Initialize NPC system
    this.npcManager = new NPCManager({
      world: this.world,
      pathFinder: this.pathFinder,
      collisionSystem: this.collisionSystem,
      groundDetector: this.groundDetector
    });
    this.world.npcManager = this.npcManager;

    // Initialize dialog system
    this.dialogManager = new DialogManager({
      npcManager: this.npcManager,
      inputManager: this.inputManager
    });
    this.npcManager.setDialogManager(this.dialogManager);
    this.dialogManager.onShopOpen = (npc, onBack, onClose) => {
      this._openShopUI(npc, onBack, onClose);
    };
    this.dialogManager.onTradeKeys = (npc, tier, done) => {
      this._handleKeyTrade(tier, done);
    };
    // Expose for InputManager's isTyping() check
    window.game = this;

    // Set up mode switching for game tab
    this.setupModeSwitching();

    // Set up callback for when world is loaded/changed
    this.world.onWorldLoaded = () => {
      if (this.undoManager) this.undoManager.clear();
      this.collisionSystem.rebuildIndex();
      this.rebuildNavMesh();
      this._updateWorldLowestY();
      this.respawnPlayer();

      // Load NPCs from world data (async — atlas loading)
      if (this.npcManager) {
        const npcData = this.world._pendingNPCData;
        this.world._pendingNPCData = null;

        // Suppress NPC mutation broadcasts when loading remote world state
        // (worldSync._isApplyingRemote is true during _loadWorldState, checked synchronously here)
        const isRemoteLoad = this.worldSync?._isApplyingRemote;
        if (isRemoteLoad) this.npcManager._suppressMutation = true;

        this.npcManager.fromJSON(npcData || []).then(() => {
          if (isRemoteLoad) this.npcManager._suppressMutation = false;
          if (npcData && npcData.length > 0) {
            Debug.log('NPC', `Loaded ${npcData.length} NPCs from world`);
          }
          if (this.npcTool) {
            this.npcTool.refreshList();
          }
          // After spawning NPCs from remote world state, ensure authority flags are correct.
          // setAuthority() was called before NPCs existed, so re-apply to newly spawned NPCs.
          if (this.npcSync && isRemoteLoad) {
            this.npcSync.syncNPCFlags();
          }
        });
      }

      // Load impostors from world data (synchronous — no async loading needed)
      const impostorData = this.world._pendingImpostorData;
      this.world._pendingImpostorData = null;
      if (impostorData && impostorData.length > 0) {
        for (const data of impostorData) {
          const impostor = new Impostor(data);
          impostor._isVegetation = true;
          this.world.addImpostor(impostor);
          impostor.sampleAO();
        }
        Debug.log('Impostor', `Loaded ${impostorData.length} impostors from world`);
      }
      if (this.impostorTool) {
        this.impostorTool.refreshList();
      }
    };

    // Set up callback for geometry changes (cuboid add/remove/move)
    this.world.onGeometryChanged = () => {
      this.collisionSystem.rebuildIndex();
      this.rebuildNavMesh();
      this._updateWorldLowestY();
    };

    // Create orbital camera controller
    this.orbitalCamera = new OrbitalCameraController(this.camera, {
      followSpeed: 8.0,
      orbitSpeed: 90
    });
    this.inputManager.setOrbitalController(this.orbitalCamera);

    // Enable orbital camera immediately at spawn position to prevent camera pop on load
    this.orbitalCamera.setTarget(Game.SPAWN_POSITION.x, Game.SPAWN_POSITION.y, Game.SPAWN_POSITION.z);
    this.orbitalCamera.enable(Game.SPAWN_POSITION);

    // Initialize audio system (music + spatial SFX)
    this.audioSystem = new AudioSystemIntegrator();

    // Initialize HUD (hidden until dungeon mode)
    this.hudManager = new HUDManager();

    // Initialize inventory
    this.inventory = new Inventory();

    // Character progression and persistence
    this.progression = new CharacterProgression();
    this.characterSave = new CharacterSave();

    this.inventory.onChange = (inv) => {
      this.hudManager.updateQuickbar(inv);
      this._updateInventoryWindow();
      this.characterSave.scheduleSave(this.progression, this.inventory);

      // Update LPC equipment visuals on the player sprite
      if (this.player && this.player.sprite && this.player.sprite.getLPCAppearance()) {
        const equippedItemDefs = {};
        for (const [slot, entry] of Object.entries(inv.equipped)) {
          if (entry) {
            equippedItemDefs[slot] = entry.def || ITEM_DEFS[entry.itemId];
          }
        }
        this.player.sprite.updateEquipment(equippedItemDefs);
        // Push equipment data to PlayerSync and broadcast immediately
        if (this.playerSync) {
          this.playerSync._localEquipment = equippedItemDefs;
          this.playerSync.broadcastImmediately();
        }
      }
    };

    this.progression.onChange = () => {
      this._updateCharacterWindow();
      this.characterSave.scheduleSave(this.progression, this.inventory);
    };

    this.progression.onLevelUp = (newLevel) => {
      if (this.player?.combatStats) {
        this.progression.applyTo(this.player.combatStats);
        this.player.combatStats.health = this.player.combatStats.maxHealth;
      }
      if (this.lootManager && this.player) {
        this.lootManager._applyEquipmentBonuses(this.player);
      }
      // Hide floating health bar (full health)
      if (this.player?._floatingHpBar) {
        this.player._floatingHpFill.style.width = '100%';
        this.player._floatingHpBar.style.display = 'none';
      }
      this._updateCharacterWindow();
      this._showLevelUpNotification(newLevel);
    };

    // Initialize quest system
    this.questManager = new QuestManager({ hudManager: this.hudManager, world: this.world });
    this.questManager.onObjectivesChanged = () => this._updateObjectivesWindow();

    // Initialize loot system
    this.lootManager = new LootManager({
      world: this.world,
      inventory: this.inventory,
      audioSystem: this.audioSystem
    });

    // Initialize combat system
    this.combatManager = new CombatManager({
      world: this.world,
      audioSystem: this.audioSystem,
      getPlayer: () => this.player
    });
    this.combatManager.hudManager = this.hudManager;
    this.combatManager.lootManager = this.lootManager;
    this.combatManager.questManager = this.questManager;
    this.combatManager.onEnemyKilled = (target, killer) => {
      if (killer === this.player || !killer) {
        const xp = CharacterProgression.getXpFromEnemy(target.combatStats, this.progression.level);
        this.progression.addXp(xp);
      }
    };

    // Initialize projectile system
    this.projectileManager = new ProjectileManager({
      world: this.world,
      combatManager: this.combatManager,
      audioSystem: this.audioSystem,
      collisionSystem: this.collisionSystem
    });

    // Detached weapon animations (boomerang throw etc.) — world-space sprites
    // that play independently of the character
    this._detachedAnims = [];
    this._boomerangSheet = null;

    // Wire quickbar slot clicks to item use
    this.hudManager.onSlotClick = (index) => {
      if (this.player) {
        this.inventory.useItem(index, this.player);
        this.lootManager._applyEquipmentBonuses(this.player);
      }
    };
    // Wire combat references to NPC manager for enemy spawning
    this.npcManager._combatManager = this.combatManager;
    this.npcManager._projectileManager = this.projectileManager;
    this.npcManager._audioSystem = this.audioSystem;

    // Initialize dungeon generator
    this.dungeonGenerator = new DungeonGenerator({
      world: this.world,
      npcManager: this.npcManager,
      combatManager: this.combatManager,
      lootManager: this.lootManager,
      questManager: this.questManager,
      hudManager: this.hudManager
    });
    this._dungeonMode = false;
    this._savedSunIntensity = window.sunIntensity;
    this._savedSkewAngle = null;
    this._deathScreenActive = false;

    // Wire map renderer to game systems
    this.mapRenderer.world = this.world;
    this.mapRenderer.lootManager = this.lootManager;
    this.mapRenderer.dungeonGenerator = this.dungeonGenerator;
    this.mapRenderer.orbitalCamera = this.orbitalCamera;

    // Player character (initialized asynchronously)
    this.player = null;

    // Initialize UI elements (FPS display, sun vector and sun intensity controls)
    this.initUI();

    // Create NPC placement tool (renders into tool sheet)
    this.npcTool = new NPCTool({
      npcManager: this.npcManager,
      world: this.world,
      camera: this.camera,
      clickToMoveHandler: this.clickToMoveHandler,
      container: this.toolSheet.getContentContainer()
    });
    this.toolbar.setNPCTool(this.npcTool);
    this.npcTool.undoManager = this.undoManager;

    // Create impostor (vegetation) placement tool (shares tool sheet)
    this.impostorTool = new ImpostorTool({
      world: this.world,
      camera: this.camera,
      container: this.toolSheet.getContentContainer(),
      editor: this.voxelEditor,
      facePicker: this.facePicker,
      toolbar: this.toolbar
    });
    this.impostorTool.undoManager = this.undoManager;

    // Create audio control panel in Settings > Audio tab
    this.audioControlPanel = new AudioControlPanel(
      this.audioSystem,
      this.settingsPanel.getTabContainer('audio')
    );
    // Game reference will be set after player is initialized

    // Create standard world by default
    this.showStandardWorld().then(() => {
      // Initialize player character after world is loaded
      this.initPlayer();
    });

    // Time tracking for frame-rate independent updates
    this.lastTime = 0;
    this.deltaTime = 0;

    // Start game loop
    this.startGameLoop();
  }

  /**
   * Initialize player character
   */
  async initPlayer() {
    try {
      // Determine which character to load — restore saved selection, fall back to 'boy'
      // trixel_selected_character is set when switching to a legacy character and
      // cleared when confirming an LPC appearance, so its presence means "use legacy".
      const savedLPC = localStorage.getItem('trixel_lpc_appearance');
      const savedCharacterName = localStorage.getItem('trixel_selected_character');
      const useLPC = savedLPC && !savedCharacterName;
      const characterName = (savedCharacterName && CHARACTER_CONFIGS[savedCharacterName])
        ? savedCharacterName
        : 'boy';

      const characterPath = `/characters/${characterName}`;
      const metadata = await CharacterMetadata.load(characterPath);
      Debug.log('Character', 'Loaded character metadata:', metadata.getSummary());

      // Load sprite atlas (prefers pre-built, falls back to runtime building)
      Debug.log('Character', 'Loading sprite atlas...');
      const atlas = await OptimizedAtlasLoader.load(characterPath, metadata);
      Debug.log('Character', `Sprite atlas loaded: ${atlas.width}x${atlas.height}${atlas.isPrebuilt ? ' (pre-built)' : ' (runtime)'}`);

      // Create player character at spawn position
      const config = CHARACTER_CONFIGS[characterName];

      this.player = new Character({
        id: 'player',
        x: Game.SPAWN_POSITION.x,
        y: Game.SPAWN_POSITION.y, // Start above ground so we can fall to detect it
        z: Game.SPAWN_POSITION.z
      });
      this.player.metadata = metadata;
      this.player.characterPath = characterPath;
      this.player.characterName = characterName;
      this.player.config = config;

      // Set character-specific animation names from config
      this.player.animations = {
        idle: config.idle,
        walk: config.walk,
        run: config.run
      };

      // Create sprite with atlas
      const sprite = new CharacterSprite(this.player, {
        spriteWidth: metadata.spriteWidth,
        spriteHeight: metadata.spriteHeight,
        scale: 2 // Scale up sprite for visibility
      });

      // Auto-sync sprite offset whenever the atlas changes.
      // LPC atlases (_isLPC) use -0.6, legacy atlases use per-character config offsets.
      // This fires synchronously inside setAtlas(), so the offset is always correct
      // for whatever atlas is currently displayed — no async timing issues.
      sprite.onAtlasChange = (atlas) => {
        const isLPC = !!atlas._isLPC;
        const offset = isLPC
          ? LPC_SPRITE_OFFSET
          : (this.player.config?.spriteOffset || DEFAULT_SPRITE_OFFSET);
        sprite.spriteOffset = { ...offset };
        if (this.characterDebug) {
          this.characterDebug.spriteOffsetX = offset.x;
          this.characterDebug.spriteOffsetY = offset.y;
          this.characterDebug.spriteOffsetZ = offset.z;
          this.characterDebug.spriteScale = offset.scale || 1.4;
          this.characterDebug.syncSlidersFromValues();
        }
        // Reveal sprite once the correct atlas (LPC or final) is loaded
        if (isLPC || !sprite._waitingForLPC) {
          sprite.element.style.visibility = '';
        }
        // Broadcast immediately when LPC atlas finishes compositing so remote
        // peers see the LPC state without waiting for the next heartbeat.
        // broadcastImmediately() called from character creator fires before
        // composition finishes (_isLPC still false on old atlas), so lpcA is
        // omitted — this ensures the corrected broadcast goes out.
        if (isLPC && this.playerSync) {
          this.playerSync.broadcastImmediately();
        }
      };

      // Hide sprite if we'll be loading an LPC appearance to prevent "boy" flash
      if (useLPC) {
        sprite._waitingForLPC = true;
        sprite.element.style.visibility = 'hidden';
      }

      sprite.setAtlas(atlas); // Initial atlas → fires callback → sets offset
      sprite.setCollisionSystem(this.collisionSystem);
      this.player.sprite = sprite;

      // Restore LPC appearance if saved (from character creator).
      // Triggers async composition — when it completes, setAtlas fires
      // the callback above which switches offset and reveals the sprite.
      if (useLPC) {
        try {
          // Ensure LPC manifest is loaded before building layer stack —
          // without this, equipment layers (cloaks, armour, etc.) are silently
          // skipped because buildLayerStack() returns [] when manifest is null.
          await loadLPCManifest();
          const appearance = JSON.parse(savedLPC);
          sprite.setLPCAppearance(appearance);
          const equippedItemDefs = {};
          for (const [slot, entry] of Object.entries(this.inventory.equipped)) {
            if (entry) equippedItemDefs[slot] = entry.def || ITEM_DEFS[entry.itemId];
          }
          sprite.updateEquipment(equippedItemDefs);
          // Sync debug dropdown — LPC custom has no legacy id
          if (this.characterDebug) this.characterDebug.syncCharacterDropdown(null);
        } catch (e) {
          // Invalid saved appearance, ignore
        }
      }

      // Create animator with atlas
      const animator = new CharacterAnimator(this.player, metadata);
      animator.setAtlas(atlas);
      this.player.animator = animator;
      animator.setState(config.idle);
      animator.onDirectionChange('south');

      // Create controller
      const controller = new CharacterController(this.player, {
        inputManager: this.inputManager,
        camera: this.camera,
        collisionSystem: this.collisionSystem,
        groundDetector: this.groundDetector,
        inventory: this.inventory
      });
      this.player.controller = controller;

      // Wire attack sounds to audio system
      controller.onAttack = (character, actionName) => {
        if (this.audioSystem) {
          // Map animation names to weapon types for SFX
          const config = character.config || {};
          let weaponType = 'punch';
          if (actionName === config.kick) weaponType = 'kick';
          else if (actionName === config.special) weaponType = 'fireball';
          const isPlayer = character === this.player;
          this.audioSystem.playAttackSound(character, weaponType, isPlayer);
        }
      };

      // Wire combat system: register attacks for hit detection
      // Intercept E key (light attack) for chest opening in dungeon mode
      controller.onAttackStart = (character, attackType) => {
        if (this._dungeonMode && attackType === 'light' && this.dungeonGenerator) {
          if (this.dungeonGenerator.tryOpenChest(character)) {
            if (character.animator) character.animator.setState('picking-up', true);
            return; // chest opened, skip combat attack
          }
        }
        // Check and consume resources (mana/stamina) before attack
        if (character === this.player && !this._tryConsumeAttackResources(attackType)) {
          return; // not enough resources
        }
        // Broadcast attack animation to peers
        if (this.playerSync && controller.attackPressed) {
          this.playerSync.broadcastAction({ type: 'anim', animName: controller.attackPressed });
        }
        // Special (F): ALWAYS fireball regardless of weapon
        if (attackType === 'special' && this.projectileManager) {
          const proj = this.projectileManager.spawnFireball(character);
          if (this.playerSync && proj) {
            this.playerSync.broadcastAction({
              type: 'fireball',
              projectileType: 'fireball',
              x: proj.impostor.x,
              y: proj.impostor.y,
              z: proj.impostor.z,
              angle: character.facingAngle
            });
          }
          return;
        }
        // Light/Heavy (E/R): spawn weapon projectile for ranged weapons
        if ((attackType === 'light' || attackType === 'heavy') && this.projectileManager) {
          const weaponDef = this._getEquippedWeaponDef();
          const projType = weaponDef?.projectileType;
          if (projType) {
            const proj = this.projectileManager.spawnProjectile(character, projType);
            if (this.playerSync && proj) {
              this.playerSync.broadcastAction({
                type: 'fireball',
                projectileType: projType,
                x: proj.impostor.x,
                y: proj.impostor.y,
                z: proj.impostor.z,
                angle: character.facingAngle
              });
            }
            return;
          }
        }
        // Boomerang: detach the weapon sprite so it plays at the throw
        // position independently. The boomerang weapon layer is excluded
        // from the composite (in LPCLayerDefinitions), so the character
        // shows the throw body animation without a double boomerang.
        if ((attackType === 'light' || attackType === 'heavy')) {
          const wDef = this._getEquippedWeaponDef();
          if (wDef?.lpcLayer?.layerId === 'weapon_ranged_boomerang') {
            this._spawnDetachedWeaponAnim(character);
            if (this.playerSync) {
              this.playerSync.broadcastAction({
                type: 'boomerang-throw',
                angle: character.facingAngle
              });
            }
          }
        }
        if (this.combatManager) {
          this.combatManager.registerAttack(character, attackType);
        }
      };

      // Initialize player combat stats
      this.player.combatStats = new CombatStats({
        maxHealth: 100,
        attackPower: 10,
        defense: 0,
        iFrames: 0.5
      });

      // Add floating resource bars to player sprite (visible when not full)
      if (this.player.sprite?.element) {
        const spriteEl = this.player.sprite.element;

        const hpBar = document.createElement('div');
        hpBar.className = 'player-hp-bar';
        hpBar.innerHTML = '<div class="player-hp-fill"></div>';
        spriteEl.appendChild(hpBar);
        this.player._floatingHpBar = hpBar;
        this.player._floatingHpFill = hpBar.querySelector('.player-hp-fill');

        const spBar = document.createElement('div');
        spBar.className = 'player-sp-bar';
        spBar.innerHTML = '<div class="player-sp-fill"></div>';
        spriteEl.appendChild(spBar);
        this.player._floatingSpBar = spBar;
        this.player._floatingSpFill = spBar.querySelector('.player-sp-fill');

        const mpBar = document.createElement('div');
        mpBar.className = 'player-mp-bar';
        mpBar.innerHTML = '<div class="player-mp-fill"></div>';
        spriteEl.appendChild(mpBar);
        this.player._floatingMpBar = mpBar;
        this.player._floatingMpFill = mpBar.querySelector('.player-mp-fill');
      }

      // Load saved character state (XP, level, inventory)
      const savedData = this.characterSave.load();
      if (savedData) {
        this.characterSave.applyTo(savedData, this.progression, this.inventory, this.player.combatStats);
        this.lootManager._applyEquipmentBonuses(this.player);
      }

      controller.onJump = (character) => {
        if (this.audioSystem) {
          this.audioSystem.playJumpSound(character, character === this.player);
        }
        // Broadcast jump animation to peers
        if (this.playerSync) {
          const jumpAnim = character.config?.jump || 'jumping-1';
          this.playerSync.broadcastAction({ type: 'anim', animName: jumpAnim });
        }
      };

      controller.onLand = (character, dropHeight) => {
        if (this.audioSystem) {
          this.audioSystem.playLandingSound(character, character === this.player, dropHeight);
        }
      };

      // Create shadow
      const shadow = new CharacterShadow(this.player, this.collisionSystem);
      this.player.shadow = shadow;

      // Create path follower for click-to-move
      const pathFollower = new PathFollower(this.player, {
        collisionSystem: this.collisionSystem,
        onRepathRequest: (destination) => {
          // Repath when stuck
          const path = this.pathFinder.findPath(
            { x: this.player.x, y: this.player.y, z: this.player.z },
            destination
          );
          if (path) {
            pathFollower.setPath(path);
          }
        }
      });
      this.player.pathFollower = pathFollower;

      // Set player reference for click-to-move handler
      this.clickToMoveHandler.setPlayer(this.player);

      // Set up click-to-attack (left click = punch, right click = fireball)
      this._setupClickToAttack();

      // Set up keyboard attack handlers (E/R/F keys — direct path like joystick)
      this._setupKeyboardAttacks();

      // Set player reference for dialog system
      if (this.dialogManager) {
        this.dialogManager.setPlayer(this.player);
      }

      // Set player reference for debug UI (offset sync handled by onAtlasChange callback)
      if (this.characterDebug) {
        this.characterDebug.player = this.player;
        this.characterDebug.game = this;
      }

      // Add to world
      this.world.addCharacter(this.player);

      // Rebuild collision index now that world is set up
      this.collisionSystem.rebuildIndex();

      // Build initial navmesh
      this.rebuildNavMesh();

      // Find the HIGHEST ground surface at the player's XZ position
      // Search from very high up to find the topmost surface
      const groundY = this.groundDetector.findGroundHeight(
        this.player.x,
        this.player.z,
        this.player.width,
        this.player.depth,
        1000,  // Start searching from very high up
        1000   // Search distance down
      );

      if (groundY !== null) {
        this.player.y = groundY;
        this.player.isGrounded = true;
        Debug.log('Character', 'Player placed on highest surface at Y:', groundY);
      } else {
        // No ground found - place at y=0 as fallback
        this.player.y = 0;
        Debug.warn('Character', 'No ground found at player position, using Y=0');
      }

      // Update orbital camera target to player's final position (after ground detection)
      // Camera is already enabled from constructor, just update target for smooth follow
      this.orbitalCamera.setTarget(this.player.x, this.player.y, this.player.z);

      // Create mobile action buttons
      // A = Jump, B = Punch (E), X = Kick (R), Y = Special
      this.actionButtons = new ActionButtons({
        onJump: () => {
          if (this.player && this.player.controller && this.player.isGrounded) {
            this.player.controller.jump();
            this.player.controller.jumpedThisFrame = true;
            const jumpAnim = this.player.config.jump;
            this.player.triggerAction(jumpAnim);
            if (this.audioSystem) this.audioSystem.playJumpSound(this.player, true);
            if (this.playerSync) this.playerSync.broadcastAction({ type: 'anim', animName: jumpAnim });
          }
        },
        onAttack: () => {
          if (this.player) {
            if (this.player.animator?.isPlayingAttack()) return;
            this._faceNearestEnemy(5);
            const weaponAnims = this._getWeaponAttackAnims();
            const animName = weaponAnims?.[0] || this.player.config.punch || 'lead-jab';
            this.player.triggerAction(animName);
            if (this.audioSystem) this.audioSystem.playAttackSound(this.player, 'punch', true);
            if (this.playerSync) this.playerSync.broadcastAction({ type: 'anim', animName });
            const ctrl = this.player.controller;
            if (ctrl?.onAttackStart) ctrl.onAttackStart(this.player, 'light');
          }
        },
        onAction1: () => {
          // X button: chest opening takes priority in dungeon mode
          if (this._dungeonMode && this.dungeonGenerator && this.player) {
            if (this.dungeonGenerator.tryOpenChest(this.player)) {
              this.player.triggerAction('picking-up');
              return; // chest opened, skip kick
            }
          }
          // X button: dialog interaction takes priority, otherwise kick
          if (this.dialogManager && (this.dialogManager.isActive || this.dialogManager._nearestNPC)) {
            this.dialogManager.onMobileInteract();
            return;
          }
          if (this.player) {
            if (this.player.animator?.isPlayingAttack()) return;
            this._faceNearestEnemy(5);
            const weaponAnims = this._getWeaponAttackAnims();
            const animName = weaponAnims?.[1] || this.player.config.kick || 'roundhouse-kick';
            this.player.triggerAction(animName);
            if (this.audioSystem) this.audioSystem.playAttackSound(this.player, 'kick', true);
            if (this.playerSync) this.playerSync.broadcastAction({ type: 'anim', animName });
            const ctrl = this.player.controller;
            if (ctrl?.onAttackStart) ctrl.onAttackStart(this.player, 'heavy');
          }
        },
        onAction2: () => {
          if (this.player) {
            if (this.player.animator?.isPlayingAttack()) return;
            const ctrl = this.player.controller;
            const animName = this.player.config.special;
            this.player.triggerAction(animName);
            if (this.audioSystem) this.audioSystem.playAttackSound(this.player, 'fireball', true);
            if (this.playerSync) this.playerSync.broadcastAction({ type: 'anim', animName });
            if (ctrl?.onAttackStart) ctrl.onAttackStart(this.player, 'special');
          }
        }
      });

      // Set game reference for audio control panel
      if (this.audioControlPanel) {
        this.audioControlPanel.setGame(this);
      }

      // Wire map renderer to player
      this.mapRenderer.player = this.player;

      Debug.log('Character', 'Player character initialized');
    } catch (error) {
      Debug.error('Character', 'Failed to initialize player:', error);
    }
  }

  /**
   * Switch to a different character
   * @param {string} characterName - Name of character folder (e.g., 'boy', 'mage')
   */
  async switchCharacter(characterName) {
    Debug.log('Character', `switchCharacter called with: ${characterName}`);

    if (!this.player) {
      Debug.error('Character', 'switchCharacter: No player exists');
      return;
    }

    // Get character config
    const config = CHARACTER_CONFIGS[characterName];
    if (!config) {
      Debug.error('Character', `No config found for character: ${characterName}`);
      return;
    }

    const characterPath = `/characters/${characterName}`;

    try {
      // Load new character metadata
      const metadata = await CharacterMetadata.load(characterPath);
      Debug.log('Character', 'Loaded character metadata:', metadata.getSummary());

      // Load sprite atlas
      const atlas = await OptimizedAtlasLoader.load(characterPath, metadata);
      Debug.log('Character', `Sprite atlas loaded: ${atlas.width}x${atlas.height}${atlas.isPrebuilt ? ' (pre-built)' : ' (runtime)'}`);

      // Update player metadata, path, and config
      this.player.metadata = metadata;
      this.player.characterPath = characterPath;
      this.player.characterName = characterName;
      this.player.config = config;
      // Note: nickname persists across character switches — no chat name update here

      // Update animation names from config
      this.player.animations = {
        idle: config.idle,
        walk: config.walk,
        run: config.run
      };

      // Update sprite atlas and per-character offset
      if (this.player.sprite) {
        // Clear runtime LPC state when switching to a legacy character
        // (keep trixel_lpc_appearance in localStorage so the loadout is preserved)
        this.player.sprite._lpcAppearance = null;
        this.player.sprite._lpcEquipmentHash = null;
        this.player.sprite._pendingComposite = null;

        this.player.sprite.spriteWidth = metadata.spriteWidth;
        this.player.sprite.spriteHeight = metadata.spriteHeight;
        this.player.sprite.setAtlas(atlas); // fires onAtlasChange → sets config offset + syncs debug viz
      }

      // Update animator atlas and reset to idle
      if (this.player.animator) {
        this.player.animator.metadata = metadata;
        this.player.animator.setAtlas(atlas);
        this.player.animator.setState(config.idle);
        this.player.animator.onDirectionChange(this.player.animator.currentDirection || 'south');
      }

      // Persist selected legacy character so it survives reload
      localStorage.setItem('trixel_selected_character', characterName);

      // Sync debug dropdown to new character
      if (this.characterDebug) this.characterDebug.syncCharacterDropdown(characterName);

      Debug.log('Character', `Switched to character: ${characterName}`);

      // Refresh paperdoll sprite with new character atlas
      this._updateCharacterWindow();
    } catch (error) {
      Debug.error('Character', 'Failed to switch character:', error);
    }
  }

  /**
   * Respawn the player character on the ground after world changes
   * Finds the highest ground surface at player's current XZ position,
   * or finds a valid cuboid to spawn on if no ground at current position
   */
  respawnPlayer() {
    if (!this.player) return;

    // In dungeon mode, respawn at level entry point
    if (this._dungeonMode && this.dungeonGenerator) {
      const level = this.dungeonGenerator.getCurrentLevel();
      if (level && level.entryPoint) {
        this.player.x = level.entryPoint.x;
        this.player.y = level.entryPoint.y;
        this.player.z = level.entryPoint.z;
        this.player.isGrounded = true;
        this.player.velocity.y = 0;
        this.orbitalCamera.setTarget(this.player.x, this.player.y, this.player.z);
        this.orbitalCamera.snapToTarget();
        this.camera.update(0);
        this.world.update(0);
        return;
      }
    }

    // First try to find ground at current position
    let groundY = this.groundDetector.findGroundHeight(
      this.player.x,
      this.player.z,
      this.player.width,
      this.player.depth,
      1000,  // Start searching from very high up
      1000   // Search distance down
    );

    if (groundY !== null) {
      this.player.y = groundY;
      this.player.isGrounded = true;
      this.player.velocity.y = 0;
      Debug.log('Character', 'Player respawned on ground at Y:', groundY);
    } else {
      // No ground at current position - find any valid cuboid to spawn on
      const spawnPoint = this.findValidSpawnPoint();
      if (spawnPoint) {
        this.player.x = spawnPoint.x;
        this.player.y = spawnPoint.y;
        this.player.z = spawnPoint.z;
        this.player.isGrounded = true;
        this.player.velocity.y = 0;
        Debug.log('Character', 'Player respawned on cuboid at:', spawnPoint);
      } else {
        // No cuboids at all - create a default spawn platform
        this.createDefaultSpawnPlatform();
        this.player.x = 0.5;
        this.player.y = 1;
        this.player.z = 0.5;
        this.player.isGrounded = true;
        this.player.velocity.y = 0;
        Debug.log('Character', 'Created default spawn platform, player placed at origin');
      }
    }

    // Update orbital camera target and snap immediately (no smoothing)
    this.orbitalCamera.setTarget(this.player.x, this.player.y, this.player.z);
    this.orbitalCamera.snapToTarget();

    // Force immediate update so player and any new cuboid are visible
    this.camera.update(0);
    this.world.update(0);
  }

  /**
   * Find a valid spawn point on any existing cuboid
   * Returns the center-top of the highest suitable cuboid, or null if none found
   * @returns {{x: number, y: number, z: number}|null}
   */
  findValidSpawnPoint() {
    if (this.world.cuboids.size === 0) return null;

    let bestCuboid = null;
    let bestY = -Infinity;

    // Find the highest cuboid that's not water/non-collision
    for (const cuboid of this.world.cuboids.values()) {
      // Skip non-collision cuboids (water, etc.)
      if (cuboid.hasNoCollision && cuboid.hasNoCollision()) continue;

      const topY = cuboid.y + cuboid.height;
      if (topY > bestY) {
        bestY = topY;
        bestCuboid = cuboid;
      }
    }

    if (!bestCuboid) {
      // All cuboids are non-collision, just pick any one
      const firstCuboid = this.world.cuboids.values().next().value;
      if (firstCuboid) {
        return {
          x: firstCuboid.x + firstCuboid.width / 2,
          y: firstCuboid.y + firstCuboid.height,
          z: firstCuboid.z + firstCuboid.depth / 2
        };
      }
      return null;
    }

    // Return center-top of the best cuboid
    return {
      x: bestCuboid.x + bestCuboid.width / 2,
      y: bestCuboid.y + bestCuboid.height,
      z: bestCuboid.z + bestCuboid.depth / 2
    };
  }

  /**
   * Create a default spawn platform when the world has no cuboids
   * Creates a grass-textured platform at the origin with grouped material (triplanar)
   */
  createDefaultSpawnPlatform() {
    // Create material for each face (cloned to avoid shared state)
    const createGrassMaterial = () => {
      const m = Material.texture('textures/grass.jpg');
      m.borderWidth = 0;
      return m;
    };

    const materials = {
      top: createGrassMaterial(),
      bottom: createGrassMaterial(),
      front: createGrassMaterial(),
      back: createGrassMaterial(),
      left: createGrassMaterial(),
      right: createGrassMaterial()
    };

    // Add the cuboid with materials
    const cuboid = this.world.addCuboid(0, 0, 0, 3, 1, 3, materials);
    const cuboidKey = cuboid.getKey();

    // Update the world first - ensures material refresh happens before triplanar is applied
    this.world.update();

    // Create a material group with triplanar projection
    const groupManager = this.world.materialGroupManager;
    const group = groupManager.createGroup({
      name: 'Spawn Platform',
      material: createGrassMaterial()
    });

    // Assign all 6 faces to the group
    const faces = ['top', 'bottom', 'front', 'back', 'left', 'right'];
    const faceAssignments = faces.map(face => ({ cuboidKey, face }));
    groupManager.assignFacesToGroup(group.id, faceAssignments);

    // Apply the triplanar projection
    groupManager.applyGroupMaterial(group.id);

    // Force render to make cuboid visible immediately
    this.world.forceRender();

    // Refresh systems
    this.collisionSystem.rebuildIndex();
    this.rebuildNavMesh();
    this.materialEditor.updateCuboidList();
    Debug.log('World', 'Created default spawn platform at origin with grouped material');
  }

  /**
   * Set up mode switching between game mode and editor/material modes
   * In game mode: click-to-move enabled, face picker disabled
   * In other modes: click-to-move disabled, face picker enabled
   */
  setupModeSwitching() {
    // Listen for tab changes
    window.addEventListener('sidebar-tab-change', (e) => {
      const tab = e.detail.tab;

      // Reset touch/pointer state to prevent stale drag state across modes
      this.inputManager.resetTouchState();

      if (tab === 'game') {
        // Game mode: clear selections, enable click-to-move, disable face picker
        this.facePicker.clearSelection();
        this.facePicker.setEnabled(false);
        this.clickToMoveHandler.enable();
        this.world.forceTransformUpdate();
        this.world.update();
      } else {
        // Editor modes: disable click-to-move, face picker controlled by submenu
        this.clickToMoveHandler.disable();
      }
    });

    // Face picker is only enabled in the material editor submenu
    window.addEventListener('toolbar-submenu-change', (e) => {
      const level = e.detail.level;
      if (level === 'material') {
        this.facePicker.setEnabled(true);
      } else {
        this.facePicker.setEnabled(false);
        // Clear selections when leaving material editor
        this.facePicker.clearSelection();
        this.world.forceTransformUpdate();
        this.world.update();
      }
    });

    // Initialize in game mode (default tab is 'game')
    this.facePicker.setEnabled(false);
    this.clickToMoveHandler.enable();
  }

  /**
   * Rebuild the navigation mesh from current world state
   */
  rebuildNavMesh() {
    const navGraph = this.navMeshBuilder.build();
    this.pathFinder.setNavGraph(navGraph);
    this.clickToMoveHandler.navGraph = navGraph;
    Debug.log('NavMesh', 'NavMesh rebuilt with', navGraph.size, 'nodes');
  }

  // ---- Dungeon System ----

  /**
   * Show dungeon configuration modal.
   */
  showDungeonConfig() {
    // Remove existing modal if any
    const existing = document.getElementById('dungeon-config-modal');
    if (existing) existing.remove();

    const modal = document.createElement('div');
    modal.id = 'dungeon-config-modal';
    modal.style.cssText = `
      position: fixed; inset: 0; z-index: 10000;
      background: rgba(0,0,0,0.7); display: flex;
      align-items: center; justify-content: center;
    `;

    const themeOptions = DungeonTheme.getThemeNames()
      .map(name => {
        const t = DungeonTheme.getTheme(name);
        return `<option value="${name}">${t.name || name}</option>`;
      }).join('');

    const panel = document.createElement('div');
    panel.style.cssText = `
      background: #1e1e2e; color: #cdd6f4; border-radius: 12px;
      padding: 24px 32px; min-width: 300px; font-family: sans-serif;
      box-shadow: 0 8px 32px rgba(0,0,0,0.5);
    `;
    // Count available keys in inventory
    const keyTypes = [
      { id: 'bronze-key', tier: 'bronze', name: 'Bronze Key', color: '#cd7f32' },
      { id: 'silver-key', tier: 'silver', name: 'Silver Key', color: '#c0c0c0' },
      { id: 'gold-key',   tier: 'gold',   name: 'Gold Key',   color: '#ffd700' }
    ];
    const keyCounts = {};
    for (const kt of keyTypes) {
      keyCounts[kt.id] = 0;
      for (const slot of (this.inventory?.slots || [])) {
        if (slot && slot.itemId === kt.id) keyCounts[kt.id] += slot.quantity;
      }
      // Also count legacy dungeon-key as bronze
      if (kt.id === 'bronze-key') {
        for (const slot of (this.inventory?.slots || [])) {
          if (slot && slot.itemId === 'dungeon-key') keyCounts[kt.id] += slot.quantity;
        }
      }
    }
    const keyOptionsHtml = keyTypes.map(kt => {
      const count = keyCounts[kt.id];
      const disabled = count <= 0 ? 'disabled' : '';
      return `<option value="${kt.tier}" ${disabled} style="color:${kt.color}">${kt.name} (${count}) - ${kt.tier === 'bronze' ? '1.2x' : kt.tier === 'silver' ? '1.5x' : '2x'} loot</option>`;
    }).join('');

    panel.innerHTML = `
      <h2 style="margin: 0 0 16px; font-size: 20px; color: #cba6f7;">Enter Dungeon</h2>
      <label style="display:block; margin-bottom: 12px;">
        <span style="display:block; margin-bottom: 4px; font-size: 13px; color: #a6adc8;">Theme</span>
        <select id="dc-theme" style="width:100%; padding: 6px 8px; background: #313244; color: #cdd6f4; border: 1px solid #45475a; border-radius: 6px;">
          ${themeOptions}
        </select>
      </label>
      <label style="display:block; margin-bottom: 12px;">
        <span style="display:block; margin-bottom: 4px; font-size: 13px; color: #a6adc8;">Levels</span>
        <select id="dc-levels" style="width:100%; padding: 6px 8px; background: #313244; color: #cdd6f4; border: 1px solid #45475a; border-radius: 6px;">
          ${(() => {
            const maxFloors = DungeonConfig.getMaxLevels(this.progression?.level || 1);
            let opts = '';
            for (let n = 1; n <= maxFloors; n++) {
              opts += `<option value="${n}"${n === maxFloors ? ' selected' : ''}>${n}</option>`;
            }
            return opts;
          })()}
        </select>
      </label>
      <label style="display:block; margin-bottom: 12px;">
        <span style="display:block; margin-bottom: 4px; font-size: 13px; color: #a6adc8;">Use Key (optional - adapts to your level)</span>
        <select id="dc-key" style="width:100%; padding: 6px 8px; background: #313244; color: #cdd6f4; border: 1px solid #45475a; border-radius: 6px;">
          <option value="none">No Key (standard dungeon)</option>
          ${keyOptionsHtml}
        </select>
      </label>
      <div style="display: flex; gap: 8px; margin-top: 20px;">
        <button id="dc-start" style="flex:1; padding: 10px; background: #89b4fa; color: #1e1e2e; border: none; border-radius: 6px; cursor: pointer; font-weight: 600; font-size: 14px;">Start</button>
        <button id="dc-cancel" style="flex:1; padding: 10px; background: #45475a; color: #cdd6f4; border: none; border-radius: 6px; cursor: pointer; font-size: 14px;">Cancel</button>
      </div>
    `;

    modal.appendChild(panel);
    document.body.appendChild(modal);

    document.getElementById('dc-cancel').addEventListener('click', () => modal.remove());
    modal.addEventListener('click', (e) => { if (e.target === modal) modal.remove(); });

    document.getElementById('dc-start').addEventListener('click', () => {
      const theme = document.getElementById('dc-theme').value;
      const levelCount = parseInt(document.getElementById('dc-levels').value, 10);
      const keySelect = document.getElementById('dc-key').value;
      modal.remove();

      const config = { theme, levelCount, targetLevel: this.progression?.level || 1 };

      // Handle keyed dungeon
      if (keySelect !== 'none') {
        const keyIdMap = { bronze: 'bronze-key', silver: 'silver-key', gold: 'gold-key' };
        const keyItemId = keyIdMap[keySelect];
        if (keyItemId && this.inventory?.hasItem(keyItemId)) {
          this.inventory.removeItemById(keyItemId);
          config.keyTier = keySelect;
          config.lootMultiplier = KEY_TIERS[keySelect]?.lootMul || 1;
        }
      }

      // In multiplayer: use instanced dungeon flow
      if (this.networkManager?.isConnected && this.dungeonInstanceManager) {
        this.dungeonInstanceManager.hostDungeon(config);
      } else {
        this.startDungeon(config);
      }
    });
  }

  /**
   * Start a dungeon run.
   * @param {Object} config
   */
  async startDungeon(config = {}) {
    this._dungeonMode = true;
    if (this.toolbar) this.toolbar._render();
    if (this.windowManager) this.windowManager.show('objectives');
    if (this.undoManager) this.undoManager.clear();
    this._savedSunIntensity = window.sunIntensity;
    this._savedSkewAngle = this.camera.skewAngle;
    this._savedMinZoom = this.camera.MIN_ZOOM;

    // Switch cloud backdrop to dungeon-specific atmosphere
    if (this.cloudBackdrop) {
      this.cloudBackdrop.setPreset(config.theme || 'stone_crypt');
    }

    // Clamp camera skew for dungeon mode (prevent side-on / below-horizon view)
    this.camera.minSkewAngle = 0.5;
    if (this.camera.skewAngle < 0.5) {
      this.camera.skewAngle = 0.5;
    }

    // Limit zoom-out in dungeon mode to keep viewport within streamed area
    // Load radius is 3 chunks × 16 units = 48 game units = 2880px.
    // At MIN_ZOOM 0.5, viewport half-width ≈ 1920px — well within streamed bounds.
    this.camera.MIN_ZOOM = 0.5;
    if (this.camera.zoomLevel < this.camera.MIN_ZOOM) {
      this.camera.zoomLevel = this.camera.MIN_ZOOM;
    }
    if (this.orbitalCamera) {
      if (this.orbitalCamera.targetZoomLevel < this.camera.MIN_ZOOM) {
        this.orbitalCamera.targetZoomLevel = this.camera.MIN_ZOOM;
      }
      if (this.orbitalCamera.currentZoomLevel < this.camera.MIN_ZOOM) {
        this.orbitalCamera.currentZoomLevel = this.camera.MIN_ZOOM;
      }
    }

    // Save current world state so edits persist when returning from dungeon
    this._savedWorldState = this.world.toJSON();
    this._savedPlayerPos = this.player ? { x: this.player.x, y: this.player.y, z: this.player.z } : null;

    // Clear current world state
    this.world.clear();
    this.npcManager.removeAll();
    this.lootManager.clearAll();

    // Generate and start dungeon (targetLevel, keyTier, lootMultiplier flow through config)
    if (!config.targetLevel) config.targetLevel = this.progression?.level || 1;
    this.dungeonGenerator.generate(config);

    // Wire loot pickup tracking for dungeon persistence
    this.lootManager.onItemCollected = (netId) => {
      if (this.dungeonGenerator) this.dungeonGenerator.onLootPickedUp(netId);
    };

    // Wire level-loaded callback
    this.dungeonGenerator.onLevelLoaded = (level, levelIndex, spawnAt) => {
      // Clear projectiles from previous level
      if (this.projectileManager) this.projectileManager.clearAll();

      // Teleport player to spawn point (exit area when returning via back-portal)
      if (this.player) {
        let sp;
        if (spawnAt === 'exit' && level.exitPoint) {
          sp = { x: level.exitPoint.x + 2, y: level.exitPoint.y, z: level.exitPoint.z + 0.5 };
        } else if (level.entryPoint) {
          sp = level.entryPoint;
        }
        if (sp) {
          this.player.x = sp.x;
          this.player.y = sp.y;
          this.player.z = sp.z;
          this.player.isGrounded = true;
          this.player.velocity.y = 0;

          // Reset player health
          if (this.player.combatStats) {
            this.player.combatStats.reset();
          }

          this.orbitalCamera.setTarget(this.player.x, this.player.y, this.player.z);
          this.orbitalCamera.snapToTarget();
        }
      }
    };

    // Wire dungeon completion
    this.dungeonGenerator.onDungeonComplete = () => {
      // Show victory screen with return button
      this._showVictoryScreen();
    };

    // Set player for damage numbers (HUD overlay stays hidden —
    // player uses floating over-character health bar instead)
    this.hudManager.setPlayer(this.player);

    await this.dungeonGenerator.startDungeon();
  }

  /**
   * Start a multiplayer dungeon using a shared seed.
   * Called by DungeonInstanceManager after room transition.
   * Unlike startDungeon(), this does NOT save world state (already saved by DIM)
   * and uses the provided seed for deterministic generation across peers.
   * @param {Object} config - { theme, levelCount, seed }
   */
  async startMultiplayerDungeon(config) {
    this._dungeonMode = true;
    if (this.toolbar) this.toolbar._render();
    if (this.windowManager) this.windowManager.show('objectives');
    if (this.undoManager) this.undoManager.clear();
    this._savedSunIntensity = window.sunIntensity;
    this._savedSkewAngle = this.camera.skewAngle;
    this._savedMinZoom = this.camera.MIN_ZOOM;

    // Cloud backdrop
    if (this.cloudBackdrop) this.cloudBackdrop.setPreset(config.theme || 'stone_crypt');

    // Camera constraints
    this.camera.minSkewAngle = 0.5;
    if (this.camera.skewAngle < 0.5) this.camera.skewAngle = 0.5;
    this.camera.MIN_ZOOM = 0.5;
    if (this.camera.zoomLevel < this.camera.MIN_ZOOM) this.camera.zoomLevel = this.camera.MIN_ZOOM;
    if (this.orbitalCamera) {
      if (this.orbitalCamera.targetZoomLevel < this.camera.MIN_ZOOM) this.orbitalCamera.targetZoomLevel = this.camera.MIN_ZOOM;
      if (this.orbitalCamera.currentZoomLevel < this.camera.MIN_ZOOM) this.orbitalCamera.currentZoomLevel = this.camera.MIN_ZOOM;
    }

    // NOTE: World state already saved by DungeonInstanceManager before instance change.
    // Do NOT save again here — world is already cleared.

    // Clear current state (should already be cleared by DIM, but be safe).
    // Suppress mutation callbacks — WorldSync is active and would broadcast
    // these removals to peers still setting up their dungeon geometry.
    const savedOnMutation = this.world.onMutation;
    const savedMgmOnMutation = this.world.materialGroupManager?.onMutation;
    const savedNpcOnMutation = this.npcManager?.onMutation;
    this.world.onMutation = null;
    if (this.world.materialGroupManager) this.world.materialGroupManager.onMutation = null;
    if (this.npcManager) this.npcManager.onMutation = null;

    this.world.clear();
    this.npcManager.removeAll();
    if (this.lootManager) this.lootManager.clearAll();

    this.world.onMutation = savedOnMutation;
    if (this.world.materialGroupManager) this.world.materialGroupManager.onMutation = savedMgmOnMutation;
    if (this.npcManager) this.npcManager.onMutation = savedNpcOnMutation;

    // Generate dungeon from shared seed (deterministic across all peers)
    this.dungeonGenerator.generate(config);

    // Wire loot pickup tracking for dungeon persistence
    this.lootManager.onItemCollected = (netId) => {
      if (this.dungeonGenerator) this.dungeonGenerator.onLootPickedUp(netId);
    };

    // Wire level-loaded callback
    this.dungeonGenerator.onLevelLoaded = (level, levelIndex, spawnAt) => {
      if (this.projectileManager) this.projectileManager.clearAll();

      // Teleport player to spawn point (exit area when returning via back-portal)
      if (this.player) {
        let sp;
        if (spawnAt === 'exit' && level.exitPoint) {
          sp = { x: level.exitPoint.x + 2, y: level.exitPoint.y, z: level.exitPoint.z + 0.5 };
        } else if (level.entryPoint) {
          sp = level.entryPoint;
        }
        if (sp) {
          this.player.x = sp.x;
          this.player.y = sp.y;
          this.player.z = sp.z;
          this.player.isGrounded = true;
          this.player.velocity.y = 0;
          this.player.velocity.x = 0;
          this.player.velocity.z = 0;

          // Reset animation state to prevent stale walk/run after teleport
          this.player.setMovementState(false, false, true);

          if (this.player.combatStats) this.player.combatStats.reset();

          this.orbitalCamera.setTarget(this.player.x, this.player.y, this.player.z);
          this.orbitalCamera.snapToTarget();
        }
      }

      // Notify DungeonInstanceManager of level change
      if (this.dungeonInstanceManager?._isInDungeon) {
        this.dungeonInstanceManager.onLocalLevelChange(levelIndex);
      }
    };

    // Wire dungeon completion
    this.dungeonGenerator.onDungeonComplete = () => {
      this._showVictoryScreen();
    };

    // Set player for damage numbers (HUD overlay stays hidden)
    this.hudManager.setPlayer(this.player);

    await this.dungeonGenerator.startDungeon();
  }

  /**
   * Exit dungeon mode and return to normal world.
   */
  async exitDungeon() {
    // In multiplayer dungeon: delegate to DungeonInstanceManager for instance transition
    if (this.dungeonInstanceManager?._isInDungeon) {
      await this.dungeonInstanceManager.returnToWorld();
      return;
    }

    this._dungeonMode = false;
    if (this.toolbar) this.toolbar._render();
    if (this.windowManager) this.windowManager.hide('objectives');
    if (this.undoManager) this.undoManager.clear();
    if (this.projectileManager) this.projectileManager.clearAll();
    this.dungeonGenerator.cleanup();

    // Restore camera skew
    this.camera.minSkewAngle = undefined;
    if (this._savedSkewAngle !== null) {
      this.camera.skewAngle = this._savedSkewAngle;
    }

    // Restore zoom limit
    if (this._savedMinZoom !== undefined) {
      this.camera.MIN_ZOOM = this._savedMinZoom;
    }

    // Restore sun
    window.sunIntensity = this._savedSunIntensity;

    // Restore cloud backdrop to overworld
    if (this.cloudBackdrop) {
      this.cloudBackdrop.setPreset('overworld');
    }

    // Hide HUD
    this.hudManager.hide();
    this.questManager.reset();

    // Restore saved world state (edits persist) or fall back to loading from file
    if (this._savedWorldState) {
      this.npcManager.removeAll();
      this.world.fromJSON(this._savedWorldState);
      this.materialEditor.updateCuboidList();
      this._savedWorldState = null;
    } else {
      await this.showStandardWorld();
    }
    this.world.forceRender();

    // Restore player position or initialize fresh
    if (this._savedPlayerPos && this.player) {
      this.player.x = this._savedPlayerPos.x;
      this.player.y = this._savedPlayerPos.y;
      this.player.z = this._savedPlayerPos.z;
      this.player.isGrounded = true;
      this.player.velocity.y = 0;
      this.orbitalCamera.setTarget(this.player.x, this.player.y, this.player.z);
      this.orbitalCamera.snapToTarget();
      this._savedPlayerPos = null;
    } else {
      this.initPlayer();
    }
  }

  /**
   * Create game windows (inventory, character sheet, map) with starter content.
   * @private
   */
  _createGameWindows() {
    const wm = this.windowManager;

    // Inventory window
    this._invSelectedSlot = null; // { type: 'bag', index } or { type: 'equip', key }
    const invWin = wm.create({
      id: 'inventory',
      title: 'Inventory',
      width: 280,
      height: 420,
      x: 60,
      y: 80,
      minWidth: 220,
      minHeight: 300,
      icon: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a4 4 0 0 0-8 0v2"/></svg>',
      onShow: () => this._updateInventoryWindow(),
      onHide: () => { this._cleanupDrag(); this._invSelectedSlot = null; },
    });
    invWin.setContent(this._buildInventoryContent());

    // Character sheet window (paperdoll equipment + stats)
    const charWin = wm.create({
      id: 'character',
      title: 'Character',
      width: 320,
      height: 540,
      x: 356,
      y: 80,
      minWidth: 220,
      minHeight: 420,
      icon: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="8" r="4"/><path d="M5 20c0-4 3.5-7 7-7s7 3 7 7"/></svg>',
      onShow: () => this._updateCharacterWindow(),
      onHide: () => { this._cleanupDrag(); this._invSelectedSlot = null; },
    });
    charWin.setContent(this._buildCharacterContent());

    // Map window (bottom-right)
    const cRect = container.getBoundingClientRect();
    const mapWin = wm.create({
      id: 'map',
      title: 'Map',
      width: 250,
      height: 250,
      x: cRect.width - 266,
      y: cRect.height - 266,
      minWidth: 200,
      minHeight: 200,
      icon: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 6l7-3 8 3 7-3v15l-7 3-8-3-7 3z"/><line x1="8" y1="3" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="21"/></svg>',
    });
    mapWin.setContent(this._buildMapContent());

    // Objectives window (top-right, shown during dungeons)
    const objWin = wm.create({
      id: 'objectives',
      title: 'Objectives',
      width: 250,
      height: 300,
      x: cRect.width - 266,
      y: 16,
      minWidth: 200,
      minHeight: 180,
      icon: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="16" y2="17"/></svg>',
      onShow: () => this._updateObjectivesWindow(),
    });
    objWin.setContent(this._buildObjectivesContent());

    // Character Creator window
    const creatorCallbacks = {
      onConfirm: (appearance) => {
        if (this.player && this.player.sprite) {
          this.player.sprite.setLPCAppearance(appearance);
          // Offset is handled by onAtlasChange when composition completes
          const equippedItemDefs = {};
          for (const [slot, entry] of Object.entries(this.inventory.equipped)) {
            if (entry) equippedItemDefs[slot] = entry.def || ITEM_DEFS[entry.itemId];
          }
          this.player.sprite.updateEquipment(equippedItemDefs);
          if (this.playerSync) {
            this.playerSync._localEquipment = equippedItemDefs;
            this.playerSync.broadcastImmediately();
          }
          // Sync debug dropdown — LPC custom has no legacy id
          if (this.characterDebug) this.characterDebug.syncCharacterDropdown(null);
        }
        wm.hide('creator');
      },
      onCancel: () => { wm.hide('creator'); },
      getEquipment: () => {
        const defs = {};
        for (const [slot, entry] of Object.entries(this.inventory.equipped)) {
          if (entry) defs[slot] = entry.def || ITEM_DEFS[entry.itemId];
        }
        return defs;
      }
    };
    const creatorWin = wm.create({
      id: 'creator',
      title: 'Character Creator',
      width: 320,
      height: 500,
      x: 80,
      y: 40,
      minWidth: 280,
      minHeight: 400,
      icon: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="8" r="4"/><path d="M5 20c0-4 3.5-7 7-7s7 3 7 7"/><path d="M16 4l2 2-6 6"/></svg>',
      onShow: () => {
        // Rebuild creator each time window opens (fresh preview animation)
        if (this._characterCreator) this._characterCreator.destroy();
        this._characterCreator = new CharacterCreator(creatorCallbacks);
        creatorWin.setContent(this._characterCreator.buildContent());
      },
      onHide: () => {
        if (this._characterCreator) {
          this._characterCreator.destroy();
          this._characterCreator = null;
        }
      },
    });

    // NPC Creator window (standalone — also accessible from editor NPC tool)
    const npcCreatorCallbacks = {
      onConfirm: (npcConfig) => {
        // If NPC tool is available, set config there for click-to-place
        if (this.npcTool) {
          this.npcTool.setCharacter('__lpc_custom__');
          this.npcTool._pendingLPCConfig = npcConfig;
          this.npcTool._lpcSelected = true;
          this.npcTool._ensureLPCOption();
          window.dispatchEvent(new CustomEvent('npc-lpc-config-changed'));
        } else if (this.player && this.npcManager) {
          // Fallback: spawn at player position
          this.npcManager.spawnNPC({
            ...npcConfig,
            x: this.player.x + 2,
            y: this.player.y,
            z: this.player.z + 2
          });
        }
        wm.hide('npc-creator');
      },
      onCancel: () => { wm.hide('npc-creator'); }
    };
    const npcCreatorWin = wm.create({
      id: 'npc-creator',
      title: 'NPC Creator',
      width: 400,
      height: 550,
      x: 120,
      y: 40,
      minWidth: 340,
      minHeight: 450,
      icon: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="8" r="4"/><path d="M5 20c0-4 3.5-7 7-7s7 3 7 7"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="16" y1="11" x2="22" y2="11"/></svg>',
      onShow: () => {
        if (this._npcCreator) this._npcCreator.destroy();
        this._npcCreator = new NPCCreator(npcCreatorCallbacks);
        npcCreatorWin.setContent(this._npcCreator.buildContent());
      },
      onHide: () => {
        if (this._npcCreator) {
          this._npcCreator.destroy();
          this._npcCreator = null;
        }
      },
    });

    // Credits window
    const creditsWin = wm.create({
      id: 'credits',
      title: 'Credits',
      width: 360,
      height: 450,
      x: 100,
      y: 60,
      minWidth: 300,
      minHeight: 300,
      icon: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>',
      onShow: async () => {
        if (this._creditsScreen) this._creditsScreen.destroy();
        this._creditsScreen = new CreditsScreen();
        creditsWin.setContent(await this._creditsScreen.buildContent());
      },
      onHide: () => {
        if (this._creditsScreen) {
          this._creditsScreen.destroy();
          this._creditsScreen = null;
        }
      },
    });

    // Register keyboard shortcuts
    wm.registerShortcut('inventory', 'i');
    wm.registerShortcut('character', 'c');
    wm.registerShortcut('map', 'm');
    wm.registerShortcut('objectives', 'o');
    wm.registerShortcut('creator', 'v');
  }

  _buildInventoryContent() {
    this._invFilter = 'all';

    const el = document.createElement('div');
    el.className = 'inv-container';

    // Filter tab bar
    const tabBar = document.createElement('div');
    tabBar.className = 'inv-tab-bar';
    const tabs = [
      { id: 'all', icon: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>', title: 'All' },
      { id: 'consumable', icon: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M10 2h4v4l4 8a3 3 0 0 1-3 4H9a3 3 0 0 1-3-4l4-8V2z"/><line x1="9" y1="2" x2="15" y2="2"/><path d="M7.5 13h9" opacity="0.5"/></svg>', title: 'Consumables' },
      { id: 'equipment', icon: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2L4 5v6c0 5.5 3.4 10.3 8 11.5 4.6-1.2 8-6 8-11.5V5l-8-3z"/><path d="M8 11l3 3 5-6" stroke-width="2"/></svg>', title: 'Equipment' },
    ];
    tabs.forEach(t => {
      const btn = document.createElement('button');
      btn.className = `inv-tab${t.id === 'all' ? ' active' : ''}`;
      btn.dataset.filter = t.id;
      btn.title = t.title;
      btn.innerHTML = t.icon;
      btn.addEventListener('click', () => {
        this._invFilter = t.id;
        tabBar.querySelectorAll('.inv-tab').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        this._updateInventoryWindow();
      });
      tabBar.appendChild(btn);
    });
    el.appendChild(tabBar);

    // Scrollable grid area
    const scrollArea = document.createElement('div');
    scrollArea.className = 'inv-grid-scroll';
    const grid = document.createElement('div');
    grid.className = 'gw-inventory-grid';
    for (let i = 0; i < Inventory.MAX_SLOTS; i++) {
      const slot = document.createElement('div');
      slot.className = 'gw-inventory-slot';
      slot.dataset.slotIndex = i;
      slot.style.cursor = 'pointer';
      slot.style.position = 'relative';
      slot.addEventListener('click', () => {
        // Suppress click fired by mobile after a drag gesture
        if (this._isDragging) return;
        if (this.inventory.slots[i]) {
          this._selectInventorySlot({ type: 'bag', index: i });
        } else {
          this._selectInventorySlot(null);
        }
      });
      slot.addEventListener('pointerdown', (e) => this._onBagSlotPointerDown(e, i));
      grid.appendChild(slot);
    }
    scrollArea.appendChild(grid);
    el.appendChild(scrollArea);

    // Footer: gold + weight
    const footer = document.createElement('div');
    footer.className = 'inv-footer';
    footer.innerHTML = `
      <span class="inv-gold-label">Gold:</span><span class="inv-gold-value">0</span>
      <span class="inv-weight-label">Weight:</span><span class="inv-weight-value">0 / ${Inventory.MAX_CARRY_WEIGHT}</span>
    `;
    el.appendChild(footer);

    // Slide-in detail panel
    const detail = document.createElement('div');
    detail.className = 'inv-detail-panel';
    el.appendChild(detail);

    return el;
  }

  _selectInventorySlot(selection) {
    // Toggle off if clicking same slot
    if (selection && this._invSelectedSlot &&
        this._invSelectedSlot.type === selection.type &&
        this._invSelectedSlot.index === selection.index &&
        this._invSelectedSlot.key === selection.key) {
      this._invSelectedSlot = null;
    } else {
      this._invSelectedSlot = selection;
    }
    this._updateInventoryWindow();
    this._updateCharacterWindow();
  }

  _getSelectedItem() {
    const sel = this._invSelectedSlot;
    if (!sel) return null;
    if (sel.type === 'bag') return this.inventory.slots[sel.index];
    if (sel.type === 'equip') return this.inventory.equipped[sel.key];
    return null;
  }

  _updateInventoryWindow() {
    const win = this.windowManager?.get('inventory');
    if (!win) return;
    const container = win.getContentContainer();
    const filter = this._invFilter || 'all';

    // Update bag slots
    const bagSlots = container.querySelectorAll('.gw-inventory-slot');
    for (let i = 0; i < bagSlots.length; i++) {
      const slotEl = bagSlots[i];
      const item = i < Inventory.MAX_SLOTS ? this.inventory.slots[i] : null;
      slotEl.innerHTML = '';
      slotEl.style.background = '';
      slotEl.style.opacity = '';
      slotEl.title = '';
      slotEl.className = 'gw-inventory-slot';

      // Highlight if selected
      if (this._invSelectedSlot?.type === 'bag' && this._invSelectedSlot.index === i) {
        slotEl.classList.add('selected');
      }

      if (item) {
        // Apply filter dimming
        const matchesFilter = filter === 'all' ||
          (filter === 'consumable' && item.def.type === 'consumable') ||
          (filter === 'equipment' && (item.def.type === 'weapon' || item.def.type === 'armor'));
        if (!matchesFilter) slotEl.style.opacity = '0.2';

        const c = item.def.color;
        slotEl.style.background = `rgba(${c.r},${c.g},${c.b}, 0.15)`;
        slotEl.title = item.def.name;

        if (item.def.rarity && item.def.rarity !== 'common') {
          slotEl.classList.add(`rarity-${item.def.rarity}`);
        }

        if (item.def.icon) {
          const icon = document.createElement('img');
          icon.src = item.def.icon;
          icon.className = 'slot-item-icon';
          icon.draggable = false;
          slotEl.appendChild(icon);
        } else {
          const label = document.createElement('span');
          label.style.cssText = 'font-size:14px;color:#fff;';
          label.textContent = (item.def.name || '?')[0].toUpperCase();
          slotEl.appendChild(label);
        }

        if (item.quantity > 1) {
          const count = document.createElement('span');
          count.className = 'slot-stack-count';
          count.textContent = item.quantity;
          slotEl.appendChild(count);
        }
      }
    }

    // Gold display
    const goldVal = container.querySelector('.inv-gold-value');
    if (goldVal) goldVal.textContent = this.inventory.gold || 0;

    // Weight display
    const weightVal = container.querySelector('.inv-weight-value');
    if (weightVal) {
      const total = this.inventory.getTotalWeight();
      const max = Inventory.MAX_CARRY_WEIGHT;
      weightVal.textContent = `${total} / ${max}`;
      // Color coding
      if (total > max) {
        weightVal.style.color = '#f87171'; // red — overweight
      } else if (total > max * 0.8) {
        weightVal.style.color = '#fbbf24'; // yellow — near max
      } else {
        weightVal.style.color = '#a0a0a0'; // normal
      }
    }

    // Update detail panel (slide in/out)
    const detailPanel = container.querySelector('.inv-detail-panel');
    if (detailPanel) {
      const hasSelection = !!this._getSelectedItem();
      detailPanel.classList.toggle('active', hasSelection);
      this._renderDetailPanel(detailPanel, RARITY_TIERS);
    }
  }

  _renderDetailPanel(panel, RARITY_TIERS) {
    panel.innerHTML = '';
    const item = this._getSelectedItem();
    if (!item) {
      const placeholder = document.createElement('div');
      placeholder.className = 'inv-detail-placeholder';
      placeholder.textContent = 'Click an item to inspect';
      panel.appendChild(placeholder);
      return;
    }

    const def = item.def;
    const rarity = RARITY_TIERS[def.rarity] || RARITY_TIERS.common;

    // Header: icon + name/type/rarity
    const header = document.createElement('div');
    header.className = 'inv-detail-header';

    const iconBox = document.createElement('div');
    iconBox.className = 'inv-detail-icon';
    if (def.icon) {
      const img = document.createElement('img');
      img.src = def.icon;
      img.draggable = false;
      iconBox.appendChild(img);
    }
    header.appendChild(iconBox);

    const info = document.createElement('div');
    const nameEl = document.createElement('div');
    nameEl.className = 'inv-detail-name';
    // Show exceptional prefix
    if (item.instanceData?.exceptional) {
      nameEl.textContent = `${item.instanceData.exceptional.prefix} ${def.name}`;
      nameEl.style.color = '#ffd700'; // gold for exceptional
    } else {
      nameEl.textContent = def.name;
      nameEl.style.color = rarity.color;
    }
    info.appendChild(nameEl);

    // Show slot label instead of generic type for equipment
    const typeEl = document.createElement('div');
    typeEl.className = 'inv-detail-type';
    if (def.slot && EQUIPMENT_SLOTS[def.slot]) {
      typeEl.textContent = EQUIPMENT_SLOTS[def.slot];
    } else if (def.slot === 'ring') {
      typeEl.textContent = 'Ring';
    } else {
      typeEl.textContent = def.type;
    }
    info.appendChild(typeEl);

    const rarityEl = document.createElement('div');
    rarityEl.className = 'inv-detail-rarity';
    rarityEl.textContent = rarity.label;
    rarityEl.style.color = rarity.color;
    info.appendChild(rarityEl);

    header.appendChild(info);
    panel.appendChild(header);

    // Item level & required level
    if (item.instanceData) {
      const levelInfo = document.createElement('div');
      levelInfo.className = 'inv-detail-type';
      levelInfo.style.fontSize = '11px';
      const parts = [`iLvl ${item.instanceData.itemLevel}`];
      if (item.instanceData.requiredLevel > 1) {
        const playerLevel = this.progression?.level || 1;
        const meetsReq = playerLevel >= item.instanceData.requiredLevel;
        parts.push(`<span style="color:${meetsReq ? '#a6e3a1' : '#f38ba8'}">Requires Lv.${item.instanceData.requiredLevel}</span>`);
      }
      levelInfo.innerHTML = parts.join(' &middot; ');
      info.appendChild(levelInfo);
    }

    // Stats (prefer scaled stats from instanceData)
    const displayStats = item.instanceData?.scaledStats || def.stats;
    const statsDiv = document.createElement('div');
    statsDiv.className = 'inv-detail-stats';
    if (displayStats?.attackPower) {
      const atk = document.createElement('span');
      atk.className = 'inv-stat';
      atk.textContent = `+${displayStats.attackPower} ATK`;
      statsDiv.appendChild(atk);
    }
    if (displayStats?.defense) {
      const defStat = document.createElement('span');
      defStat.className = 'inv-stat def';
      defStat.textContent = `+${displayStats.defense} DEF`;
      statsDiv.appendChild(defStat);
    }
    if (def.effect?.heal) {
      const heal = document.createElement('span');
      heal.className = 'inv-stat heal';
      heal.textContent = `+${def.effect.heal} HP`;
      statsDiv.appendChild(heal);
    }
    // Weight
    if (def.weight) {
      const wt = document.createElement('span');
      wt.className = 'inv-stat weight';
      wt.textContent = `${def.weight} wt`;
      statsDiv.appendChild(wt);
    }
    if (statsDiv.children.length > 0) panel.appendChild(statsDiv);

    // Description
    const desc = document.createElement('div');
    desc.className = 'inv-detail-desc';
    desc.textContent = def.description || '';
    panel.appendChild(desc);

    // Sell value
    const sellVal = getSellPrice(item.itemId);
    if (sellVal > 0) {
      const sell = document.createElement('div');
      sell.className = 'inv-detail-sell';
      sell.textContent = `Sell value: ${sellVal}g`;
      panel.appendChild(sell);
    }

    // Action buttons
    const actions = document.createElement('div');
    actions.className = 'inv-detail-actions';
    const sel = this._invSelectedSlot;

    if (sel.type === 'bag') {
      if (def.type === 'consumable') {
        const useBtn = document.createElement('button');
        useBtn.className = 'inv-action-btn';
        useBtn.textContent = def.effect?.heal ? 'Drink' : 'Use';
        useBtn.addEventListener('click', () => {
          this.inventory.useItem(sel.index, this.player);
          this.lootManager._applyEquipmentBonuses(this.player);
          if (!this.inventory.slots[sel.index]) this._invSelectedSlot = null;
          this._updateInventoryWindow();
          this._updateCharacterWindow();
        });
        actions.appendChild(useBtn);
      }
      if (def.slot) {
        const equipBtn = document.createElement('button');
        equipBtn.className = 'inv-action-btn';
        equipBtn.textContent = 'Equip';
        equipBtn.addEventListener('click', () => {
          this.inventory.equipItem(sel.index);
          this.lootManager._applyEquipmentBonuses(this.player);
          this._invSelectedSlot = null;
          this._updateInventoryWindow();
          this._updateCharacterWindow();
        });
        actions.appendChild(equipBtn);
      }
      if (def.type !== 'quest') {
        const dropBtn = document.createElement('button');
        dropBtn.className = 'inv-action-btn inv-action-drop';
        dropBtn.textContent = 'Drop';
        dropBtn.addEventListener('click', () => {
          this._dropSelectedItem();
        });
        actions.appendChild(dropBtn);
      }
    } else if (sel.type === 'equip') {
      const unequipBtn = document.createElement('button');
      unequipBtn.className = 'inv-action-btn';
      unequipBtn.textContent = 'Unequip';
      const hasFreeSlot = this.inventory.slots.some(s => s === null);
      if (!hasFreeSlot) {
        unequipBtn.style.opacity = '0.4';
        unequipBtn.style.cursor = 'not-allowed';
        unequipBtn.title = 'Inventory full — free up a bag slot first';
      } else {
        unequipBtn.addEventListener('click', () => {
          this.inventory.unequipItem(sel.key);
          this.lootManager._applyEquipmentBonuses(this.player);
          this._invSelectedSlot = null;
          this._updateInventoryWindow();
          this._updateCharacterWindow();
        });
      }
      actions.appendChild(unequipBtn);
    }

    if (actions.children.length > 0) panel.appendChild(actions);
  }

  _dropSelectedItem() {
    if (!this._invSelectedSlot || !this.player) return;
    const sel = this._invSelectedSlot;

    if (sel.type === 'bag') {
      const slot = this.inventory.slots[sel.index];
      if (!slot || slot.def.type === 'quest') return;
      const removed = this.inventory.removeItem(sel.index);
      if (removed) {
        this.lootManager.dropItemAtPlayer(removed.itemId, removed.quantity, this.player);
      }
    }
    this._invSelectedSlot = null;
    this._updateInventoryWindow();
  }

  // ========== Drag-and-Drop Equipment ==========

  /**
   * Begin dragging an item from a bag slot.
   */
  /** Clean up any in-flight drag (ghost, listeners, highlights). */
  _cleanupDrag() {
    if (this._dragState) {
      if (this._dragState.ghost) this._dragState.ghost.remove();
      this._dragState = null;
    }
    if (this._dragListeners) {
      document.removeEventListener('pointermove', this._dragListeners.move);
      document.removeEventListener('pointerup', this._dragListeners.up);
      document.removeEventListener('pointercancel', this._dragListeners.up);
      this._dragListeners = null;
    }
    // Stop auto-scroll animation
    if (this._dragScrollRAF) {
      cancelAnimationFrame(this._dragScrollRAF);
      this._dragScrollRAF = null;
    }
    // Remove ALL orphaned ghost elements (safety net for mobile edge cases)
    document.querySelectorAll('.inv-drag-ghost').forEach(g => g.remove());
    this._highlightDropTargets(null, false);
    this._highlightBagDropTargets(-1, false);
  }

  _onBagSlotPointerDown(e, slotIndex) {
    const item = this.inventory.slots[slotIndex];
    if (!item) return; // nothing to drag
    if (e.button !== 0) return; // left click only

    e.preventDefault();
    e.stopPropagation();

    // Clean up any stale drag from a previous cancelled gesture
    this._cleanupDrag();

    // Use currentTarget (the slot element) not e.target (could be a child).
    // The slot stays in the DOM even when innerHTML is cleared during re-renders.
    const captureEl = e.currentTarget;
    captureEl.setPointerCapture(e.pointerId);

    this._dragState = {
      slotIndex,
      item,
      startX: e.clientX,
      startY: e.clientY,
      dragging: false,
      ghost: null,
      pointerId: e.pointerId,
      captureTarget: captureEl,
    };

    const onMove = (ev) => {
      const state = this._dragState;
      if (!state) return;

      // Require a small move threshold before starting drag
      if (!state.dragging) {
        const dx = ev.clientX - state.startX;
        const dy = ev.clientY - state.startY;
        if (dx * dx + dy * dy < 25) return; // 5px threshold
        state.dragging = true;
        // Suppress synthesized click events on mobile during and after drag
        this._isDragging = true;
        // Create ghost element
        const ghost = document.createElement('div');
        ghost.className = 'inv-drag-ghost';
        if (state.item.def.icon) {
          const img = document.createElement('img');
          img.src = state.item.def.icon;
          img.draggable = false;
          ghost.appendChild(img);
        } else {
          ghost.textContent = (state.item.def.name || '?')[0];
        }
        document.body.appendChild(ghost);
        state.ghost = ghost;
        // Highlight equip slots if item is equippable
        if (state.item.def.slot) {
          const charWin = this.windowManager?.get('character');
          if (charWin && !charWin.isVisible()) charWin.show();
          this._highlightDropTargets(state.item.def.slot, true);
        }
        // Highlight bag slots as drop targets
        this._highlightBagDropTargets(state.slotIndex, true);

        // Cache scroll area ref for auto-scroll during drag
        const invWin = this.windowManager?.get('inventory');
        state.scrollArea = invWin?.getContentContainer()?.querySelector('.inv-grid-scroll') || null;
      }

      // Prevent browser scroll/pan during active drag (mobile)
      if (state.dragging) ev.preventDefault();

      // Move ghost
      if (state.ghost) {
        state.ghost.style.left = `${ev.clientX - 20}px`;
        state.ghost.style.top = `${ev.clientY - 20}px`;
      }

      // Auto-scroll inventory when dragging near top/bottom edges
      if (state.dragging && state.scrollArea) {
        const rect = state.scrollArea.getBoundingClientRect();
        const edgeSize = 40;
        const y = ev.clientY;
        if (y >= rect.top && y < rect.top + edgeSize) {
          state.scrollSpeed = -(1 - (y - rect.top) / edgeSize) * 6;
        } else if (y > rect.bottom - edgeSize && y <= rect.bottom) {
          state.scrollSpeed = (1 - (rect.bottom - y) / edgeSize) * 6;
        } else {
          state.scrollSpeed = 0;
        }
        // Start rAF scroll loop if needed
        if (state.scrollSpeed && !this._dragScrollRAF) {
          const scrollArea = state.scrollArea;
          const doScroll = () => {
            const s = this._dragState;
            if (!s?.dragging || !s.scrollSpeed) {
              this._dragScrollRAF = null;
              return;
            }
            scrollArea.scrollTop += s.scrollSpeed;
            this._dragScrollRAF = requestAnimationFrame(doScroll);
          };
          this._dragScrollRAF = requestAnimationFrame(doScroll);
        }
      }
    };

    const onUp = (ev) => {
      const state = this._dragState;

      // Release pointer capture
      if (state?.captureTarget) {
        try { state.captureTarget.releasePointerCapture(state.pointerId); } catch (_) {}
      }

      // Remove all listeners
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      document.removeEventListener('pointercancel', onUp);
      this._dragListeners = null;

      // Stop auto-scroll
      if (this._dragScrollRAF) {
        cancelAnimationFrame(this._dragScrollRAF);
        this._dragScrollRAF = null;
      }

      this._dragState = null;
      if (!state) return;

      // Clean up ghost & highlights — remove ALL ghosts as safety net
      if (state.ghost) state.ghost.remove();
      document.querySelectorAll('.inv-drag-ghost').forEach(g => g.remove());
      this._highlightDropTargets(null, false);
      this._highlightBagDropTargets(-1, false);

      if (!state.dragging) return; // was just a click, not a drag

      // Clear _isDragging after a delay so synthesized mobile click is suppressed
      setTimeout(() => { this._isDragging = false; }, 300);

      // pointercancel has no meaningful coordinates — skip drop logic
      if (ev.type === 'pointercancel') return;

      const dropEl = document.elementFromPoint(ev.clientX, ev.clientY);
      if (!dropEl) return;

      // Check equip slot drop (for equippable items)
      if (state.item.def.slot) {
        const equipSlot = dropEl.closest('.gw-equip-slot');
        if (equipSlot) {
          const targetKey = equipSlot.dataset.equipKey;
          if (targetKey) {
            const success = this.inventory.equipItemToSlot(state.slotIndex, targetKey);
            if (success) {
              this.lootManager._applyEquipmentBonuses(this.player);
              this._invSelectedSlot = null;
              this._updateInventoryWindow();
              this._updateCharacterWindow();
              return;
            }
          }
        }
      }

      // Check bag slot drop (reorder)
      const bagSlot = dropEl.closest('.gw-inventory-slot');
      if (bagSlot) {
        const targetIndex = parseInt(bagSlot.dataset.slotIndex, 10);
        if (!isNaN(targetIndex) && targetIndex !== state.slotIndex) {
          this.inventory.swapSlots(state.slotIndex, targetIndex);
          window.game?.audioSystem?.audio?.playUiConfirm();
          this._invSelectedSlot = null;
          this._updateInventoryWindow();
          return;
        }
      }
    };

    // Store refs so _cleanupDrag can remove them if a new drag starts before onUp fires
    this._dragListeners = { move: onMove, up: onUp };

    // Non-passive so we can preventDefault on pointermove to block mobile scroll during drag
    document.addEventListener('pointermove', onMove, { passive: false });
    document.addEventListener('pointerup', onUp, { passive: true });
    document.addEventListener('pointercancel', onUp, { passive: true });
  }

  /**
   * Highlight valid equipment drop targets during drag.
   */
  _highlightDropTargets(itemSlot, show) {
    const charWin = this.windowManager?.get('character');
    if (!charWin) return;
    const container = charWin.getContentContainer();
    const slots = container.querySelectorAll('.gw-equip-slot');

    slots.forEach(slotEl => {
      slotEl.classList.remove('drag-over');
      if (!show) return;
      const key = slotEl.dataset.equipKey;
      // Check if this slot accepts the dragged item
      if (itemSlot === 'ring' && (key === 'ring1' || key === 'ring2')) {
        slotEl.classList.add('drag-over');
      } else if (itemSlot === key) {
        slotEl.classList.add('drag-over');
      }
    });
  }

  /**
   * Highlight bag slots as valid drop targets during drag.
   */
  _highlightBagDropTargets(sourceIndex, show) {
    const invWin = this.windowManager?.get('inventory');
    if (!invWin) return;
    const container = invWin.getContentContainer();
    const slots = container.querySelectorAll('.gw-inventory-slot');

    slots.forEach(slotEl => {
      slotEl.classList.remove('drag-over');
      if (!show) return;
      const idx = parseInt(slotEl.dataset.slotIndex, 10);
      if (idx !== sourceIndex) {
        slotEl.classList.add('drag-over');
      }
    });
  }

  // ========== Shop UI ==========

  _openShopUI(npc, onBack, onClose) {
    // Remove existing shop overlay if any
    this._closeShopUI();

    this._shopOnBack = onBack;
    this._shopOnClose = onClose;
    this._shopTab = 'buy'; // 'buy' or 'sell'

    // Create overlay (reuses dialog overlay pattern)
    const overlay = document.createElement('div');
    overlay.className = 'dialog-overlay shop-overlay';
    overlay.style.display = 'flex';
    overlay.addEventListener('click', (e) => {
      e.stopPropagation();
      this._closeShopUI();
      if (onClose) onClose();
    });

    const container = document.createElement('div');
    container.className = 'dialog-box shop-container';
    container.addEventListener('click', (e) => e.stopPropagation());

    // Header
    const header = document.createElement('div');
    header.className = 'shop-header';
    const title = document.createElement('span');
    title.className = 'shop-title';
    title.textContent = "Merchant's Wares";
    header.appendChild(title);
    const goldDisplay = document.createElement('span');
    goldDisplay.className = 'shop-gold';
    goldDisplay.innerHTML = `<img src="icons/items/I_GoldCoin.png" class="shop-gold-icon" alt=""><span class="shop-gold-text">${this.inventory.gold}</span>`;
    header.appendChild(goldDisplay);
    container.appendChild(header);

    // Tabs
    const tabs = document.createElement('div');
    tabs.className = 'shop-tabs';
    const buyTab = document.createElement('button');
    buyTab.className = 'shop-tab active';
    buyTab.textContent = 'Buy';
    buyTab.addEventListener('click', () => {
      this._shopTab = 'buy';
      buyTab.classList.add('active');
      sellTab.classList.remove('active');
      this._renderShopItems(itemList, feedbackEl);
    });
    const sellTab = document.createElement('button');
    sellTab.className = 'shop-tab';
    sellTab.textContent = 'Sell';
    sellTab.addEventListener('click', () => {
      this._shopTab = 'sell';
      sellTab.classList.add('active');
      buyTab.classList.remove('active');
      this._renderShopItems(itemList, feedbackEl);
    });
    tabs.appendChild(buyTab);
    tabs.appendChild(sellTab);
    container.appendChild(tabs);

    // Item list
    const itemList = document.createElement('div');
    itemList.className = 'shop-item-list';
    container.appendChild(itemList);

    // Feedback area
    const feedbackEl = document.createElement('div');
    feedbackEl.className = 'shop-feedback';
    container.appendChild(feedbackEl);

    // Footer
    const footer = document.createElement('div');
    footer.className = 'shop-footer';
    const backBtn = document.createElement('button');
    backBtn.className = 'shop-back-btn';
    backBtn.textContent = 'Back to conversation';
    backBtn.addEventListener('click', () => {
      this._closeShopUI();
      if (onBack) onBack();
    });
    footer.appendChild(backBtn);
    container.appendChild(footer);

    overlay.appendChild(container);
    document.body.appendChild(overlay);
    this._shopOverlay = overlay;
    this._shopGoldText = goldDisplay.querySelector('.shop-gold-text');
    this._shopBuyTab = buyTab;
    this._shopSellTab = sellTab;

    this._renderShopItems(itemList, feedbackEl);
  }

  _renderShopItems(listEl, feedbackEl) {
    listEl.innerHTML = '';

    if (this._shopTab === 'buy') {
      for (const stock of MERCHANT_STOCK) {
        const def = ITEM_DEFS[stock.itemId];
        if (!def) continue;
        const price = def.buyPrice || 0;

        const row = document.createElement('div');
        row.className = 'shop-item-row';

        // Icon
        if (def.icon) {
          const icon = document.createElement('img');
          icon.src = def.icon;
          icon.className = 'shop-item-icon';
          icon.draggable = false;
          row.appendChild(icon);
        }

        // Info
        const info = document.createElement('div');
        info.className = 'shop-item-info';
        const nameEl = document.createElement('div');
        nameEl.className = 'shop-item-name';
        nameEl.textContent = def.name;
        if (def.rarity && def.rarity !== 'common') {
          nameEl.style.color = RARITY_TIERS[def.rarity]?.color || '';
        }
        info.appendChild(nameEl);
        const descEl = document.createElement('div');
        descEl.className = 'shop-item-desc';
        if (def.stats?.attackPower) descEl.textContent = `+${def.stats.attackPower} ATK`;
        else if (def.stats?.defense) descEl.textContent = `+${def.stats.defense} DEF`;
        else if (def.effect?.heal) descEl.textContent = `+${def.effect.heal} HP`;
        info.appendChild(descEl);
        row.appendChild(info);

        // Price
        const priceEl = document.createElement('span');
        priceEl.className = 'shop-item-price';
        priceEl.textContent = `${price}g`;
        row.appendChild(priceEl);

        // Buy button
        const buyBtn = document.createElement('button');
        buyBtn.className = 'shop-action-btn';
        buyBtn.textContent = 'Buy';
        const canStack = def.stackable && this.inventory.slots.some(s => s && s.itemId === stock.itemId);
        const hasFreeSlot = this.inventory.slots.some(s => s === null);
        if (this.inventory.gold < price) {
          buyBtn.classList.add('disabled');
          buyBtn.title = 'Not enough gold';
        } else if (!hasFreeSlot && !canStack) {
          buyBtn.classList.add('disabled');
          buyBtn.title = 'Inventory full — free up a bag slot first';
        }
        buyBtn.addEventListener('click', () => {
          this._shopBuyItem(stock.itemId, price, listEl, feedbackEl);
        });
        row.appendChild(buyBtn);

        listEl.appendChild(row);
      }
    } else {
      // Sell tab — show player inventory items
      let hasItems = false;
      for (let i = 0; i < this.inventory.slots.length; i++) {
        const slot = this.inventory.slots[i];
        if (!slot) continue;
        const def = slot.def;
        const sellPrice = getSellPrice(slot.itemId);
        if (sellPrice <= 0 && def.type !== 'treasure') continue; // no sell value and not a treasure

        hasItems = true;
        const row = document.createElement('div');
        row.className = 'shop-item-row';

        if (def.icon) {
          const icon = document.createElement('img');
          icon.src = def.icon;
          icon.className = 'shop-item-icon';
          icon.draggable = false;
          row.appendChild(icon);
        }

        const info = document.createElement('div');
        info.className = 'shop-item-info';
        const nameEl = document.createElement('div');
        nameEl.className = 'shop-item-name';
        nameEl.textContent = def.name + (slot.quantity > 1 ? ` x${slot.quantity}` : '');
        if (def.rarity && def.rarity !== 'common') {
          nameEl.style.color = RARITY_TIERS[def.rarity]?.color || '';
        }
        info.appendChild(nameEl);
        const descEl = document.createElement('div');
        descEl.className = 'shop-item-desc';
        if (def.stats?.attackPower) descEl.textContent = `+${def.stats.attackPower} ATK`;
        else if (def.stats?.defense) descEl.textContent = `+${def.stats.defense} DEF`;
        else if (def.effect?.heal) descEl.textContent = `+${def.effect.heal} HP`;
        info.appendChild(descEl);
        row.appendChild(info);

        const priceEl = document.createElement('span');
        priceEl.className = 'shop-item-price sell';
        priceEl.textContent = `${sellPrice}g`;
        row.appendChild(priceEl);

        const sellBtn = document.createElement('button');
        sellBtn.className = 'shop-action-btn sell';
        sellBtn.textContent = 'Sell';
        sellBtn.addEventListener('click', () => {
          this._shopSellItem(i, sellPrice, listEl, feedbackEl);
        });
        row.appendChild(sellBtn);

        listEl.appendChild(row);
      }
      if (!hasItems) {
        const empty = document.createElement('div');
        empty.className = 'shop-empty';
        empty.textContent = 'Nothing to sell.';
        listEl.appendChild(empty);
      }
    }
  }

  _shopBuyItem(itemId, price, listEl, feedbackEl) {
    const def = ITEM_DEFS[itemId];
    if (!def) return;

    if (this.inventory.gold < price) {
      this._showShopFeedback(feedbackEl, 'Not enough gold!', 'error');
      return;
    }

    const added = this.inventory.addItem(itemId, 1, def);
    if (!added) {
      this._showShopFeedback(feedbackEl, 'Inventory full — free up a bag slot first!', 'error');
      return;
    }

    this.inventory.removeGold(price);
    window.game?.audioSystem?.audio?.playUiConfirm();
    this._showShopFeedback(feedbackEl, `Bought ${def.name}!`, 'success');
    this._updateShopGold();
    this._renderShopItems(listEl, feedbackEl);
    this._updateInventoryWindow();
  }

  _shopSellItem(slotIndex, sellPrice, listEl, feedbackEl) {
    const slot = this.inventory.slots[slotIndex];
    if (!slot) return;

    const name = slot.def.name;
    // Sell one at a time for stackables
    if (slot.quantity > 1) {
      slot.quantity--;
      this.inventory._notify();
    } else {
      this.inventory.removeItem(slotIndex);
    }

    this.inventory.addGold(sellPrice);
    window.game?.audioSystem?.audio?.playUiConfirm();
    this._showShopFeedback(feedbackEl, `Sold ${name} for ${sellPrice}g!`, 'success');
    this._updateShopGold();
    this._renderShopItems(listEl, feedbackEl);
    this._updateInventoryWindow();
  }

  _showShopFeedback(el, message, type) {
    el.textContent = message;
    el.className = `shop-feedback ${type}`;
    // Clear after 2s
    clearTimeout(this._shopFeedbackTimer);
    this._shopFeedbackTimer = setTimeout(() => {
      el.textContent = '';
      el.className = 'shop-feedback';
    }, 2000);
  }

  _updateShopGold() {
    if (this._shopGoldText) {
      this._shopGoldText.textContent = this.inventory.gold;
    }
    // Also update HUD gold
    this.hud?.updateGold(this.inventory.gold);
  }

  _closeShopUI() {
    if (this._shopOverlay) {
      this._shopOverlay.remove();
      this._shopOverlay = null;
    }
    clearTimeout(this._shopFeedbackTimer);
  }

  // ====== Dungeon Key Trade ======

  /** Map key tier to the itemIds that count as that tier */
  static KEY_TIER_ITEMS = {
    bronze: ['bronze-key', 'dungeon-key'],
    silver: ['silver-key'],
    gold:   ['gold-key', 'master-key']
  };

  _handleKeyTrade(tier, done) {
    const validIds = Game.KEY_TIER_ITEMS[tier];
    if (!validIds) {
      done("I don't recognize that key type. You trying to scam me?!");
      return;
    }

    const tierLabel = tier.charAt(0).toUpperCase() + tier.slice(1);

    // Count keys of the requested tier in bag
    let keyCount = 0;
    for (const slot of this.inventory.slots) {
      if (slot && validIds.includes(slot.itemId)) keyCount += slot.quantity;
    }

    if (keyCount < 3) {
      done(`Wait... *counts on fingers* ...you only have ${keyCount} ${tierLabel} Key${keyCount !== 1 ? 's' : ''}! I need THREE. Go fight some dungeon bosses! They drop 'em like candy. Scary, bone-crunching candy.`);
      return;
    }

    // Pre-check: will there be room for the reward after removing 3 keys?
    const hasFreeSlot = this.inventory.slots.some(s => s === null);
    if (!hasFreeSlot) {
      let simRemoved = 0;
      let wouldFree = false;
      for (const slot of this.inventory.slots) {
        if (slot && validIds.includes(slot.itemId) && simRemoved < 3) {
          const take = Math.min(slot.quantity, 3 - simRemoved);
          simRemoved += take;
          if (slot.quantity - take <= 0) { wouldFree = true; break; }
        }
      }
      if (!wouldFree) {
        done("Whoa there — your bags are STUFFED! I'm not taking your keys if you've got no room for the reward. Clear out some space and come back!");
        return;
      }
    }

    // Remove 3 keys of the requested tier
    let removed = 0;
    for (let i = 0; i < this.inventory.slots.length && removed < 3; i++) {
      const slot = this.inventory.slots[i];
      if (slot && validIds.includes(slot.itemId)) {
        const take = Math.min(slot.quantity, 3 - removed);
        slot.quantity -= take;
        removed += take;
        if (slot.quantity <= 0) this.inventory.slots[i] = null;
      }
    }
    this.inventory._notify();

    // Roll tier-specific mystery reward
    const reward = this._rollMysteryReward(tier);
    const def = ITEM_DEFS[reward.itemId];

    // Create a level-scaled item instance at the player's level
    const playerLevel = this.progression?.level || 1;
    const exceptionalChance = tier === 'gold' ? 0.25 : tier === 'silver' ? 0.15 : 0.05;
    const instanceData = createItemInstance(reward.itemId, {
      itemLevel: playerLevel,
      exceptionalChance
    });

    const added = this.inventory.addItem(reward.itemId, 1, def, instanceData);

    if (!added) {
      // Inventory full — refund 3 keys
      const refundId = validIds[0];
      this.inventory.addItem(refundId, 3, ITEM_DEFS[refundId]);
      done("Hold on — your bags are FULL! Come back when you've got room. I'm not dropping a good item on the floor. I have STANDARDS.");
      return;
    }

    window.game?.audioSystem?.audio?.playUiConfirm();
    this._updateInventoryWindow();
    this.hud?.updateGold(this.inventory.gold);

    const displayName = instanceData ? getInstanceDisplayName(def, instanceData) : def.name;
    const stats = instanceData?.scaledStats || def.stats || {};
    const statParts = [];
    if (stats.attackPower) statParts.push(`+${stats.attackPower} ATK`);
    if (stats.defense) statParts.push(`+${stats.defense} DEF`);
    const statText = statParts.join(', ');
    const lvlText = instanceData ? ` (iLvl ${instanceData.itemLevel})` : '';

    if (def.rarity === 'legendary') {
      done(`YESSS! *grabs keys lovingly* And YOUR reward is... ${displayName}${lvlText}! ${statText}! LEGENDARY! I got this from a dungeon so deep the hellrats had formed a GOVERNMENT. Don't ask about the election. It was violent.`);
    } else if (def.rarity === 'epic') {
      done(`OH BABY! *pockets keys reverently* Here's your prize — ${displayName}${lvlText}! ${statText}! EPIC! This was pulled from a dungeon where the bears had learned to READ. Terrifying stuff.`);
    } else {
      done(`PLEASURE doing business! *pockets keys* Here you go — ${displayName}${lvlText}! ${statText}! Got it from a bear who owed me a favor. Actually, he doesn't know he owed me a favor. Bears are bad with contracts.`);
    }
  }

  _rollMysteryReward(tier) {
    const pool = MYSTERY_REWARDS_BY_TIER[tier] || MYSTERY_REWARDS_BY_TIER.bronze;
    const total = pool.reduce((sum, r) => sum + r.weight, 0);
    let roll = Math.random() * total;
    for (const reward of pool) {
      roll -= reward.weight;
      if (roll <= 0) return reward;
    }
    return pool[pool.length - 1];
  }

  _buildCharacterContent() {
    const el = document.createElement('div');

    // Header
    const header = document.createElement('div');
    header.className = 'gw-char-header';
    const lvl = this.progression?.level || 1;
    header.innerHTML = `<div class="gw-char-name">Adventurer</div><div class="gw-char-level">Level ${lvl}</div>`;
    el.appendChild(header);

    // Bars (HP + MP + SP + XP)
    const bars = document.createElement('div');
    bars.className = 'gw-char-bars';
    bars.innerHTML = `
      <div class="gw-char-bar">
        <div class="gw-char-bar-fill health" style="width:100%"></div>
        <div class="gw-char-bar-label"><span>HP</span><span>100 / 100</span></div>
      </div>
      <div class="gw-char-bar gw-bar-sp">
        <div class="gw-char-bar-fill gw-fill-sp" style="width:100%;background:linear-gradient(90deg,#ca8a04,#facc15)"></div>
        <div class="gw-char-bar-label"><span>SP</span><span>100 / 100</span></div>
      </div>
      <div class="gw-char-bar gw-bar-mp">
        <div class="gw-char-bar-fill gw-fill-mp" style="width:100%;background:linear-gradient(90deg,#3b82f6,#60a5fa)"></div>
        <div class="gw-char-bar-label"><span>MP</span><span>50 / 50</span></div>
      </div>
      <div class="gw-char-bar gw-bar-xp">
        <div class="gw-char-bar-fill mana" style="width:0%;background:linear-gradient(90deg,#a855f7,#c084fc)"></div>
        <div class="gw-char-bar-label"><span>XP</span><span>0 / 100</span></div>
      </div>
    `;
    el.appendChild(bars);

    // Diablo-style paperdoll with animated character sprite
    const paperdoll = document.createElement('div');
    paperdoll.className = 'gw-paperdoll';

    // Fixed-width inner container keeps slots compact regardless of window width
    const inner = document.createElement('div');
    inner.className = 'gw-paperdoll-inner';

    // Animated player sprite (replaces SVG silhouette)
    const spriteEl = document.createElement('div');
    spriteEl.className = 'gw-paperdoll-sprite';
    inner.appendChild(spriteEl);

    // Equipment slots — each positioned absolutely via CSS class
    const slotKeys = [
      'head', 'neck', 'shoulders', 'back',
      'mainhand', 'body', 'offhand',
      'hands', 'belt', 'ring1', 'ring2',
      'legs', 'feet'
    ];

    for (const key of slotKeys) {
      const slot = document.createElement('div');
      slot.className = `gw-equip-slot gw-slot-${key}`;
      slot.dataset.equipKey = key;
      slot.style.cursor = 'pointer';
      slot.addEventListener('click', () => {
        // Suppress click fired by mobile after a drag gesture
        if (this._isDragging) return;
        if (this.inventory.equipped[key]) {
          this._selectInventorySlot({ type: 'equip', key });
        }
      });
      inner.appendChild(slot);
    }
    paperdoll.appendChild(inner);
    el.appendChild(paperdoll);

    // Primary attributes section
    const attrSection = document.createElement('div');
    attrSection.className = 'gw-char-attrs';
    attrSection.style.cssText = 'padding: 6px 10px; font-size: 12px;';

    // Unspent points header
    const pointsRow = document.createElement('div');
    pointsRow.className = 'gw-attr-points-row';
    pointsRow.style.cssText = 'display:flex; justify-content:space-between; align-items:center; margin-bottom:6px; font-size:12px; color:#a6adc8;';
    pointsRow.innerHTML = `<span class="gw-attr-points-label">Stat Points: <strong class="gw-attr-points-value" style="color:#f9e2af;">0</strong></span>`;
    attrSection.appendChild(pointsRow);

    // Attribute rows
    const attrDefs = [
      { key: 'str', label: 'STR', desc: 'Melee damage, carry weight', color: '#f38ba8' },
      { key: 'dex', label: 'DEX', desc: 'Ranged damage, attack speed', color: '#a6e3a1' },
      { key: 'int', label: 'INT', desc: 'Spell damage, mana pool', color: '#89b4fa' },
      { key: 'con', label: 'CON', desc: 'Health, health regen', color: '#fab387' },
      { key: 'wis', label: 'WIS', desc: 'Mana regen, minor spell bonus', color: '#cba6f7' },
      { key: 'end', label: 'END', desc: 'Stamina pool, stamina regen', color: '#f9e2af' },
    ];
    for (const attr of attrDefs) {
      const row = document.createElement('div');
      row.className = 'gw-attr-row';
      row.dataset.attr = attr.key;
      row.style.cssText = 'display:flex; align-items:center; gap:6px; padding:2px 0;';
      row.title = attr.desc;
      row.innerHTML = `
        <span class="gw-attr-label" style="width:32px; color:${attr.color}; font-weight:600;">${attr.label}</span>
        <span class="gw-attr-value" style="width:36px; text-align:right; color:#cdd6f4;">5</span>
        <span class="gw-attr-bonus" style="width:40px; font-size:11px; color:#6c7086;"></span>
        <button class="gw-attr-plus" data-attr="${attr.key}" style="
          width:20px; height:20px; border:1px solid #45475a; border-radius:4px;
          background:#313244; color:#a6e3a1; font-size:14px; font-weight:bold;
          cursor:pointer; display:none; line-height:1; padding:0;
        ">+</button>
      `;
      attrSection.appendChild(row);
    }

    // Reset stats button
    const resetRow = document.createElement('div');
    resetRow.style.cssText = 'margin-top:8px; text-align:center;';
    resetRow.innerHTML = `<button class="gw-attr-reset" style="
      padding:4px 16px; font-size:11px; background:#45475a; color:#cdd6f4;
      border:1px solid #585b70; border-radius:4px; cursor:pointer;
    ">Reset Stats</button>`;
    attrSection.appendChild(resetRow);

    el.appendChild(attrSection);

    // Wire attribute allocation buttons
    attrSection.addEventListener('click', (e) => {
      const plusBtn = e.target.closest('.gw-attr-plus');
      if (plusBtn && this.progression) {
        const attr = plusBtn.dataset.attr;
        if (this.progression.allocatePoint(attr)) {
          this._updateCharacterWindow();
          this.characterSave.scheduleSave(this.progression, this.inventory);
        }
      }
      const resetBtn = e.target.closest('.gw-attr-reset');
      if (resetBtn && this.progression && this.inventory) {
        const cost = this.progression.respecCost;
        if (this.inventory.gold < cost) {
          this.hudManager?.showNotification?.(`Not enough gold (need ${cost})`);
          return;
        }
        if (!confirm(`Reset all stat points for ${cost} gold?`)) return;
        this.inventory.addGold(-cost);
        this.progression.redistributeStats();
        // Reapply stats
        this.progression.applyTo(this.player.combatStats);
        this._updateCharacterWindow();
        this.characterSave.scheduleSave(this.progression, this.inventory);
      }
    });

    // Detail panel for equipped items (click to inspect/unequip)
    const detail = document.createElement('div');
    detail.className = 'inv-detail-panel';
    el.appendChild(detail);

    return el;
  }

  _updateCharacterWindow() {
    const win = this.windowManager?.get('character');
    if (!win) return;
    const container = win.getContentContainer();
    const p = this.progression;
    const stats = this.player?.combatStats;

    // Character name — show nickname if available, otherwise "Adventurer"
    const nameEl = container.querySelector('.gw-char-name');
    if (nameEl) {
      const nick = this.multiplayerPanel?.getNickname();
      nameEl.textContent = nick || 'Adventurer';
    }

    // Level text
    const levelEl = container.querySelector('.gw-char-level');
    if (levelEl && p) {
      if (p.level >= CharacterProgression.MAX_LEVEL) {
        levelEl.textContent = `Level ${p.level} (MAX)`;
      } else {
        levelEl.textContent = `Level ${p.level}`;
      }
    }

    // HP bar
    if (stats) {
      const hpFill = container.querySelector('.gw-char-bar-fill.health');
      const hpBar = container.querySelector('.gw-char-bar');
      if (hpFill) hpFill.style.width = `${(stats.health / stats.maxHealth) * 100}%`;
      if (hpBar) {
        const label = hpBar.querySelector('.gw-char-bar-label span:last-child');
        if (label) label.textContent = `${Math.ceil(stats.health)} / ${stats.maxHealth}`;
      }

      // MP bar
      const mpFill = container.querySelector('.gw-fill-mp');
      const mpBar = container.querySelector('.gw-bar-mp');
      if (mpFill) mpFill.style.width = `${(stats.mana / stats.maxMana) * 100}%`;
      if (mpBar) {
        const label = mpBar.querySelector('.gw-char-bar-label span:last-child');
        if (label) label.textContent = `${Math.ceil(stats.mana)} / ${stats.maxMana}`;
      }

      // SP bar
      const spFill = container.querySelector('.gw-fill-sp');
      const spBar = container.querySelector('.gw-bar-sp');
      if (spFill) spFill.style.width = `${(stats.stamina / stats.maxStamina) * 100}%`;
      if (spBar) {
        const label = spBar.querySelector('.gw-char-bar-label span:last-child');
        if (label) label.textContent = `${Math.ceil(stats.stamina)} / ${stats.maxStamina}`;
      }
    }

    // XP bar
    if (p) {
      const xpFill = container.querySelector('.gw-bar-xp .gw-char-bar-fill');
      const xpBar = container.querySelector('.gw-bar-xp');
      if (xpFill) {
        xpFill.style.width = `${p.xpProgress * 100}%`;
      }
      if (xpBar) {
        const label = xpBar.querySelector('.gw-char-bar-label span:last-child');
        if (label) {
          label.textContent = p.level >= CharacterProgression.MAX_LEVEL
            ? 'MAX' : `${p.xp} / ${p.xpToNextLevel}`;
        }
      }
    }

    // Primary attributes
    if (p) {
      const pointsEl = container.querySelector('.gw-attr-points-value');
      if (pointsEl) pointsEl.textContent = p.unspentPoints;

      const showPlus = p.unspentPoints > 0;
      const attrs = p.attributes;
      for (const row of container.querySelectorAll('.gw-attr-row')) {
        const key = row.dataset.attr;
        if (!key || !attrs) continue;
        const baseVal = attrs[key] || 5;
        const bonusKey = 'bonus' + key.charAt(0).toUpperCase() + key.slice(1);
        const bonus = stats?.[bonusKey] || 0;
        const valEl = row.querySelector('.gw-attr-value');
        const bonusEl = row.querySelector('.gw-attr-bonus');
        const plusBtn = row.querySelector('.gw-attr-plus');
        if (valEl) valEl.textContent = baseVal;
        if (bonusEl) bonusEl.textContent = bonus > 0 ? `(+${bonus})` : '';
        if (plusBtn) plusBtn.style.display = showPlus ? '' : 'none';
      }

      // Pulse attribute section when points available
      const attrSection = container.querySelector('.gw-char-attrs');
      if (attrSection) {
        attrSection.style.borderColor = showPlus ? '#f9e2af' : '';
        attrSection.style.borderWidth = showPlus ? '1px' : '';
        attrSection.style.borderStyle = showPlus ? 'solid' : '';
        attrSection.style.borderRadius = showPlus ? '6px' : '';
      }
    }

    // Initialize/update animated player sprite in paperdoll
    const spriteEl = container.querySelector('.gw-paperdoll-sprite');
    const currentAtlas = this.player?.sprite?.atlas;
    if (spriteEl && currentAtlas && spriteEl._atlasRef !== currentAtlas) {
      // Clear previous animation if switching characters
      if (spriteEl._animInterval) clearInterval(spriteEl._animInterval);
      spriteEl._atlasRef = currentAtlas;
      const atlas = currentAtlas;
      const idleAnim = this.player.config?.idle || 'breathing-idle';
      const direction = 'south-west';
      const frameCount = atlas.getFrameCount(idleAnim, direction);
      if (frameCount > 0) {
        const bodySize = atlas.bodyFrameSize || atlas.frameWidth;
        Object.assign(spriteEl.style, {
          backgroundImage: `url(${atlas.url})`,
          backgroundSize: `${atlas.width}px ${atlas.height}px`,
          width: `${bodySize}px`,
          height: `${bodySize}px`,
          imageRendering: 'pixelated',
        });
        // Adjust bgPosition for oversize tiles: body is centered within tile,
        // so shift by half the size difference to show only the body portion.
        const adjustBgPos = (frameData) => {
          if (!frameData) return;
          const ts = frameData.tileSize || bodySize;
          if (ts > bodySize) {
            const offset = Math.floor((ts - bodySize) / 2);
            const bgX = frameData.x + offset;
            const bgY = frameData.y + offset;
            spriteEl.style.backgroundPosition = `-${bgX}px -${bgY}px`;
          } else {
            spriteEl.style.backgroundPosition = frameData.bgPosition;
          }
        };
        // Set first frame
        adjustBgPos(atlas.getAnimationFrame(idleAnim, direction, 0));
        // Cycle frames
        let frame = 0;
        spriteEl._animInterval = setInterval(() => {
          frame = (frame + 1) % frameCount;
          adjustBgPos(atlas.getAnimationFrame(idleAnim, direction, frame));
        }, 150);
      }
    }

    // Update paperdoll equipment slots
    const equipSlots = container.querySelectorAll('.gw-equip-slot');
    equipSlots.forEach(slotEl => {
      const key = slotEl.dataset.equipKey;
      if (!key) return;
      const item = this.inventory.equipped[key];
      slotEl.innerHTML = '';
      slotEl.title = '';
      slotEl.classList.remove('selected', 'rarity-uncommon', 'rarity-rare', 'rarity-epic', 'rarity-legendary');
      slotEl.style.borderColor = '';
      slotEl.style.borderStyle = '';
      slotEl.style.background = '';

      // Highlight if selected
      if (this._invSelectedSlot?.type === 'equip' && this._invSelectedSlot.key === key) {
        slotEl.classList.add('selected');
      }

      if (item) {
        const c = item.def.color;
        if (!slotEl.classList.contains('selected')) {
          slotEl.style.borderColor = `rgba(${c.r},${c.g},${c.b}, 0.6)`;
          slotEl.style.borderStyle = 'solid';
          slotEl.style.background = `rgba(${c.r},${c.g},${c.b}, 0.12)`;
        }
        // Tooltip with exceptional prefix and item level
        let eqTooltip = item.def.name;
        if (item.instanceData?.exceptional) eqTooltip = `${item.instanceData.exceptional.prefix} ${eqTooltip}`;
        if (item.instanceData?.itemLevel > 1) eqTooltip += ` (iLvl ${item.instanceData.itemLevel})`;
        const eqStats = item.instanceData?.scaledStats || item.def.stats;
        if (eqStats?.attackPower) eqTooltip += ` +${eqStats.attackPower} ATK`;
        if (eqStats?.defense) eqTooltip += ` +${eqStats.defense} DEF`;
        slotEl.title = eqTooltip;
        // Rarity border
        if (item.instanceData?.exceptional) {
          slotEl.classList.add('rarity-legendary'); // gold border for exceptional
        } else if (item.def.rarity && item.def.rarity !== 'common') {
          slotEl.classList.add(`rarity-${item.def.rarity}`);
        }
        if (item.def.icon) {
          const icon = document.createElement('img');
          icon.src = item.def.icon;
          icon.className = 'slot-item-icon';
          icon.draggable = false;
          slotEl.appendChild(icon);
        }
      } else {
        // Show slot label placeholder
        const label = EQUIPMENT_SLOTS[key] || key;
        const placeholder = document.createElement('span');
        placeholder.className = 'gw-equip-slot-label';
        placeholder.textContent = label;
        slotEl.appendChild(placeholder);
      }
    });

    // (Stats grid replaced by primary attributes above)

    // Update detail panel on character sheet (for equipped item inspection)
    const detailPanel = container.querySelector('.inv-detail-panel');
    if (detailPanel) {
      const hasEquipSelection = this._invSelectedSlot?.type === 'equip' && !!this._getSelectedItem();
      detailPanel.classList.toggle('active', hasEquipSelection);
      this._renderDetailPanel(detailPanel, RARITY_TIERS);
    }
  }

  _buildMapContent() {
    const el = document.createElement('div');
    el.className = 'gw-map-container';

    const canvas = document.createElement('canvas');
    canvas.className = 'gw-map-canvas';
    canvas.width = 300;
    canvas.height = 300;
    el.appendChild(canvas);
    this.mapRenderer.attachToCanvas(canvas);

    return el;
  }

  _updateMapWindow(dt) {
    const win = this.windowManager?.get('map');
    if (!win || !win.isVisible()) return;

    const canvas = win.getContentContainer().querySelector('.gw-map-canvas');
    if (!canvas) return;

    // Resize canvas to match container
    const rect = canvas.parentElement.getBoundingClientRect();
    const w = Math.floor(rect.width) || 300;
    const h = Math.floor(rect.height) || 300;
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w;
      canvas.height = h;
    }

    // Ensure attached
    if (this.mapRenderer.canvas !== canvas) {
      this.mapRenderer.attachToCanvas(canvas);
    }

    this.mapRenderer.isDungeonMode = !!this._dungeonMode;
    this.mapRenderer.update(dt);
  }

  _buildObjectivesContent() {
    const el = document.createElement('div');
    el.className = 'gw-objectives-container';
    el.style.cssText = 'padding: 8px; font-family: "Cinzel", "Times New Roman", serif;';
    el.innerHTML = '<div class="gw-objectives-list" style="font-size:13px;color:#cdd6f4;"></div>';
    return el;
  }

  _updateObjectivesWindow() {
    const win = this.windowManager?.get('objectives');
    if (!win) return;
    const container = win.getContentContainer();
    const listEl = container.querySelector('.gw-objectives-list');
    if (!listEl) return;

    const objectives = this.questManager?.objectives;
    let html = '';

    if (objectives && objectives.length > 0) {
      html += '<div style="font-size:14px;font-weight:600;color:#89b4fa;margin-bottom:8px;letter-spacing:0.5px;">OBJECTIVES</div>';
      for (const obj of objectives) {
        const done = obj.complete || obj.completed;
        const icon = done ? '\u2713' : '\u2022';
        const color = done ? '#22c55e' : '#cdd6f4';
        const decoration = done ? 'line-through' : 'none';
        html += `<div style="margin:4px 0;color:${color};text-decoration:${decoration};">${icon} ${obj.label || obj.description}</div>`;
      }
    } else {
      html += '<div style="color:rgba(255,255,255,0.4);font-style:italic;">No active objectives</div>';
    }

    listEl.innerHTML = html;

    // Leave dungeon button
    if (this._dungeonMode) {
      let btn = container.querySelector('.gw-leave-dungeon');
      if (!btn) {
        btn = document.createElement('button');
        btn.className = 'gw-leave-dungeon';
        btn.textContent = 'Leave Dungeon';
        btn.style.cssText = `
          display:block;width:100%;margin-top:16px;padding:8px 12px;
          background:rgba(153,27,27,0.6);color:#fca5a5;
          border:1px solid rgba(220,38,38,0.4);border-radius:4px;
          cursor:pointer;font-family:'Cinzel',serif;font-size:11px;
          font-weight:600;letter-spacing:0.5px;text-transform:uppercase;
          transition:background 0.2s,border-color 0.2s;
        `;
        btn.addEventListener('mouseenter', () => {
          btn.style.background = 'rgba(185,28,28,0.8)';
          btn.style.borderColor = 'rgba(248,113,113,0.6)';
        });
        btn.addEventListener('mouseleave', () => {
          btn.style.background = 'rgba(153,27,27,0.6)';
          btn.style.borderColor = 'rgba(220,38,38,0.4)';
        });
        btn.addEventListener('click', () => {
          if (window.game?.exitDungeon) window.game.exitDungeon();
        });
        container.appendChild(btn);
      }
    }
  }

  /**
   * Show victory screen after dungeon completion.
   * @private
   */
  _showLevelUpNotification(level) {
    window.game?.audioSystem?.audio?.playUiFanfare();
    const banner = document.createElement('div');
    banner.style.cssText = `
      position: fixed; top: 20%; left: 50%; transform: translateX(-50%);
      z-index: 9999; pointer-events: none;
      font-family: 'Cinzel Decorative', 'Times New Roman', serif;
      font-size: 36px; font-weight: 700; color: #ffd700;
      text-shadow: 0 0 20px rgba(255,215,0,0.4), 0 0 40px rgba(255,170,0,0.2), 0 2px 8px rgba(0,0,0,0.8);
      opacity: 0; transition: opacity 0.3s;
      white-space: nowrap;
    `;
    banner.innerHTML = `LEVEL ${level}!<br><span style="font-size:16px; font-family:'Cinzel','Times New Roman',serif; color:#cba6f7;">+3 Stat Points</span>`;
    document.body.appendChild(banner);
    requestAnimationFrame(() => { banner.style.opacity = '1'; });
    setTimeout(() => { banner.style.opacity = '0'; }, 2500);
    setTimeout(() => banner.remove(), 3000);
  }

  _showVictoryScreen() {
    const overlay = document.createElement('div');
    overlay.id = 'dungeon-victory';
    overlay.style.cssText = `
      position: fixed; inset: 0; z-index: 10000;
      background: rgba(0,0,0,0.8); display: flex;
      flex-direction: column; align-items: center; justify-content: center;
      color: #ffd700;
    `;
    overlay.innerHTML = `
      <h1 style="font-family: 'Cinzel Decorative', 'Times New Roman', serif; font-size: 48px; margin: 0 0 16px; text-shadow: 0 0 20px rgba(255,215,0,0.4), 0 0 40px rgba(255,170,0,0.2), 0 2px 8px rgba(0,0,0,0.8);">DUNGEON COMPLETE</h1>
      <p style="font-family: 'Cinzel', 'Times New Roman', serif; font-size: 18px; color: #cdd6f4; margin: 0 0 32px;">All levels cleared!</p>
      <button id="victory-return" style="font-family: 'Cinzel', 'Times New Roman', serif; padding: 14px 32px; background: #89b4fa; color: #1e1e2e; border: none; border-radius: 8px; cursor: pointer; font-weight: 600; font-size: 16px;">Return to World</button>
    `;
    document.body.appendChild(overlay);

    document.getElementById('victory-return').addEventListener('click', () => {
      overlay.remove();
      this.exitDungeon();
    });
  }

  // ---- Dungeon Chest & Death ----

  /**
   * Check if player is near a chest and show/hide prompt.
   * @private
   */
  _updateChestPrompt() {
    if (!this.hudManager || !this.player || !this.dungeonGenerator) return;
    let nearChest = false;
    const chestLvl = this.dungeonGenerator.currentLevelIndex;
    for (const [, cuboids] of this.dungeonGenerator.streamer._chunkCuboids) {
      for (const cuboid of cuboids) {
        if (cuboid._dungeonRole !== 'chest') continue;
        if (this.dungeonGenerator._openedChests.has(`L${chestLvl}_${cuboid.getKey()}`)) continue;
        const cx = cuboid.x + cuboid.width / 2;
        const cz = cuboid.z + cuboid.depth / 2;
        const dx = this.player.x - cx;
        const dz = this.player.z - cz;
        if (Math.sqrt(dx * dx + dz * dz) < this.dungeonGenerator.CHEST_RANGE) {
          nearChest = true;
          break;
        }
      }
      if (nearChest) break;
    }
    this.hudManager.showChestPrompt(nearChest);
  }

  /**
   * Set up click-to-attack: left click = punch toward cursor, right click = fireball toward cursor.
   * Only active when player exists and not in editor/dialog.
   * @private
   */
  _setupClickToAttack() {
    if (this._clickToAttackSetup) return;
    this._clickToAttackSetup = true;

    const container = document.getElementById('container');
    if (!container) return;

    // Track mousedown position to distinguish clicks from drags
    let attackMouseDown = null;

    container.addEventListener('mousedown', (e) => {
      if (e.button !== 0 && e.button !== 2) return;
      attackMouseDown = { x: e.clientX, y: e.clientY, button: e.button };
    });

    window.addEventListener('mouseup', (e) => {
      if (!attackMouseDown || attackMouseDown.button !== e.button) {
        attackMouseDown = null;
        return;
      }

      const moveDist = Math.abs(e.clientX - attackMouseDown.x) + Math.abs(e.clientY - attackMouseDown.y);
      const button = attackMouseDown.button;
      attackMouseDown = null;

      if (moveDist > 5) return; // was a drag, not a click

      if (!this.player || this.player.combatStats?.isDead) return;
      if (this._deathScreenActive) return;
      if (this.dialogManager?.isActive) return;

      const target = document.elementFromPoint(e.clientX, e.clientY);
      if (target && target.closest('.coords, .info-panel, .gui-panel, .sidebar-panel, .top-toolbar, .top-sheet, .settings-panel, .hud-container, .death-screen')) return;

      const controller = this.player.controller;
      if (!controller || !controller.enabled) return;
      // Block attacks while previous attack animation is still playing
      if (this.player.animator?.isPlayingAttack()) return;

      // Convert click to world position
      const CUBE_SIZE = Game.CUBE_SIZE;
      const screenX = e.clientX - window.innerWidth / 2;
      const screenY = e.clientY - window.innerHeight / 2;
      const renderingYOffset = CUBE_SIZE / 2;
      const adjustedScreenY = screenY - renderingYOffset * this.camera.zoomLevel;

      const playerWorldY = this.player.y * CUBE_SIZE;
      const worldPos = Transform.screenToWorld(screenX, adjustedScreenY, this.camera, playerWorldY);

      const clickX = worldPos.x / CUBE_SIZE;
      const clickZ = -worldPos.z / CUBE_SIZE;

      // Direction from player to click
      const dx = clickX - this.player.x;
      const dz = clickZ - this.player.z;
      if (Math.abs(dx) < 0.01 && Math.abs(dz) < 0.01) return;

      if (button === 0) {
        // LEFT CLICK: attack only if clicking near an enemy, otherwise let click-to-move handle it
        const nearbyEnemy = this._findEnemyNearClick(clickX, clickZ, 2.0);
        if (!nearbyEnemy) return; // No enemy near click — click-to-move will handle navigation

        // Face the enemy and punch
        const eDx = nearbyEnemy.x - this.player.x;
        const eDz = nearbyEnemy.z - this.player.z;
        const angle = DirectionResolver.angleFromMovement(eDx, eDz);
        if (angle !== null) this.player.setFacing(angle);

        const config = this.player.config || {};
        const weaponAnims = this._getWeaponAttackAnims();
        const animName = weaponAnims?.[0] || config.punch || 'lead-jab';
        this.player.triggerAction(animName);
        if (this.audioSystem) this.audioSystem.playAttackSound(this.player, 'punch', true);
        if (this.playerSync) this.playerSync.broadcastAction({ type: 'anim', animName });
        if (controller.onAttackStart) controller.onAttackStart(this.player, 'light');

        // Prevent click-to-move from also triggering
        this._clickToAttackConsumed = true;
        setTimeout(() => { this._clickToAttackConsumed = false; }, 50);

      } else if (button === 2) {
        // RIGHT CLICK (no drag): fireball toward cursor
        const angle = DirectionResolver.angleFromMovement(dx, dz);
        if (angle !== null) this.player.setFacing(angle);

        const config = this.player.config || {};
        const animName = config.special || 'fireball';
        this.player.triggerAction(animName);
        if (this.audioSystem) this.audioSystem.playAttackSound(this.player, 'fireball', true);
        if (this.playerSync) this.playerSync.broadcastAction({ type: 'anim', animName });
        if (controller.onAttackStart) controller.onAttackStart(this.player, 'special');
      }
    });
  }

  /**
   * Get the equipped weapon's item definition (from mainhand slot).
   * @returns {Object|null} Item definition or null if unarmed
   * @private
   */
  _getEquippedWeaponDef() {
    const weapon = this.inventory?.equipped?.mainhand;
    if (!weapon) return null;
    return weapon.def || ITEM_DEFS[weapon.itemId] || null;
  }

  /**
   * Check resource cost for an attack and deduct if sufficient.
   * @param {string} attackType - 'light', 'heavy', or 'special'
   * @returns {boolean} true if attack is allowed (resources deducted)
   */
  _tryConsumeAttackResources(attackType) {
    const stats = this.player?.combatStats;
    if (!stats) return true;
    const weaponDef = this._getEquippedWeaponDef();
    const projType = weaponDef?.projectileType;
    const isSpellWeapon = projType && SPELL_PROJECTILES.includes(projType);

    let manaCost = 0;
    let staminaCost = 0;

    if (attackType === 'special') {
      manaCost = 20;
    } else if (attackType === 'heavy') {
      if (isSpellWeapon) {
        manaCost = 20;
      } else {
        staminaCost = 25;
      }
    } else if (attackType === 'light') {
      if (isSpellWeapon) {
        manaCost = 10;
      }
      // Melee/bow light attacks are free
    }

    if (manaCost > 0 && stats.mana < manaCost) {
      this.hudManager?.showNotification('Not enough mana', '#6699ff');
      return false;
    }
    if (staminaCost > 0 && stats.stamina < staminaCost) {
      this.hudManager?.showNotification('Not enough stamina', '#ffcc00');
      return false;
    }

    if (manaCost > 0) stats.mana -= manaCost;
    if (staminaCost > 0) stats.stamina -= staminaCost;
    return true;
  }

  /**
   * Regenerate health, mana, and stamina each frame.
   */
  _updateRegeneration(deltaTime) {
    const stats = this.player?.combatStats;
    if (!stats || stats.isDead) return;

    // Health regen delay countdown (blocks regen after taking damage)
    if (stats.healthRegenDelay > 0) {
      stats.healthRegenDelay -= deltaTime;
    }

    // Health regen (slow, delayed after damage)
    if (stats.health < stats.maxHealth && stats.healthRegenDelay <= 0) {
      stats.health = Math.min(stats.maxHealth, stats.health + stats.healthRegen * deltaTime);
    }

    // Mana regen (always)
    if (stats.mana < stats.maxMana) {
      stats.mana = Math.min(stats.maxMana, stats.mana + stats.manaRegen * deltaTime);
    }

    // Stamina regen (only when not sprinting)
    const ctrl = this.player.controller;
    const isSprinting = ctrl?.isRunning && this.player.isMoving;
    if (!isSprinting && stats.stamina < stats.maxStamina) {
      stats.stamina = Math.min(stats.maxStamina, stats.stamina + stats.staminaRegen * deltaTime);
    }

    // Update floating resource bars (show when not full, hide when full)
    this._updateFloatingResourceBars();
  }

  /**
   * Update floating MP/SP bars over the player's head.
   * Shows bars when below max, hides when full.
   * @private
   */
  _updateFloatingResourceBars() {
    const p = this.player;
    if (!p?.combatStats) return;
    const s = p.combatStats;

    // Stamina bar
    if (p._floatingSpFill) {
      const pct = (s.stamina / s.maxStamina) * 100;
      p._floatingSpFill.style.width = `${pct}%`;
      if (p._floatingSpBar) p._floatingSpBar.style.display = pct >= 99.9 ? 'none' : 'block';
    }
    // Mana bar
    if (p._floatingMpFill) {
      const pct = (s.mana / s.maxMana) * 100;
      p._floatingMpFill.style.width = `${pct}%`;
      if (p._floatingMpBar) p._floatingMpBar.style.display = pct >= 99.9 ? 'none' : 'block';
    }
    // Health bar: hide when full (already shown by CombatManager on damage)
    if (p._floatingHpFill) {
      const pct = (s.health / s.maxHealth) * 100;
      p._floatingHpFill.style.width = `${pct}%`;
      p._floatingHpFill.style.backgroundColor = pct > 60 ? '#22c55e' : pct > 30 ? '#eab308' : '#ef4444';
      if (p._floatingHpBar) p._floatingHpBar.style.display = pct >= 99.9 ? 'none' : 'block';
    }
  }

  // ── Detached weapon animations (boomerang) ──────────────────────────

  /**
   * Spawn a detached boomerang weapon sprite at the character's throw position.
   * Uses the raw weapon spritesheet (not the full composite atlas) so only
   * the boomerang is rendered. Creates an Impostor for proper depth sorting.
   * @private
   */
  _spawnDetachedWeaponAnim(character) {
    const SHEET_URL = '/lpc/spritesheets/weapon/ranged/boomerang/boomerang.webp';
    const FRAME_COUNT = 6;  // slash_reverse frames
    const FRAME_TIME = 80;  // ms per frame (matches weapon-slash-reverse)

    // Ensure spritesheet is cached
    if (!this._boomerangSheet) {
      this._boomerangSheet = new Image();
      this._boomerangSheet.src = SHEET_URL;
    }
    const img = this._boomerangSheet;

    // Create impostor for depth-sorted world-space rendering
    const impostor = new Impostor({
      id: `boomerang_${Date.now()}`,
      x: character.x,
      y: character.y,
      z: character.z,
      textureUrl: '',
      spriteWidth: 1,
      spriteHeight: 1,
      scale: character.height
    });
    impostor._isProjectile = true;
    impostor._skipSync = true;
    impostor._skipValidation = true;
    impostor._isDynamic = true;
    impostor.sortWidth = 0.9;
    impostor.sortDepth = 0.9;
    impostor._sortMinY = character.y;
    impostor._sortHeight = 2;

    // Style the impostor element for spritesheet rendering
    Object.assign(impostor.element.style, {
      backgroundImage: '',
      backgroundSize: '',
      imageRendering: 'pixelated',
      overflow: 'visible'
    });

    // Inner visual for the spritesheet (supports oversize overflow)
    const visual = document.createElement('div');
    Object.assign(visual.style, {
      position: 'absolute',
      imageRendering: 'pixelated',
      backgroundRepeat: 'no-repeat',
      pointerEvents: 'none'
    });
    impostor.element.appendChild(visual);

    this.world.addImpostor(impostor);
    if (this.camera) impostor.update(this.camera);
    impostor.element.style.display = '';

    const anim = {
      impostor,
      visual,
      img,
      character,
      facingAngle: character.facingAngle,
      height: character.height,
      currentFrame: 0,
      frameCount: FRAME_COUNT,
      frameTime: FRAME_TIME,
      elapsed: 0,
      frameW: 0,
      frameH: 0,
      ready: false,
      done: false
    };

    const onReady = () => {
      anim.frameW = img.width / FRAME_COUNT;
      anim.frameH = img.height / 4;
      anim.ready = true;
      visual.style.backgroundImage = `url(${img.src})`;
    };

    if (img.complete && img.naturalWidth > 0) {
      onReady();
    } else {
      img.onload = onReady;
    }

    this._detachedAnims.push(anim);
  }

  // Direction → LPC spritesheet row (4 rows: N=0, W=1, S=2, E=3)
  static _LPC_DIR = {
    'south':      { row: 2, flip: false },
    'south-east': { row: 3, flip: false },
    'east':       { row: 3, flip: false },
    'north-east': { row: 0, flip: false },
    'north':      { row: 0, flip: false },
    'north-west': { row: 0, flip: true  },
    'west':       { row: 1, flip: false },
    'south-west': { row: 1, flip: false }
  };

  /**
   * Update all detached weapon animations. Called each frame from game loop.
   * @private
   */
  _updateDetachedAnims(deltaTime, camera) {
    if (!this._detachedAnims || this._detachedAnims.length === 0) return;

    const isAndroid = DOMUtils.isAndroid();
    const zoom = isAndroid ? camera.zoomLevel : 1;
    const effectiveCubeSize = camera.CUBE_SIZE * zoom;

    for (let i = this._detachedAnims.length - 1; i >= 0; i--) {
      const anim = this._detachedAnims[i];
      if (!anim.ready) continue;

      // Advance frames
      anim.elapsed += deltaTime * 1000;
      while (anim.elapsed >= anim.frameTime) {
        anim.elapsed -= anim.frameTime;
        anim.currentFrame++;
        if (anim.currentFrame >= anim.frameCount) {
          anim.done = true;
          break;
        }
      }

      if (anim.done) {
        this.world.removeImpostor(anim.impostor.id);
        this._detachedAnims.splice(i, 1);
        continue;
      }

      // Follow the character's position (but keep locked throw direction)
      if (anim.character) {
        anim.impostor.x = anim.character.x;
        anim.impostor.y = anim.character.y;
        anim.impostor.z = anim.character.z;
      }

      // Direction from fixed facing angle + current camera
      const dir = DirectionResolver.resolve(anim.facingAngle, camera.angle || 0);
      const { row, flip } = Game._LPC_DIR[dir];

      // Spritesheet frame source position
      const srcX = anim.currentFrame * anim.frameW;
      const srcY = row * anim.frameH;

      // Scale: match character sprite (base body 64px)
      const scale = (anim.height * effectiveCubeSize) / 64;
      const dispW = anim.frameW * scale;
      const dispH = anim.frameH * scale;
      const sheetW = anim.img.width * scale;
      const sheetH = anim.img.height * scale;

      // The impostor positions itself via update(). We override the element
      // size to be body-sized (64*scale) and let the visual overflow for
      // the oversize weapon sprite.
      const bodySize = 64 * scale;
      anim.impostor.update(camera);

      Object.assign(anim.impostor.element.style, {
        width: `${bodySize}px`,
        height: `${bodySize}px`,
        display: ''
      });

      // Visual: oversize weapon frame centered on body
      const offsetX = (dispW - bodySize) / 2;
      const offsetY = (dispH - bodySize) / 2;

      Object.assign(anim.visual.style, {
        width: `${dispW}px`,
        height: `${dispH}px`,
        left: `${-offsetX}px`,
        top: `${-offsetY}px`,
        backgroundSize: `${sheetW}px ${sheetH}px`,
        backgroundPosition: `-${srcX * scale}px -${srcY * scale}px`,
        transform: flip ? 'scaleX(-1)' : ''
      });
    }
  }

  /**
   * Get the weapon-specific attack animation names [light, heavy, special].
   * @returns {string[]|null} Array of trixel animation names, or null if unarmed
   * @private
   */
  _getWeaponAttackAnims() {
    const def = this._getEquippedWeaponDef();
    if (!def?.attackAnims) return null;
    // Weapon animations (weapon-slash, weapon-thrust, etc.) only exist in LPC
    // composite atlases. Legacy character atlases don't have them, so fall back
    // to character-specific punch/kick animations by returning null.
    const atlas = this.player?.animator?.atlas;
    if (!atlas?.animationLookup?.has(def.attackAnims[0])) return null;
    return def.attackAnims;
  }

  /**
   * Set up keyboard attack handlers (E = punch, R = kick, F = fireball).
   * Handles attacks directly via keydown events — same path as joystick
   * and click-to-attack — so keyboard attacks work even if
   * Character.update() is skipped or keyboard events are partially blocked.
   * @private
   */
  _setupKeyboardAttacks() {
    if (this._keyboardAttacksSetup) return;
    this._keyboardAttacksSetup = true;

    const held = { e: false, r: false, f: false };

    window.addEventListener('keydown', (e) => {
      // Don't handle when typing in input fields or dialog is active
      const el = document.activeElement;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return;
      if (this.dialogManager?.isActive) return;

      const key = e.key.length === 1 ? e.key.toLowerCase() : e.key;
      if (key !== 'e' && key !== 'r' && key !== 'f') return;
      if (e.repeat) return;
      if (held[key]) return;
      held[key] = true;

      if (!this.player || this.player.combatStats?.isDead) return;
      if (this._deathScreenActive) return;
      const ctrl = this.player.controller;
      if (!ctrl || !ctrl.enabled) return;
      // Block attacks while previous attack animation is still playing
      if (this.player.animator?.isPlayingAttack()) return;

      const config = this.player.config || {};
      const weaponAnims = this._getWeaponAttackAnims();

      if (key === 'e') {
        // Light attack — also opens chests in dungeon mode
        if (this._dungeonMode && this.dungeonGenerator) {
          if (this.dungeonGenerator.tryOpenChest(this.player)) {
            this.player.triggerAction('picking-up');
            return;
          }
        }
      }

      if (key === 'e') {
        const animName = weaponAnims?.[0] || config.punch || 'lead-jab';
        this.player.triggerAction(animName);
        if (this.audioSystem) this.audioSystem.playAttackSound(this.player, 'punch', true);
        if (this.playerSync) this.playerSync.broadcastAction({ type: 'anim', animName });
        if (ctrl.onAttackStart) ctrl.onAttackStart(this.player, 'light');
      } else if (key === 'r') {
        // Heavy attack
        const animName = weaponAnims?.[1] || config.kick || 'roundhouse-kick';
        this.player.triggerAction(animName);
        if (this.audioSystem) this.audioSystem.playAttackSound(this.player, 'kick', true);
        if (this.playerSync) this.playerSync.broadcastAction({ type: 'anim', animName });
        if (ctrl.onAttackStart) ctrl.onAttackStart(this.player, 'heavy');
      } else if (key === 'f') {
        // Special attack: ALWAYS fireball regardless of weapon
        const animName = config.special || 'fireball';
        this.player.triggerAction(animName);
        if (this.audioSystem) this.audioSystem.playAttackSound(this.player, 'fireball', true);
        if (this.playerSync) this.playerSync.broadcastAction({ type: 'anim', animName });
        if (ctrl.onAttackStart) ctrl.onAttackStart(this.player, 'special');
      }
    });

    window.addEventListener('keyup', (e) => {
      const key = e.key.length === 1 ? e.key.toLowerCase() : e.key;
      if (key === 'e' || key === 'r' || key === 'f') {
        held[key] = false;
      }
    });

    // Reset held state on blur to prevent stuck keys
    window.addEventListener('blur', () => {
      held.e = false;
      held.r = false;
      held.f = false;
    });
  }

  /**
   * Auto-face the player toward the nearest hostile enemy within radius.
   * Used on mobile where joystick aiming is imprecise.
   * @param {number} radius - Search radius in game units
   * @private
   */
  _faceNearestEnemy(radius = 5) {
    if (!this.player || !this.world) return;
    let nearest = null;
    let nearestDist = radius;
    for (const char of this.world.characters.values()) {
      if (char === this.player) continue;
      if (!char.combatStats || char.combatStats.isDead) continue;
      if (!char.combatStats.isHostile) continue;
      const dx = char.x - this.player.x;
      const dz = char.z - this.player.z;
      const dist = Math.sqrt(dx * dx + dz * dz);
      if (dist < nearestDist) {
        nearest = char;
        nearestDist = dist;
      }
    }
    if (nearest) {
      const dx = nearest.x - this.player.x;
      const dz = nearest.z - this.player.z;
      const angle = DirectionResolver.angleFromMovement(dx, dz);
      if (angle !== null) this.player.setFacing(angle);
    }
  }

  /**
   * Find a hostile enemy near a world position (for click-to-attack).
   * @param {number} wx - World X
   * @param {number} wz - World Z
   * @param {number} radius - Search radius
   * @returns {Character|null}
   * @private
   */
  _findEnemyNearClick(wx, wz, radius) {
    let closest = null;
    let closestDist = radius;

    for (const char of this.world.characters.values()) {
      if (char === this.player) continue;
      if (!char.combatStats || char.combatStats.isDead) continue;
      if (!char.combatStats.isHostile) continue;

      const dx = char.x - wx;
      const dz = char.z - wz;
      const dist = Math.sqrt(dx * dx + dz * dz);
      if (dist < closestDist) {
        closest = char;
        closestDist = dist;
      }
    }
    return closest;
  }

  /**
   * Trigger player death (from combat or falling off map).
   * Shows death screen and plays death music.
   * @private
   */
  _triggerPlayerDeath() {
    if (this._deathScreenActive) return;
    this._deathScreenActive = true;

    // Mark dead
    if (this.player.combatStats) {
      this.player.combatStats.isDead = true;
      this.player.combatStats.health = 0;
    }
    if (this.player.controller) this.player.controller.enabled = false;

    // Death sound
    if (this.audioSystem) {
      this.audioSystem.playDeathSound(this.player, true);
    }

    // Set sad music mood
    if (this.audioSystem && this.audioSystem.audio && this.audioSystem.audio.musicGenerator) {
      this.audioSystem.audio.musicGenerator.setMood('chill');
    }

    // --- Death cam: slow quadratic zoom into the body ---
    const startZoom = this.orbitalCamera.currentZoomLevel;
    this._deathCam = {
      savedZoom: this.orbitalCamera.targetZoomLevel,
      savedFollowSpeed: this.orbitalCamera.followSpeed,
      startZoom,
      endZoom: startZoom * 2,
      elapsed: 0,
      duration: 3.5 // seconds for full zoom
    };
    this.orbitalCamera.followSpeed = 2.0;

    // Show death screen after death animation has time to play
    setTimeout(() => {
      this.hudManager.showDeathScreen(() => {
        this._stopDeathCam();
        this._deathScreenActive = false;
        // Reset player
        if (this.player.combatStats) {
          this.player.combatStats.reset();
          this.progression.applyTo(this.player.combatStats);
          this.lootManager._applyEquipmentBonuses(this.player);
        }
        // Hide floating health bar (full health after respawn)
        if (this.player._floatingHpBar) {
          this.player._floatingHpFill.style.width = '100%';
          this.player._floatingHpBar.style.display = 'none';
        }
        if (this.player.sprite?._visual) {
          this.player.sprite._visual.style.opacity = '';
          this.player.sprite._visual.style.filter = '';
        }
        if (this.player.controller) this.player.controller.enabled = true;
        if (this.player.animator && this.player.animations) {
          this.player.animator.setState(this.player.animations.idle, true);
        }
        this.respawnPlayer();
      });
    }, 1200);
  }

  /**
   * Stop the death cam and restore camera to pre-death state.
   * @private
   */
  _stopDeathCam() {
    if (!this._deathCam) return;
    // Restore zoom and follow speed to pre-death values
    this.orbitalCamera.targetZoomLevel = this._deathCam.savedZoom;
    this.orbitalCamera.currentZoomLevel = this._deathCam.savedZoom;
    this.camera.zoomLevel = this._deathCam.savedZoom;
    this.orbitalCamera.followSpeed = this._deathCam.savedFollowSpeed;
    this._deathCam = null;
  }

  /**
   * Cache the lowest cuboid Y in the world.
   * Used by void fall detection — player must be below this to trigger respawn.
   * @private
   */
  _updateWorldLowestY() {
    let lowest = Infinity;
    for (const cuboid of this.world.cuboids.values()) {
      if (cuboid.y < lowest) lowest = cuboid.y;
    }
    this._worldLowestY = lowest === Infinity ? -Infinity : lowest;
  }

  /**
   * Trigger void respawn in non-dungeon mode.
   * Shows a minimal respawn overlay (no death text).
   * @private
   */
  _triggerVoidRespawn() {
    if (this._deathScreenActive) return;
    this._deathScreenActive = true;

    this.hudManager.showVoidRespawnScreen(() => {
      this._deathScreenActive = false;
      this.respawnPlayer();
    });
  }

  /**
   * Initialize UI elements, including FPS display,
   * sun vector controls, and a sun intensity control.
   */
  initUI() {
    // Add FPS display (top left) — hidden by default, toggled in Settings > Debug
    this.fpsDisplay = document.createElement('div');
    this.fpsDisplay.className = 'fps-display';
    this.fpsDisplay.style.display = 'none';
    document.body.appendChild(this.fpsDisplay);

    // Add World Controls to Settings > Game tab
    this.initWorldControls();

    // Add Multiplayer controls to Settings > Game tab
    this.initMultiplayer();

    // Add Profanity Filter toggle to Settings > Game tab
    this.initProfanityFilter();

    // Add Character Creator button to Settings > Game tab
    this.initCharacterButton();

    // Add Sky controls to Settings > Display tab
    this.initSkyControls();

    // Add Vegetation controls to Settings > Display tab
    this.initVegetationControls();

    // Add Lighting/AO controls to Settings > Display tab
    this.initLightingControls();

    // Add Rendering controls to Settings > Display tab
    this.initRenderingControls();

    // Add Camera Info to Settings > Debug tab
    this.initCameraInfo();

    // Add FPS toggle to Settings > Debug tab
    this.initFpsToggle();

    // FPS tracking.
    this.frames = 0;
    this.lastFpsUpdate = 0;
    this.fps = 0;
  }

  /**
   * Initialize camera info display in the Debug tab
   */
  initCameraInfo() {
    const debugContainer = this.settingsPanel.getTabContainer('debug');

    const cameraSection = document.createElement('div');
    cameraSection.id = 'cameraInfoSection';
    cameraSection.className = 'gui-section';

    cameraSection.innerHTML = `
      <div class="gui-section-header">
        <h3 class="gui-section-title">Camera Info</h3>
        <svg class="gui-section-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </div>
      <div class="gui-section-body">
        <div class="gui-info" style="font-family: var(--font-mono); font-size: 11px; line-height: 1.6;">
          <div id="rot">Rotation: 0°</div>
          <div id="skewValue">Skew: 0.30</div>
          <div id="zoomDisplay">Zoom: 1.00</div>
          <div id="pos">Position: [0, 0]</div>
          <div id="cullStats">Culled: 0/0</div>
        </div>
      </div>
    `;

    debugContainer.appendChild(cameraSection);

    // Add collapse functionality
    const header = cameraSection.querySelector('.gui-section-header');
    header.addEventListener('click', () => {
      cameraSection.classList.toggle('collapsed');
    });
  }

  /**
   * Initialize Profanity Filter toggle in the Game tab
   */
  initProfanityFilter() {
    const gameContainer = this.settingsPanel.getTabContainer('game');

    const section = document.createElement('div');
    section.className = 'gui-section';
    section.innerHTML = `
      <div class="gui-section-header">
        <span class="gui-section-title">Chat Filter</span>
      </div>
      <div class="gui-section-body">
        <div class="gui-row">
          <label class="gui-checkbox-container">
            <input type="checkbox" class="gui-checkbox" id="profanityFilterToggle"
              ${ProfanityFilter.isEnabled() ? 'checked' : ''}>
            <span class="gui-checkbox-label">Profanity Filter</span>
          </label>
        </div>
        <div style="color:#888; font-size:11px; padding:2px 4px 0;">
          Messages to AI are always filtered.
        </div>
      </div>
    `;
    gameContainer.appendChild(section);

    document.getElementById('profanityFilterToggle').addEventListener('change', (e) => {
      ProfanityFilter.setEnabled(e.target.checked);
    });
  }

  /**
   * Initialize Character Creator button in the Game tab
   */
  initCharacterButton() {
    const gameContainer = this.settingsPanel.getTabContainer('game');

    const section = document.createElement('div');
    section.className = 'gui-section';
    section.innerHTML = `
      <div class="gui-section-header">
        <span class="gui-section-title">Character</span>
      </div>
      <div class="gui-section-body">
        <button id="openCharacterCreatorBtn" class="gui-button gui-button-full">
          Open Character Creator
        </button>
        <div style="color:#888; font-size:11px; padding:2px 4px 0;">
          Customize your character's appearance. (Shortcut: V)
        </div>
      </div>
    `;
    gameContainer.appendChild(section);

    document.getElementById('openCharacterCreatorBtn').addEventListener('click', () => {
      this.settingsPanel.close();
      this.windowManager.show('creator');
    });
  }

  /**
   * Initialize FPS toggle in the Debug tab
   */
  initFpsToggle() {
    const debugContainer = this.settingsPanel.getTabContainer('debug');

    const fpsSection = document.createElement('div');
    fpsSection.className = 'gui-section';
    fpsSection.innerHTML = `
      <div class="gui-section-header">
        <span class="gui-section-title">Performance</span>
      </div>
      <div class="gui-section-body">
        <div class="gui-row">
          <label class="gui-checkbox-container">
            <input type="checkbox" class="gui-checkbox" id="fpsToggle">
            <span class="gui-checkbox-label">Show FPS Counter</span>
          </label>
        </div>
      </div>
    `;
    debugContainer.appendChild(fpsSection);

    document.getElementById('fpsToggle').addEventListener('change', (e) => {
      this.fpsDisplay.style.display = e.target.checked ? 'block' : 'none';
    });
  }

  /**
   * Initialize sky/cloud backdrop controls in the Display tab
   */
  initSkyControls() {
    const displayContainer = this.settingsPanel.getTabContainer('display');

    const skySection = document.createElement('div');
    skySection.id = 'skyControls';
    skySection.className = 'gui-section';

    const layerCount = this.cloudBackdrop ? this.cloudBackdrop.getLayerCount() : 3;
    const tileSize = this.cloudBackdrop ? this.cloudBackdrop.getTileSize() : 64;

    skySection.innerHTML = `
      <div class="gui-section-header">
        <h3 class="gui-section-title">Sky</h3>
        <svg class="gui-section-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </div>
      <div class="gui-section-body">
        <div class="gui-row">
          <label class="gui-checkbox-container">
            <input type="checkbox" id="skyEnabled" class="gui-checkbox" checked>
            <span class="gui-checkbox-label">Enable Sky</span>
          </label>
        </div>
        <div class="gui-row">
          <span class="gui-label">Layers</span>
          <div class="gui-slider-row">
            <input type="range" id="skyLayers" class="gui-slider" min="1" max="3" step="1" value="${layerCount}">
            <input type="number" id="skyLayersValue" class="gui-input gui-slider-value" value="${layerCount}" step="1" min="1" max="3">
          </div>
        </div>
        <div class="gui-row">
          <span class="gui-label">Quality</span>
          <select id="skyTileSize" class="gui-input" style="width:auto;min-width:80px">
            <option value="8"${tileSize === 8 ? ' selected' : ''}>8</option>
            <option value="16"${tileSize === 16 ? ' selected' : ''}>16</option>
            <option value="32"${tileSize === 32 ? ' selected' : ''}>32</option>
            <option value="64"${tileSize === 64 ? ' selected' : ''}>64</option>
            <option value="128"${tileSize === 128 ? ' selected' : ''}>128</option>
          </select>
        </div>
      </div>
    `;

    displayContainer.appendChild(skySection);

    // Collapse toggle
    skySection.querySelector('.gui-section-header').addEventListener('click', () => {
      skySection.classList.toggle('collapsed');
    });

    // Enable/disable
    document.getElementById('skyEnabled').addEventListener('change', (e) => {
      if (this.cloudBackdrop) this.cloudBackdrop.setEnabled(e.target.checked);
    });

    // Layer count
    const skyLayersSlider = document.getElementById('skyLayers');
    const skyLayersValue = document.getElementById('skyLayersValue');
    skyLayersSlider.addEventListener('input', (e) => {
      const val = parseInt(e.target.value);
      skyLayersValue.value = val;
      if (this.cloudBackdrop) this.cloudBackdrop.setLayerCount(val);
    });
    skyLayersValue.addEventListener('input', (e) => {
      const val = Math.max(1, Math.min(3, parseInt(e.target.value) || 1));
      skyLayersSlider.value = val;
      if (this.cloudBackdrop) this.cloudBackdrop.setLayerCount(val);
    });

    // Tile size (quality)
    document.getElementById('skyTileSize').addEventListener('change', (e) => {
      const val = parseInt(e.target.value);
      if (this.cloudBackdrop) this.cloudBackdrop.setTileSize(val);
    });
  }

  /**
   * Initialize vegetation display controls in the Display tab
   */
  initVegetationControls() {
    const displayContainer = this.settingsPanel.getTabContainer('display');

    const vegSection = document.createElement('div');
    vegSection.id = 'vegetationControls';
    vegSection.className = 'gui-section';

    vegSection.innerHTML = `
      <div class="gui-section-header">
        <h3 class="gui-section-title">Vegetation</h3>
        <svg class="gui-section-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </div>
      <div class="gui-section-body">
        <div class="gui-row">
          <label class="gui-checkbox-container">
            <input type="checkbox" id="vegEnabled" class="gui-checkbox" checked>
            <span class="gui-checkbox-label">Show Vegetation</span>
          </label>
        </div>
        <div class="gui-row">
          <span class="gui-label">Density</span>
          <div class="gui-slider-row">
            <input type="range" id="vegDensity" class="gui-slider" min="0" max="1" step="0.05" value="${DungeonConfig.VEGETATION_DENSITY}">
            <input type="number" id="vegDensityValue" class="gui-input gui-slider-value" value="${Math.round(DungeonConfig.VEGETATION_DENSITY * 100)}" step="5" min="0" max="100">
          </div>
        </div>
      </div>
    `;

    displayContainer.appendChild(vegSection);

    // Collapse toggle
    const header = vegSection.querySelector('.gui-section-header');
    header.addEventListener('click', () => {
      vegSection.classList.toggle('collapsed');
    });

    // Vegetation enabled toggle
    document.getElementById('vegEnabled').addEventListener('change', (e) => {
      this.world._vegetationVisible = e.target.checked;
      if (e.target.checked) {
        // Re-show: if in dungeon mode, reload vegetation from streamer
        if (this._dungeonMode && this.dungeonGenerator?.streamer) {
          this.dungeonGenerator.streamer.reloadAllVegetation();
        } else {
          // Normal world: just show existing vegetation impostors
          for (const impostor of this.world.impostors.values()) {
            if (impostor._isVegetation) {
              impostor.element.style.display = '';
            }
          }
        }
      } else {
        this.world.setVegetationVisible(false);
      }
      this.world.forceRender();
    });

    // Density slider
    const vegDensitySlider = document.getElementById('vegDensity');
    const vegDensityValue = document.getElementById('vegDensityValue');
    vegDensitySlider.addEventListener('input', (e) => {
      const value = parseFloat(e.target.value);
      DungeonConfig.VEGETATION_DENSITY = value;
      vegDensityValue.value = Math.round(value * 100);
      // Reload dungeon vegetation with new density
      if (this._dungeonMode && this.dungeonGenerator?.streamer) {
        this.dungeonGenerator.streamer.reloadAllVegetation();
      }
    });
    vegDensityValue.addEventListener('input', (e) => {
      const pct = parseInt(e.target.value) || 0;
      const value = Math.max(0, Math.min(1, pct / 100));
      DungeonConfig.VEGETATION_DENSITY = value;
      vegDensitySlider.value = value;
      if (this._dungeonMode && this.dungeonGenerator?.streamer) {
        this.dungeonGenerator.streamer.reloadAllVegetation();
      }
    });
  }

  /**
   * Initialize lighting and AO controls in the Game tab
   */
  initLightingControls() {
    const displayContainer = this.settingsPanel.getTabContainer('display');

    const lightingSection = document.createElement('div');
    lightingSection.id = 'lightingControls';
    lightingSection.className = 'gui-section';

    lightingSection.innerHTML = `
      <div class="gui-section-header">
        <h3 class="gui-section-title">Lighting</h3>
        <svg class="gui-section-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </div>
      <div class="gui-section-body">
        <div class="gui-subsection">
          <div class="gui-subsection-title">Sun Direction</div>
          <div class="gui-row">
            <span class="gui-label">X</span>
            <div class="gui-slider-row">
              <input type="range" id="sunX" class="gui-slider" min="-2" max="2" step="0.1" value="${window.sunVector.x}">
              <input type="number" id="sunXValue" class="gui-input gui-slider-value" value="${window.sunVector.x}" step="0.1">
            </div>
          </div>
          <div class="gui-row">
            <span class="gui-label">Y</span>
            <div class="gui-slider-row">
              <input type="range" id="sunY" class="gui-slider" min="-2" max="2" step="0.1" value="${window.sunVector.y}">
              <input type="number" id="sunYValue" class="gui-input gui-slider-value" value="${window.sunVector.y}" step="0.1">
            </div>
          </div>
          <div class="gui-row">
            <span class="gui-label">Z</span>
            <div class="gui-slider-row">
              <input type="range" id="sunZ" class="gui-slider" min="-2" max="2" step="0.1" value="${window.sunVector.z}">
              <input type="number" id="sunZValue" class="gui-input gui-slider-value" value="${window.sunVector.z}" step="0.1">
            </div>
          </div>
          <div class="gui-row">
            <span class="gui-label">Intensity</span>
            <div class="gui-slider-row">
              <input type="range" id="sunIntensity" class="gui-slider" min="0" max="1" step="0.1" value="${window.sunIntensity}">
              <input type="number" id="sunIntensityValue" class="gui-input gui-slider-value" value="${window.sunIntensity}" step="0.1" min="0" max="1">
            </div>
          </div>
        </div>

        <div class="gui-subsection">
          <div class="gui-subsection-title">Ambient Occlusion</div>
          <div class="gui-row">
            <label class="gui-checkbox-container">
              <input type="checkbox" id="aoEnabled" class="gui-checkbox" ${AmbientOcclusion.enabled ? 'checked' : ''}>
              <span class="gui-checkbox-label">Enabled</span>
            </label>
          </div>
          <div class="gui-row">
            <span class="gui-label">Intensity</span>
            <div class="gui-slider-row">
              <input type="range" id="aoIntensity" class="gui-slider" min="0" max="2" step="0.1" value="${AmbientOcclusion.intensity}">
              <input type="number" id="aoIntensityValue" class="gui-input gui-slider-value" value="${AmbientOcclusion.intensity}" step="0.1">
            </div>
          </div>
          <div class="gui-row">
            <span class="gui-label">Radius</span>
            <div class="gui-slider-row">
              <input type="range" id="aoRadius" class="gui-slider" min="0.5" max="8" step="0.5" value="${AmbientOcclusion.radius}">
              <input type="number" id="aoRadiusValue" class="gui-input gui-slider-value" value="${AmbientOcclusion.radius}" step="0.5">
            </div>
          </div>
          <div class="gui-row">
            <span class="gui-label">Samples</span>
            <div class="gui-slider-row">
              <input type="range" id="aoSamples" class="gui-slider" min="4" max="32" step="4" value="${AmbientOcclusion.numRays}">
              <input type="number" id="aoSamplesValue" class="gui-input gui-slider-value" value="${AmbientOcclusion.numRays}" step="4" min="4" max="32">
            </div>
          </div>
          <div class="gui-row">
            <span class="gui-label">Resolution</span>
            <div class="gui-slider-row">
              <input type="range" id="aoResolution" class="gui-slider" min="1" max="8" step="1" value="${AmbientOcclusion.texelsPerUnit}">
              <input type="number" id="aoResolutionValue" class="gui-input gui-slider-value" value="${AmbientOcclusion.texelsPerUnit}" step="1" min="1" max="8">
            </div>
          </div>
        </div>
      </div>
    `;

    displayContainer.appendChild(lightingSection);

    // Add collapse functionality
    const header = lightingSection.querySelector('.gui-section-header');
    header.addEventListener('click', () => {
      lightingSection.classList.toggle('collapsed');
    });

    // Sun X - sync slider and number input
    const sunXSlider = document.getElementById('sunX');
    const sunXValue = document.getElementById('sunXValue');
    sunXSlider.addEventListener('input', e => {
      window.sunVector.x = parseFloat(e.target.value);
      sunXValue.value = window.sunVector.x;
      this.forceWorldUpdate();
    });
    sunXValue.addEventListener('input', e => {
      window.sunVector.x = parseFloat(e.target.value) || 0;
      sunXSlider.value = window.sunVector.x;
      this.forceWorldUpdate();
    });

    // Sun Y
    const sunYSlider = document.getElementById('sunY');
    const sunYValue = document.getElementById('sunYValue');
    sunYSlider.addEventListener('input', e => {
      window.sunVector.y = parseFloat(e.target.value);
      sunYValue.value = window.sunVector.y;
      this.forceWorldUpdate();
    });
    sunYValue.addEventListener('input', e => {
      window.sunVector.y = parseFloat(e.target.value) || 0;
      sunYSlider.value = window.sunVector.y;
      this.forceWorldUpdate();
    });

    // Sun Z
    const sunZSlider = document.getElementById('sunZ');
    const sunZValue = document.getElementById('sunZValue');
    sunZSlider.addEventListener('input', e => {
      window.sunVector.z = parseFloat(e.target.value);
      sunZValue.value = window.sunVector.z;
      this.forceWorldUpdate();
    });
    sunZValue.addEventListener('input', e => {
      window.sunVector.z = parseFloat(e.target.value) || 0;
      sunZSlider.value = window.sunVector.z;
      this.forceWorldUpdate();
    });

    // Sun Intensity
    const sunIntensitySlider = document.getElementById('sunIntensity');
    const sunIntensityValue = document.getElementById('sunIntensityValue');
    sunIntensitySlider.addEventListener('input', e => {
      window.sunIntensity = parseFloat(e.target.value);
      sunIntensityValue.value = window.sunIntensity;
      this.forceWorldUpdate();
    });
    sunIntensityValue.addEventListener('input', e => {
      window.sunIntensity = parseFloat(e.target.value) || 0;
      sunIntensitySlider.value = window.sunIntensity;
      this.forceWorldUpdate();
    });

    // AO Enabled
    document.getElementById('aoEnabled').addEventListener('change', e => {
      AmbientOcclusion.setEnabled(e.target.checked);
      if (e.target.checked && this.world) {
        // In dungeon mode, use precalculation with full level geometry
        if (this._dungeonMode && this.dungeonGenerator?.getCurrentLevel()) {
          const level = this.dungeonGenerator.getCurrentLevel();
          AmbientOcclusion.buildVoxelGridFromDefs(level.cuboidDefs, this.world);
          AmbientOcclusion.precalculateForDefs(level.cuboidDefs);
        } else {
          AmbientOcclusion.initialize(this.world);
        }
      } else if (!e.target.checked) {
        // When disabling AO, exit dungeon mode if active
        AmbientOcclusion.exitDungeonMode();
      }
      this.forceWorldUpdate();
    });

    // AO Intensity
    const aoIntensitySlider = document.getElementById('aoIntensity');
    const aoIntensityValue = document.getElementById('aoIntensityValue');
    aoIntensitySlider.addEventListener('input', e => {
      const value = parseFloat(e.target.value);
      AmbientOcclusion.setIntensity(value);
      aoIntensityValue.value = value;
      this.forceWorldUpdate();
    });
    aoIntensityValue.addEventListener('input', e => {
      const value = parseFloat(e.target.value) || 0;
      AmbientOcclusion.setIntensity(value);
      aoIntensitySlider.value = value;
      this.forceWorldUpdate();
    });

    // AO Radius
    const aoRadiusSlider = document.getElementById('aoRadius');
    const aoRadiusValue = document.getElementById('aoRadiusValue');
    aoRadiusSlider.addEventListener('input', e => {
      const value = parseFloat(e.target.value);
      AmbientOcclusion.setRadius(value);
      aoRadiusValue.value = value;
      this.forceWorldUpdate();
    });
    aoRadiusValue.addEventListener('input', e => {
      const value = parseFloat(e.target.value) || 0.5;
      AmbientOcclusion.setRadius(value);
      aoRadiusSlider.value = value;
      this.forceWorldUpdate();
    });

    // AO Samples
    const aoSamplesSlider = document.getElementById('aoSamples');
    const aoSamplesValue = document.getElementById('aoSamplesValue');
    aoSamplesSlider.addEventListener('input', e => {
      const value = parseInt(e.target.value);
      AmbientOcclusion.setNumRays(value);
      aoSamplesValue.value = value;
      this.forceWorldUpdate();
    });
    aoSamplesValue.addEventListener('input', e => {
      const value = parseInt(e.target.value) || 4;
      AmbientOcclusion.setNumRays(value);
      aoSamplesSlider.value = value;
      this.forceWorldUpdate();
    });

    // AO Resolution
    const aoResolutionSlider = document.getElementById('aoResolution');
    const aoResolutionValue = document.getElementById('aoResolutionValue');
    aoResolutionSlider.addEventListener('input', e => {
      const value = parseInt(e.target.value);
      AmbientOcclusion.setTexelsPerUnit(value);
      aoResolutionValue.value = value;
      this.forceWorldUpdate();
    });
    aoResolutionValue.addEventListener('input', e => {
      const value = parseInt(e.target.value) || 1;
      AmbientOcclusion.setTexelsPerUnit(value);
      aoResolutionSlider.value = value;
      this.forceWorldUpdate();
    });
  }

  /**
   * Initialize rendering strategy controls in the Display tab
   */
  initRenderingControls() {
    const displayContainer = this.settingsPanel.getTabContainer('display');

    const renderSection = document.createElement('div');
    renderSection.id = 'renderingControls';
    renderSection.className = 'gui-section';

    renderSection.innerHTML = `
      <div class="gui-section-header">
        <h3 class="gui-section-title">Rendering</h3>
        <svg class="gui-section-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </div>
      <div class="gui-section-body">
        <div class="gui-row">
          <label class="gui-checkbox-container">
            <input type="checkbox" id="useVirtualDOM" class="gui-checkbox" ${this.world.useVirtualDOM ? 'checked' : ''}>
            <span class="gui-checkbox-label">Virtual DOM (uncheck for innerHTML clear)</span>
          </label>
        </div>
        <div class="gui-row">
          <label class="gui-checkbox-container">
            <input type="checkbox" id="deferredSort" class="gui-checkbox" ${this.world.deferredSort ? 'checked' : ''}>
            <span class="gui-checkbox-label">Deferred sort (uncheck to sort every frame)</span>
          </label>
        </div>
      </div>
    `;

    displayContainer.appendChild(renderSection);

    renderSection.querySelector('.gui-section-header').addEventListener('click', () => {
      renderSection.classList.toggle('collapsed');
    });

    document.getElementById('useVirtualDOM').addEventListener('change', (e) => {
      this.world.useVirtualDOM = e.target.checked;
    });

    document.getElementById('deferredSort').addEventListener('change', (e) => {
      this.world.deferredSort = e.target.checked;
    });
  }

  /**
   * Force all cuboids to update by setting their needsUpdate flag
   */
  forceWorldUpdate() {
    // Mark all cuboids for update
    this.world.cuboids.forEach((cuboid) => {
      cuboid.needsUpdate = true;
    });
    // Resample impostor AO when AO settings change (or reset when AO disabled)
    this.world._impostorAODirty = true;
  }

  /**
   * Show the standard world.
   */
  async showStandardWorld() {
    await this.world.loadFromURL('worlds/demo.world.json');
    // Refresh the Material Editor cuboid list after world creation
    this.materialEditor.updateCuboidList();
  }

  /**
   * Initialize world controls panel (save/load/select worlds)
   * Renders into Settings > Game tab
   */
  initWorldControls() {
    const gameContainer = this.settingsPanel.getTabContainer('game');

    const worldSection = document.createElement('div');
    worldSection.id = 'worldControls';
    worldSection.className = 'gui-section';

    worldSection.innerHTML = `
      <div class="gui-section-header">
        <h3 class="gui-section-title">World Manager</h3>
        <svg class="gui-section-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </div>
      <div class="gui-section-body">
        <div class="gui-row gui-row-stacked">
          <label class="gui-label gui-label-stacked">Load World</label>
          <select id="worldSelect" class="gui-select">
            <option value="demo">Demo World</option>
            <option value="flat">Flat Terrain</option>
            <option value="city">City</option>
            <option value="terrain">Terrain</option>
          </select>
        </div>

        <div class="gui-button-group">
          <button id="loadWorldBtn" class="gui-button gui-button-primary">Load</button>
          <button id="loadFileBtn" class="gui-button gui-button-success">Import</button>
        </div>

        <hr class="gui-divider">

        <button id="saveWorldBtn" class="gui-button gui-button-full" style="background: #4a1d6a; border-color: #6b2d9a;">Export World</button>

        <hr class="gui-divider">

        <button id="respawnBtn" class="gui-button gui-button-full" style="background: #1a6b4a; border-color: #2d9a6b;">Respawn Player</button>

        <div id="worldInfo" class="gui-info" style="margin-top: 8px;"></div>
      </div>
    `;

    gameContainer.appendChild(worldSection);

    // Add collapse functionality
    const header = worldSection.querySelector('.gui-section-header');
    header.addEventListener('click', () => {
      worldSection.classList.toggle('collapsed');
    });

    // Event listeners
    document.getElementById('loadWorldBtn').addEventListener('click', () => this.loadSelectedWorld());
    document.getElementById('loadFileBtn').addEventListener('click', () => this.importWorldFromFile());
    document.getElementById('saveWorldBtn').addEventListener('click', () => this.exportWorld());
    document.getElementById('respawnBtn').addEventListener('click', () => this.handleRespawnButton());
  }

  /**
   * Initialize multiplayer networking and UI
   */
  initMultiplayer() {
    this.networkManager = new NetworkManager();
    this.roomService = new RoomService();
    this.worldSync = null;
    this.playerSync = null;
    this.npcSync = null;
    this.dungeonInstanceManager = new DungeonInstanceManager(this);

    const gameContainer = this.settingsPanel.getTabContainer('game');
    this.multiplayerPanel = new MultiplayerPanel(gameContainer, {
      onJoin: async (roomId, options) => {
        const overlay = new JoinOverlay();
        overlay.show(() => this._rejectJoin?.('Cancelled'));
        const log = (msg) => overlay.log(msg);

        try {
          // Pre-compute password hash (for Firebase verification) and Trystero room ID
          let trysteroRoomId = roomId;
          if (options.password) {
            options.passwordHash = await hashPassword(options.password);
            // Pure hex room ID — no special chars that MQTT brokers might reject
            trysteroRoomId = await derivePasswordRoomId(roomId, options.password);
          }
          await this._joinRoom(roomId, trysteroRoomId, options, (status) => overlay.setStatus(status), log);
          // Brief delay to show final status before dismissing
          await new Promise(r => setTimeout(r, 500));
          overlay.hide();
        } catch (err) {
          if (err.message === 'Cancelled') {
            overlay.hide();
          } else {
            overlay.showError('Failed to connect: ' + err.message);
          }
          this._leaveRoom();
          throw err;
        }
      },
      onLeave: () => this._leaveRoom(),
      listRooms: () => this.roomService.listRooms(),
      roomInfo: (roomId) => this.roomService.roomExists(roomId),
      onNicknameChange: (nick) => {
        if (this.playerSync) {
          const displayNick = this.playerSync.setLocalNickname(nick);
          this.playerChat?.setLocalPlayerName(displayNick || nick);
        }
      }
    });

    // Auto-join from URL param (?room=XYZ)
    const urlParams = new URLSearchParams(window.location.search);
    const roomFromUrl = urlParams.get('room');
    if (roomFromUrl) {
      // Delay slightly to ensure player character is loaded
      setTimeout(() => this.multiplayerPanel.autoJoin(roomFromUrl), 1000);
    }

    // Clean up on tab close / navigation — fires before the page unloads.
    // Uses sendBeacon for the room listing update (reliable during unload)
    // and synchronous WebRTC teardown so peers detect the leave immediately.
    window.addEventListener('beforeunload', () => {
      if (this.networkManager?.isConnected) {
        const remainingPeers = this.networkManager.peerCount;

        // Update room listing via sendBeacon (survives page unload).
        // Must use Blob with JSON content type — sendBeacon defaults to text/plain.
        if (remainingPeers > 0 && this._displayRoomId) {
          const body = new Blob(
            [JSON.stringify({ action: 'updatePlayers', roomId: this._displayRoomId, count: remainingPeers })],
            { type: 'application/json' }
          );
          navigator.sendBeacon('/api/rooms', body);
        }

        // Synchronous teardown — closes WebRTC connections so peers
        // get onPeerLeave immediately instead of waiting for ICE timeout.
        this.networkManager.leave();
      }
    });
  }

  async _joinRoom(displayRoomId, trysteroRoomId, options = {}, onStatusChange = null, onLog = null) {
    this._displayRoomId = displayRoomId;
    const roomId = displayRoomId; // Alias for minimal diff in peer lifecycle handlers
    // Set up all sync managers and callbacks BEFORE connecting to the room.
    // Trystero's joinRoom() can fire onPeerJoin within milliseconds on fast
    // connections — any handler not registered by then would miss the event.

    // Wire diagnostic logging into NetworkManager for chunk-level visibility
    this.networkManager._onDiagnostic = onLog || null;

    // World sync
    this.worldSync = new WorldSync(this.world, this.networkManager);

    // Player sync
    if (this.player) {
      this.playerSync = new PlayerSync(this.world, this.networkManager, this.player);
      // Wire remote projectile events to ProjectileManager
      this.playerSync.onRemoteFireball = (data) => {
        if (!this.projectileManager) return;
        if (data.projectileType && data.projectileType !== 'fireball') {
          this.projectileManager.spawnRemoteProjectile(data);
        } else {
          this.projectileManager.spawnRemoteFireball(data);
        }
      };
      // Wire remote boomerang throw to detached weapon anim
      this.playerSync.onRemoteBoomerang = (character, angle) => {
        // Override facing angle to the broadcast throw angle, then spawn
        const savedAngle = character.facingAngle;
        character.facingAngle = angle;
        this._spawnDetachedWeaponAnim(character);
        character.facingAngle = savedAngle;
      };
      // Wire instance manager for cross-instance filtering (persists across dungeon transitions)
      this.playerSync._dungeonInstanceManager = this.dungeonInstanceManager;
      this.mapRenderer.playerSync = this.playerSync;
      // Initialize equipment data from current inventory for LPC broadcasting
      if (this.inventory) {
        const equippedItemDefs = {};
        for (const [slot, entry] of Object.entries(this.inventory.equipped)) {
          if (entry) equippedItemDefs[slot] = entry.def || ITEM_DEFS[entry.itemId];
        }
        this.playerSync._localEquipment = equippedItemDefs;
      }
    }

    // NPC sync — authority runs AI, broadcasts positions at 4Hz,
    // followers interpolate with velocity-predicted lerp
    if (this.npcManager) {
      this.npcSync = new NPCSync(this.npcManager, this.networkManager);
      this.npcSync.setAuthority(true); // Initially we're the only peer
      // Wire instance manager for cross-instance filtering
      this.npcSync._dungeonInstanceManager = this.dungeonInstanceManager;
    }

    // Player nickname — set on PlayerSync for broadcast + dedup
    const nickname = this.multiplayerPanel.getNickname();
    if (this.playerSync) {
      this.playerSync.setLocalNickname(nickname);
      let dedupMessageShown = false;
      this.playerSync.onDisplayNickChange = (displayNick) => {
        this.playerChat?.setLocalPlayerName(displayNick);
        // Update the "Playing as:" display in the multiplayer panel
        this.multiplayerPanel?.setDisplayNickname(displayNick);
        // Update character window header with new nickname
        this._updateCharacterWindow();
        // Notify via system message if this was a dedup rename (has #N suffix) — once only
        if (displayNick && displayNick.includes('#') && !dedupMessageShown) {
          dedupMessageShown = true;
          this.playerChat?.addSystemMessage(`Name taken — playing as ${displayNick}`);
        }
      };
    }

    // Player chat — multiplayer text chat via 'chat' data channel
    this.playerChat = new PlayerChat();
    this.playerChat.setLocalPlayerName(nickname);
    this.playerChat.setNetworkManager(this.networkManager);
    this.networkManager.onChat = (data, peerId) => {
      this.playerChat.addRemoteMessage(data, peerId);
      // Show in-world chat bubble on remote player's sprite
      if (data?.msg && this.playerSync) {
        this.playerSync.showChatBubble(peerId, ProfanityFilter.filter(data.msg.slice(0, 60)));
      }
    };
    this.playerChat.onLocalSend = (text) => {
      // Show in-world chat bubble on local player's sprite
      this.playerSync?.showChatBubble(null, text.slice(0, 60));
    };
    this.playerChat.show();

    // Wire action channel to dispatch dungeon-*, npc-*, loot-*, and player actions.
    // This is the SINGLE unified action handler — never overwritten by dungeon transitions.
    {
      const playerOnAction = this.networkManager.onAction;
      this.networkManager.onAction = (data, peerId) => {
        if (data.type?.startsWith('dungeon-')) {
          this.dungeonInstanceManager?.handleAction(data, peerId);
        } else if (data.type?.startsWith('npc-')) {
          // Skip NPC actions from peers in a different instance
          const dim = this.dungeonInstanceManager;
          if (dim) {
            const myInst = dim._isInDungeon ? dim._instanceId : null;
            const theirInst = dim._peerInstances.get(peerId) || null;
            if (myInst !== theirInst) return;
          }
          this.npcSync?.handleAction(data, peerId);
        } else if (data.type?.startsWith('loot-')) {
          // Skip loot actions from peers in a different instance
          const dim = this.dungeonInstanceManager;
          if (dim) {
            const myInst = dim._isInDungeon ? dim._instanceId : null;
            const theirInst = dim._peerInstances.get(peerId) || null;
            if (myInst !== theirInst) return;
          }
          this.lootManager?.handleNetworkAction(data);
          // Track cross-level pickups for dungeon persistence
          if (data.type === 'loot-pickup' && this.dungeonGenerator) {
            this.dungeonGenerator.onLootPickedUp(data.netId);
          }
        } else {
          playerOnAction?.(data, peerId);
        }
      };
    }

    // Wire dialog system to broadcast NPC actions to all peers
    if (this.dialogManager) {
      this.dialogManager.onNPCAction = (data) => {
        if (this.networkManager?.isConnected) {
          this.networkManager.broadcastAction(data);
        }
      };
    }

    // Track whether we've received world state from another peer.
    // Wrap the callback that WorldSync already registered.
    this._hasReceivedWorldState = false;
    // Flag: true while we're a newcomer to the room. Prevents proactively sending
    // our (potentially empty) world state to established peers — we should receive, not send.
    this._justJoined = true;

    // Rejoin from dungeon: pre-restore saved world state immediately.
    // This ensures we have valid world data and can serve it to other returning peers.
    // All peers saved the same world state before entering the dungeon, so the
    // pre-restored state is authoritative. We set _hasReceivedWorldState to prevent
    // a second fromJSON() from overwriting it — the extra toJSON→fromJSON round-trip
    // can lose material group face assignments.
    if (options.isRejoin && options.savedWorldState) {
      onLog?.('Pre-restoring saved world state for rejoin');
      this.world.fromJSON(options.savedWorldState);
      this.materialEditor?.updateCuboidList();
      this.world.forceRender();
      this._justJoined = false; // We have valid state — serve it to other returners
      this._hasReceivedWorldState = true; // Prevent overwrite by peer's world state
      if (this.worldSync) this.worldSync._isReady = true;
    }
    const worldSyncOnWorldState = this.networkManager.onWorldState;
    this.networkManager.onWorldState = (json) => {
      // Deduplicate: only process the FIRST world state received.
      // Multiple peers may respond to our request, and we also get a proactive
      // send from the authority's onPeerJoin. Processing duplicates causes
      // repeated fromJSON() calls that reset the DOM, kill music, and lag.
      if (this._hasReceivedWorldState) return;
      this._hasReceivedWorldState = true;
      this._justJoined = false;
      // Mark WorldSync as ready to serve world state to future newcomers
      if (this.worldSync) this.worldSync._isReady = true;
      onLog?.(`World state received: ${json.cuboids?.length || 0} cuboids, ${json.npcs?.length || 0} NPCs`);
      onStatusChange?.('Loading world...');
      worldSyncOnWorldState?.(json);
      this._resolveJoin?.();
    };

    // Peer lifecycle
    this.networkManager.onPeerJoin = (peerId) => {
      const count = this.networkManager.peerCount;
      onLog?.(`Peer joined (${peerId.slice(0,6)}...), ${count} peer(s)`);
      this.multiplayerPanel.setPeerCount(count);
      this.roomService.updatePlayerCount(roomId, count + 1); // +1 for self
      this.playerChat?.addSystemMessage('A player joined');

      // New peers start in main world — track in DIM
      this.dungeonInstanceManager?._peerInstances.delete(peerId);

      // Re-evaluate authority — instance-aware
      if (this.npcSync) {
        if (this.dungeonInstanceManager?._isInDungeon) {
          this.dungeonInstanceManager._updateLevelAuthority();
        } else {
          this.dungeonInstanceManager?._updateMainWorldAuthority() ??
            this.npcSync.setAuthority(this.networkManager.isOldestPeer());
        }
      }

      // Broadcast our position + hello handshake with staggered retries so the
      // new peer sees us even if data channels take a moment to open fully.
      if (this.playerSync) {
        this.playerSync.notifyPeerJoined();
      }
      if (!this._hasReceivedWorldState) {
        onStatusChange?.('Peer connected, waiting for world...');
      }

      // If we're in a dungeon, broadcast our instance to the new peer
      if (this.dungeonInstanceManager?._isInDungeon) {
        this.dungeonInstanceManager._broadcastInstanceChange();
      }

      // Send world state to newly joined peer — but only if we're NOT a newcomer
      // ourselves AND we're in the main world (dungeon world is locally generated).
      // Serialize ONCE and reuse for all retries — toJSON() + JSON.stringify()
      // blocks the main thread for 50-200ms on mobile per call.
      if (!this._justJoined && this.worldSync && !this.dungeonInstanceManager?._isInDungeon) {
        const json = JSON.stringify(this.world.toJSON());
        for (const delay of [0, 2000, 5000, 8000]) {
          setTimeout(() => {
            if (!this.networkManager.isConnected) return;
            if (!this.networkManager.peers.has(peerId)) return; // peer left
            this.networkManager.sendWorldState(peerId, json);
          }, delay);
        }
      }
    };

    this.networkManager.onPeerLeave = (peerId) => {
      const count = this.networkManager.peerCount;
      onLog?.(`Peer left (${peerId.slice(0,6)}...), ${count} peer(s)`);
      this.multiplayerPanel.setPeerCount(count);
      this.roomService.updatePlayerCount(roomId, count + 1);

      // Chat notification — get nickname (or characterName) before removePeer clears it
      const leavingRemote = this.playerSync?.remotePlayers?.get(peerId);
      const leaveLabel = leavingRemote?.nickname
        || (leavingRemote?.targetState?.characterName
          ? leavingRemote.targetState.characterName.charAt(0).toUpperCase() + leavingRemote.targetState.characterName.slice(1)
          : 'A player');
      this.playerChat?.addSystemMessage(`${leaveLabel} left the room`);

      this.playerSync?.removePeer(peerId);

      // Clean up DIM tracking
      this.dungeonInstanceManager?._peerInstances.delete(peerId);
      this.dungeonInstanceManager?._peerLevels.delete(peerId);

      // Re-evaluate authority — instance-aware
      if (this.npcSync) {
        if (this.dungeonInstanceManager?._isInDungeon) {
          this.dungeonInstanceManager._updateLevelAuthority();
        } else {
          const wasAuthority = this.npcSync._isAuthority;
          if (this.dungeonInstanceManager) {
            this.dungeonInstanceManager._updateMainWorldAuthority();
          } else {
            this.npcSync.setAuthority(this.networkManager.isOldestPeer());
          }
          // If we just became authority (old authority left), restore NPC behavior states
          if (!wasAuthority && this.npcSync._isAuthority) {
            this.npcSync.restoreBehaviorStates();
            onLog?.('Authority handoff — this peer is now NPC authority');
            this.npcSync.syncNPCFlags();
            if (this.dungeonGenerator?.streamer) {
              this.dungeonGenerator.streamer.networkControlNPCs = false;
            }
          }
        }
      }
      // If we're now alone and still waiting for world state, establish ourselves.
      if (!this._hasReceivedWorldState && this.networkManager.peerCount === 0) {
        this._establishAsSource(onStatusChange, 'All peers left, starting fresh...');
        onLog?.('All peers left — establishing as source');
      }
    };

    // Check if the room already exists in the database BEFORE connecting.
    // If it doesn't exist, we're creating a new room — no need to wait 12s for peers.
    // If it does exist, we need the full wait for slow Android WebRTC signaling.
    // isRejoin: skip DB check, use fast alone timer (returning from dungeon)
    let roomExistsInDB = true; // Safe default: assume peers might exist
    let roomHasPassword = false; // Whether the EXISTING room in Firebase has a password
    if (options.isRejoin) {
      roomExistsInDB = true; // Assume room exists (we were just in it)
      onLog?.('Rejoining room — fast connect');
    } else {
      try {
        onStatusChange?.('Checking room...');
        const existsResult = await this.roomService.roomExists(displayRoomId);
        roomExistsInDB = existsResult.exists;
        roomHasPassword = existsResult.hasPassword || false;
        onLog?.(roomExistsInDB ? 'Room exists in DB — waiting for peers' : 'New room — fast start');
      } catch {
        onLog?.('DB check failed — assuming room exists');
      }
    }

    // Verify password BEFORE connecting to Trystero — instant reject on wrong password.
    // Only verify when the EXISTING room in Firebase has a password stored.
    if (roomExistsInDB && roomHasPassword && options.passwordHash) {
      onStatusChange?.('Verifying password...');
      const valid = await this.roomService.verifyPassword(displayRoomId, options.passwordHash);
      if (!valid) {
        onLog?.('Wrong password — rejected');
        throw new Error('Wrong password');
      }
      onLog?.('Password verified');
    }

    // NOW connect to the P2P room — all handlers are in place
    onStatusChange?.('Connecting to room...');
    onLog?.('Joining P2P room: ' + displayRoomId + (options.hasPassword ? ' (password-protected)' : ''));
    this.networkManager.joinRoom(trysteroRoomId);

    // Register in public room listing (non-blocking — doesn't affect P2P).
    // Only create if this is a new room — don't overwrite existing room data
    // (a wrong-password joiner would clobber the real room's hasPassword/playerCount).
    if (roomExistsInDB) {
      this.roomService.joinExistingRoom(displayRoomId);
    } else {
      this.roomService.createRoom(displayRoomId, options);
    }

    // Return a Promise that resolves when world state is established.
    // Replaces the old fire-and-forget timer approach that caused the Android bug:
    // the 5s "alone" timer would fire before slow Android WebRTC connected,
    // setting _hasReceivedWorldState=true and permanently blocking the real world state.
    // New room (not in DB): 2s alone check — we know nobody can be in there.
    // Existing room (in DB): 20s alone check — some Android tablets (Samsung) need 15s+
    // for MQTT broker discovery + WebRTC ICE negotiation over mobile networks.
    // Rejoin: 3s alone check — peers should still be there, but allow for brief reconnect gap
    // Password rooms that exist: 8s fallback (if server-side verify was skipped due to old API)
    const ALONE_CHECK_MS = options.isRejoin ? 3000
      : (roomExistsInDB && options.hasPassword ? 8000
        : (roomExistsInDB ? 20000 : 2000));
    const HARD_TIMEOUT_MS = 30000; // Absolute deadline — resolve with local world
    const joinStartTime = Date.now();

    return new Promise((resolve, reject) => {
      this._resolveJoin = () => {
        this._resolveJoin = null;
        this._rejectJoin = null;
        this._clearJoinTimers();
        resolve();
      };
      this._rejectJoin = (reason) => {
        this._resolveJoin = null;
        this._rejectJoin = null;
        this._clearJoinTimers();
        reject(new Error(reason));
      };

      // Rejoin with pre-restored state: resolve immediately — world is already loaded
      if (options.isRejoin && this._hasReceivedWorldState) {
        this._resolveJoin();
        return;
      }

      // Hard timeout: 30s total — establish with whatever we have
      this._joinTimerIds = [];
      this._joinTimeoutId = setTimeout(() => {
        if (this._hasReceivedWorldState) return;
        onLog?.('Hard timeout (30s) — establishing with local world');
        this._establishAsSource(onStatusChange, 'Connection timed out, starting fresh...');
      }, HARD_TIMEOUT_MS);

      // Request world state with retries + integrated alone-detection.
      // Instead of a blind 5s timer, check peerCount at each attempt.
      // Schedule: 500ms, 1.5s, 3.5s, 6.5s, 10.5s, 15.5s, 21.5s, 28.5s — 8 attempts
      // Covers the full 30s window before the hard timeout.
      const requestWithRetry = (attempt) => {
        if (attempt > 7 || !this.networkManager.isConnected) return;
        if (this._hasReceivedWorldState) return;

        const delay = 500 + attempt * 1000;
        const timerId = setTimeout(() => {
          if (this._hasReceivedWorldState) return;

          // Alone check: if enough time has passed and still no peers, we're first.
          // This replaces the old 5s blind timer that raced against slow Android WebRTC.
          // For rejoin: also establish if peers are present but none responded —
          // when all peers return from dungeon simultaneously, none has _isReady=true
          // initially, so sync requests go unanswered. Pre-restored state is valid.
          const elapsed = Date.now() - joinStartTime;
          const isAlone = this.networkManager.peerCount === 0;
          if (elapsed >= ALONE_CHECK_MS && (isAlone || options.isRejoin)) {
            // Password room that exists but no peers: reject (wrong password fallback)
            if (options.hasPassword && roomExistsInDB && isAlone) {
              onLog?.('No peers found — wrong password or room is empty');
              this.networkManager.leave();
              this.roomService.leaveRoom(0);
              this._displayRoomId = null;
              this._rejectJoin?.('Wrong password or room is empty');
              return;
            }
            onLog?.(`${isAlone ? 'Alone' : 'No response'} after ${(elapsed/1000).toFixed(1)}s — establishing`);
            // Rejoin: world was already pre-restored above, no need to fromJSON again
            if (!options.isRejoin && options.savedWorldState) {
              onLog?.('Restoring saved world state');
              this.world.fromJSON(options.savedWorldState);
              this.materialEditor?.updateCuboidList();
              this.world.forceRender();
            }
            this._establishAsSource(onStatusChange,
              options.isRejoin ? 'Room empty, restored world...' : 'No other peers, starting fresh...');
            return;
          }

          // Request world state from any connected peer
          if (this.networkManager.peerCount > 0) {
            onLog?.(`Requesting world state (attempt ${attempt + 1})`);
            this.networkManager.requestWorldState();
          } else {
            onLog?.(`Retry ${attempt + 1}: no peers yet`);
          }
          requestWithRetry(attempt + 1);
        }, delay);
        this._joinTimerIds.push(timerId);
      };
      requestWithRetry(0);
    });
  }

  /**
   * Establish this peer as the world state source (first peer or all peers left).
   * Sets the dedup flags, resolves the join Promise, and updates status.
   * @param {function|null} onStatusChange
   * @param {string} statusMessage
   */
  _establishAsSource(onStatusChange, statusMessage) {
    if (this._hasReceivedWorldState) return;
    this._justJoined = false;
    this._hasReceivedWorldState = true;
    if (this.worldSync) this.worldSync._isReady = true;
    onStatusChange?.(statusMessage);
    this._resolveJoin?.();
  }

  /**
   * Clear all pending join timers (retry attempts + hard timeout).
   */
  _clearJoinTimers() {
    if (this._joinTimeoutId) {
      clearTimeout(this._joinTimeoutId);
      this._joinTimeoutId = null;
    }
    if (this._joinTimerIds) {
      for (const id of this._joinTimerIds) clearTimeout(id);
      this._joinTimerIds = null;
    }
  }

  _leaveRoom() {
    // Cancel any in-progress join
    this._rejectJoin?.('Left room');
    this._clearJoinTimers();

    const remainingPeers = this.networkManager.peerCount;

    // Clean up player sync
    if (this.playerSync) {
      this.playerSync.destroy();
      this.playerSync = null;
    }

    // Clean up NPC sync
    if (this.npcSync) {
      this.npcSync.destroy();
      this.npcSync = null;
    }

    // Clean up world sync
    if (this.worldSync) {
      this.worldSync.destroy();
      this.worldSync = null;
    }

    // Clean up player chat
    if (this.playerChat) {
      this.playerChat.destroy();
      this.playerChat = null;
    }

    // Disconnect from room
    this.networkManager.leave();

    // Update room listing
    this.roomService.leaveRoom(remainingPeers);
    this._displayRoomId = null;
  }

  /**
   * Handle the respawn button click
   * Repositions player on a valid cuboid, creating one if necessary
   */
  handleRespawnButton() {
    const infoDiv = document.getElementById('worldInfo');

    if (!this.player) {
      infoDiv.textContent = 'Error: No player character';
      return;
    }

    // Check if world has any cuboids
    if (this.world.cuboids.size === 0) {
      // Create default spawn platform
      this.createDefaultSpawnPlatform();
      this.player.x = 1.5;
      this.player.y = 1;
      this.player.z = 1.5;
      this.player.isGrounded = true;
      this.player.velocity.y = 0;
      this.orbitalCamera.setTarget(this.player.x, this.player.y, this.player.z);
      this.orbitalCamera.snapToTarget();

      // Force immediate update so player and cuboid are visible
      this.camera.update(0);
      this.world.update(0);

      infoDiv.textContent = 'Created spawn platform and respawned';
      return;
    }

    // Find a valid spawn point
    const spawnPoint = this.findValidSpawnPoint();
    if (spawnPoint) {
      this.player.x = spawnPoint.x;
      this.player.y = spawnPoint.y;
      this.player.z = spawnPoint.z;
      this.player.isGrounded = true;
      this.player.velocity.y = 0;
      this.orbitalCamera.setTarget(this.player.x, this.player.y, this.player.z);
      this.orbitalCamera.snapToTarget();

      // Force immediate update so player is visible at new position
      this.camera.update(0);
      this.world.update(0);

      infoDiv.textContent = `Respawned at (${spawnPoint.x.toFixed(1)}, ${spawnPoint.y.toFixed(1)}, ${spawnPoint.z.toFixed(1)})`;
    } else {
      infoDiv.textContent = 'Error: No valid spawn point found';
    }
  }

  /**
   * Load the selected world from the dropdown
   */
  async loadSelectedWorld() {
    // Leave multiplayer room — loading a new world would destroy other players' work
    if (this.networkManager?.isConnected) {
      this.multiplayerPanel.leave();
    }
    this._savedWorldState = null;
    this._savedPlayerPos = null;
    const select = document.getElementById('worldSelect');
    const worldName = select.value;
    const infoDiv = document.getElementById('worldInfo');

    try {
      infoDiv.textContent = 'Loading...';

      if (worldName === 'demo') {
        // Load the built-in demo world
        this.showStandardWorld();
        infoDiv.textContent = 'Loaded: Demo World';
      } else {
        // Load from worlds folder
        const metadata = await this.world.loadFromURL(`worlds/${worldName}.world.json`);
        this.materialEditor.updateCuboidList();
        infoDiv.textContent = `Loaded: ${metadata.name || worldName}`;
      }
    } catch (error) {
      Debug.error('World', 'Failed to load world:', error);
      infoDiv.textContent = `Error: ${error.message}`;
    }
  }

  /**
   * Import world from a local file
   */
  async importWorldFromFile() {
    // Leave multiplayer room — loading a new world would destroy other players' work
    if (this.networkManager?.isConnected) {
      this.multiplayerPanel.leave();
    }
    this._savedWorldState = null;
    this._savedPlayerPos = null;
    const infoDiv = document.getElementById('worldInfo');

    try {
      infoDiv.textContent = 'Selecting file...';
      const metadata = await this.world.loadFromFile();
      this.materialEditor.updateCuboidList();
      infoDiv.textContent = `Imported: ${metadata.name || 'Untitled'}`;
    } catch (error) {
      Debug.error('World', 'Failed to import world:', error);
      infoDiv.textContent = `Error: ${error.message}`;
    }
  }

  /**
   * Export current world to a JSON file
   */
  exportWorld() {
    const infoDiv = document.getElementById('worldInfo');
    const filename = prompt('Enter filename:', 'my-world');

    if (filename) {
      const name = prompt('Enter world name:', filename);
      const author = prompt('Enter author name (optional):', '');
      const description = prompt('Enter description (optional):', '');

      this.world.saveToFile(filename, { name, author, description });
      infoDiv.textContent = `Exported: ${filename}.world.json`;
    }
  }

  /**
   * Start the game loop.
   */
  startGameLoop() {
    this.lastTime = performance.now();
    this.lastFpsUpdate = this.lastTime;
    this._paused = false;
    this._settleFrames = 0;

    this._loop = currentTime => {
      if (this._paused) return;

      this.deltaTime = (currentTime - this.lastTime) / 1000;
      this.lastTime = currentTime;
      // Cap deltaTime to prevent physics overshoot, knockback teleportation,
      // and velocity integration errors during lag spikes or slow mobile frames.
      // 50ms (~20fps minimum) is generous enough for smooth gameplay while
      // preventing 6-10x overshoot that 100ms allows.
      const maxDelta = 0.05;
      if (this.deltaTime > maxDelta) {
        this.deltaTime = maxDelta;
      }

      // Update FPS counter.
      this.frames++;
      if (currentTime - this.lastFpsUpdate >= 1000) {
        this.fps = Math.round((this.frames * 1000) / (currentTime - this.lastFpsUpdate));
        this.lastFpsUpdate = currentTime;
        this.frames = 0;
        this.fpsDisplay.textContent = `FPS: ${this.fps}`;
      }

      // During settle frames after tab resume, skip character updates to avoid
      // triggering expensive SAT sort while the browser re-composites CSS 3D layers
      const settling = this._settleFrames > 0;
      if (settling) this._settleFrames--;

      // Update input, camera, and world.
      this.inputManager.update(this.deltaTime);

      // Update orbital camera target if player exists
      if (this.player && this.orbitalCamera.isEnabled()) {
        this.orbitalCamera.setTargetCharacter(this.player);
        // Death cam: slow quadratic zoom into the fallen player
        if (this._deathCam && this._deathCam.elapsed < this._deathCam.duration) {
          this._deathCam.elapsed += this.deltaTime;
          const t = Math.min(this._deathCam.elapsed / this._deathCam.duration, 1);
          // Quadratic ease-out: fast start, gentle settle
          const eased = t * (2 - t);
          const zoom = this._deathCam.startZoom + (this._deathCam.endZoom - this._deathCam.startZoom) * eased;
          this.orbitalCamera.targetZoomLevel = zoom;
          this.orbitalCamera.currentZoomLevel = zoom;
          this.camera.zoomLevel = zoom;
        }
        this.orbitalCamera.update(this.deltaTime);
      }

      this.camera.update(this.deltaTime);

      // Update cloud backdrop
      if (this.cloudBackdrop) {
        this.cloudBackdrop.update(this.deltaTime);
      }

      // Update combat system BEFORE world render so knockback positions
      // are applied before sprites render (prevents one-frame position lag)
      if (!settling && this.combatManager) {
        this.combatManager.update(this.deltaTime);
      }

      // Regenerate health/mana/stamina
      if (!settling) this._updateRegeneration(this.deltaTime);

      // Update projectiles BEFORE world render so collision/movement
      // is resolved before sprite positioning
      if (!settling && this.projectileManager) {
        this.projectileManager.update(this.deltaTime, this.camera);
      }

      // Update detached weapon animations (boomerang throw etc.)
      this._updateDetachedAnims(this.deltaTime, this.camera);

      // Interpolate remote players BEFORE world render so their sprite
      // positions are current for the SAT depth sort. Broadcasting local
      // state uses the previous frame's position (fine at 10Hz).
      if (!settling && this.playerSync) {
        this.playerSync.update(this.deltaTime);
      }

      // NPC drift correction BEFORE world render so corrected positions
      // are used in the SAT sort.
      if (!settling && this.npcSync) {
        this.npcSync.update(this.deltaTime);
      }

      this.world.update(settling ? 0 : this.deltaTime);

      // Update loot system (hover animations, auto-pickup)
      if (!settling && this.lootManager && this.player) {
        this.lootManager.update(this.deltaTime, this.player);
      }

      // Update HUD (health bar, damage numbers)
      if (!settling && this.hudManager) {
        this.hudManager.update(this.deltaTime);
      }

      // Update game windows
      if (!settling) {
        // Map updates every frame for smooth scrolling/rotation
        this._updateMapWindow(this.deltaTime);
        this._charUpdateAccum = (this._charUpdateAccum || 0) + this.deltaTime;
        if (this._charUpdateAccum >= 0.5) {
          this._charUpdateAccum = 0;
          this._updateCharacterWindow();
        }
      }

      // Update dungeon system (streamer, portal detection, minimap, chests, fall detection)
      if (!settling && this._dungeonMode && this.dungeonGenerator && this.player) {
        // In multiplayer dungeon, pass remote player positions for multi-position chunk loading
        if (this.dungeonInstanceManager?._isInDungeon && this.playerSync) {
          this.dungeonGenerator.updateMultiplayer(this.deltaTime, this.player, this.playerSync.remotePlayers, this.dungeonInstanceManager._myLevel);
        } else {
          this.dungeonGenerator.update(this.deltaTime, this.player);
        }

        // Check EXIT portal (forward to next level)
        if (!this._levelTransitioning && this.dungeonGenerator.checkExitPortal(this.player)) {
          this._levelTransitioning = true;
          this.questManager.onExitReached();
          // Clear any combat visual state (i-frame flicker, hit flash)
          if (this.player.combatStats) this.player.combatStats.reset();
          if (this.player.sprite?._visual) {
            this.player.sprite._visual.style.opacity = '';
            this.player.sprite._visual.style.filter = '';
          }
          const completedIdx = this.dungeonGenerator.currentLevelIndex;
          const isFinalLevel = completedIdx + 1 >= this.dungeonGenerator.levels.length;
          const alreadyCompleted = this.dungeonGenerator._completedLevels.has(completedIdx);
          this.dungeonGenerator._completedLevels.add(completedIdx);

          if (isFinalLevel) {
            // Skip per-level banner — _onDungeonComplete will show "DUNGEON COMPLETE!"
            this.dungeonGenerator.loadNextLevel();
            this._levelTransitioning = false;
          } else if (alreadyCompleted) {
            // Already completed this level before (backtracked and re-exited) — skip banner
            this.dungeonGenerator.loadNextLevel();
            this._levelTransitioning = false;
          } else {
            this.hudManager.showLevelComplete(`Level ${completedIdx + 1} Complete!`);
            setTimeout(() => {
              this.dungeonGenerator.loadNextLevel();
              this._levelTransitioning = false;
            }, 2000);
          }
        }

        // Check BACK portal (backward to previous level, level 2+ only)
        if (!this._levelTransitioning && this.dungeonGenerator.checkBackPortal(this.player)) {
          this._levelTransitioning = true;
          if (this.player.combatStats) this.player.combatStats.reset();
          if (this.player.sprite?._visual) {
            this.player.sprite._visual.style.opacity = '';
            this.player.sprite._visual.style.filter = '';
          }
          this.dungeonGenerator.loadLevel(this.dungeonGenerator.currentLevelIndex - 1, { spawnAt: 'exit' });
          this._levelTransitioning = false;
        }

        // Check chest proximity for prompt
        this._updateChestPrompt();

        // Check fall off map
        if (!this._deathScreenActive && this.dungeonGenerator.checkFallOffMap(this.player)) {
          this._triggerPlayerDeath();
        }

        // Minimap now handled by Map GameWindow (_updateMapWindow)
      }

      // Check void fall in non-dungeon mode — only once player is below the
      // lowest surface in the world (can't land on anything), then wait 1s.
      // Special case: if world is empty (_worldLowestY === -Infinity), always trigger.
      if (!settling && !this._dungeonMode && !this._deathScreenActive && this.player
          && !this.player.isGrounded && this.player.velocity.y < 0
          && this._worldLowestY !== undefined
          && (this._worldLowestY === -Infinity || this.player.y < this._worldLowestY)) {
        this._voidFallTimer = (this._voidFallTimer || 0) + this.deltaTime;
        if (this._voidFallTimer >= 1.0) {
          this._voidFallTimer = 0;
          this._triggerVoidRespawn();
        }
      } else {
        this._voidFallTimer = 0;
      }

      // Update dialog system (proximity checks, interaction prompt)
      if (!settling && this.dialogManager) {
        this.dialogManager.update(this.deltaTime, this.camera);
      }

      // Update audio system (listener position, footsteps, spatial SFX)
      if (!settling && this.audioSystem) {
        this.audioSystem.update(this.deltaTime, this);
      }

      // Update character debug visualization
      if (this.characterDebug && this.player) {
        this.characterDebug.update();
        this.characterDebug.updateDebugInfo(this.player);
        // Pass sprite offset to sprite
        if (this.player.sprite) {
          this.player.sprite.spriteOffset = this.characterDebug.getSpriteOffset();
        }
      }

      // Update click-to-move destination marker position
      if (this.clickToMoveHandler) {
        this.clickToMoveHandler.updateMarker();
      }

      // Process queued AO updates within frame budget
      if (AmbientOcclusion.enabled) {
        // Update player position for distance-based AO priority
        if (this.player) {
          AmbientOcclusion._playerPos = this.player.position;
        }
        AmbientOcclusion.processUpdateQueue();
      }

      // Update voxel editor (ghost preview)
      this.voxelEditor.update();

      requestAnimationFrame(this._loop);
    };

    // Pause/resume on tab visibility change to prevent background CPU waste
    // and state corruption from throttled RAF in inactive tabs
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this._paused = true;
      } else {
        this._resumeAfterPause();
      }
    });

    requestAnimationFrame(this._loop);
  }

  /**
   * Resume the game loop after a tab visibility pause.
   * Resets timing and subsystem timers so the first frame is clean.
   */
  _resumeAfterPause() {
    if (!this._paused) return;

    // Reset timing so first frame has near-zero delta
    this.lastTime = performance.now();
    this.lastFpsUpdate = this.lastTime;
    this.frames = 0;

    // Reset accumulated state in character subsystems
    this._resetSubsystemTimers();

    // Reset network sync timers — WebRTC messages accumulate while paused,
    // and stale velocity/timing data from before the pause would cause
    // jittery interpolation and overshoot on resume.
    if (this.playerSync) {
      this.playerSync._broadcastTimer = 0;
      // Clear stale velocity predictions on all remote players
      for (const remote of this.playerSync.remotePlayers.values()) {
        remote.velocity = null;
        remote.lastUpdateTime = Date.now();
      }
    }
    if (this.npcSync) {
      this.npcSync._broadcastTimer = 0;
      // Clear stale velocity predictions on all remote NPCs
      for (const remote of this.npcSync._remoteNPCs.values()) {
        remote.velocity = null;
        remote.lastUpdateTime = Date.now();
      }
    }

    // Give the browser 2 settle frames to re-composite CSS 3D layers
    // before allowing character updates (which trigger expensive SAT sort)
    this._settleFrames = 2;

    // Resume the RAF loop
    this._paused = false;
    requestAnimationFrame(this._loop);
  }

  /**
   * Reset accumulated timers in character subsystems after a pause.
   * Prevents false stuck detection, immediate NPC pathfinding bursts,
   * and animation frame skips on resume.
   */
  _resetSubsystemTimers() {
    for (const character of this.world.characters.values()) {
      // Reset PathFollower stuck/fall detection baselines
      if (character.pathFollower) {
        character.pathFollower._stuckTimer = 0;
        character.pathFollower._lastPosition = { x: character.x, z: character.z };
        character.pathFollower._lastY = character.y;
      }

      // Reset NPC idle timer and re-randomize so they don't all move at once
      if (character.behavior) {
        character.behavior.idleTimer = 0;
        character.behavior.nextIdleTime = character.behavior._randomIdleTime();
      }

      // Reset animator elapsed time
      if (character.animator) {
        character.animator.elapsedTime = 0;
      }
    }
  }
}

// Initialize game when DOM is loaded.
window.addEventListener('DOMContentLoaded', () => {
  const game = new Game();
  window.game = game;
});